/**
 * Spanish (es) translation dictionary for KinShield.
 *
 * Keep keys identical to en.js. Any missing key will fall back to the key
 * itself at runtime, so partial coverage is safe.
 */
const es = {
  // ── Common / shared ────────────────────────────────────────────────
  "common.backTo": "Volver a",
  "common.save": "Guardar",
  "common.cancel": "Cancelar",
  "common.close": "Cerrar",
  "common.loading": "Cargando...",
  "common.refresh": "Actualizar",
  "common.refreshing": "Actualizando...",
  "common.enabled": "Activado",
  "common.disabled": "Desactivado",
  "common.configured": "Configurado",
  "common.notAvailableDemo": "No disponible en modo demostración",
  "common.demoMode": "Modo demostraci\u00f3n",
  "common.comingSoon": "Pr\u00f3ximamente",
  "common.signUp": "Registrarse",
  "common.logIn": "Iniciar sesión",
  "common.signOut": "Cerrar sesión",

  // ── Edición (consumidor vs corporativo) terminología ───────────────
  "edition.consumer.account": "Cuenta Familiar",
  "edition.consumer.member": "Miembro de la Familia",
  "edition.consumer.members": "Miembros de la Familia",
  "edition.consumer.owner": "Propietario de la Cuenta",
  "edition.consumer.group": "Grupo",
  "edition.consumer.groups": "Grupos",
  "edition.consumer.addMember": "Agregar Miembro de la Familia",
  "edition.consumer.plansTitle": "Elige tu plan",
  "edition.corporate.account": "Cuenta de Empresa",
  "edition.corporate.member": "Miembro del Equipo",
  "edition.corporate.members": "Miembros del Equipo",
  "edition.corporate.owner": "Administrador",
  "edition.corporate.group": "Equipo",
  "edition.corporate.groups": "Equipos",
  "edition.corporate.addMember": "Agregar Miembro del Equipo",
  "edition.corporate.plansTitle": "Elige tu plan empresarial",

  // ── Registro / selección de tipo de cuenta ─────────────────────────
  "register.step": "Paso {current} de {total}",
  "register.accountType.title": "¿Cómo usarás KinShield?",
  "register.accountType.subtitle": "Elige la opción que mejor se adapte a ti.",
  "register.accountType.selected": "Tipo de cuenta:",
  "register.accountType.change": "Cambiar",
  "register.accountType.individual": "Individual y Familia",
  "register.accountType.individualDesc":
    "Protégete a ti y a tu hogar de estafas y fraudes.",
  "register.accountType.business": "Empresa y Equipo",
  "register.accountType.businessDesc":
    "Protege a tus empleados con capacitación en concientización de seguridad.",
  "register.companyName": "Nombre de la Empresa",
  "register.companyNamePlaceholder": "p. ej., Acme Inc.",
  "register.companyAdminEmailHelp":
    "Este es el correo electrónico del Administrador de la Cuenta de la Empresa, quien gestionará la cuenta de KinShield de tu organización.",
  "register.companyMeta.industry": "Industria",
  "register.companyMeta.industryPlaceholder": "Selecciona tu industria",
  "register.companyMeta.employeeCount": "Número de Empleados",
  "register.companyMeta.employeeCountPlaceholder": "p. ej., 25",

  // ── Perfil de la empresa (autoservicio del propietario de la cuenta) ─
  "company.profile.title": "Perfil de la Empresa",
  "company.profile.description":
    "Consulta y actualiza los datos de tu empresa (solo el propietario de la cuenta).",
  "company.profile.companyName": "Nombre de la Empresa",
  "company.profile.descriptionLabel": "Descripción",
  "company.profile.descriptionPlaceholder":
    "Una breve descripción de tu empresa (opcional)",
  "company.profile.industry": "Industria",
  "company.profile.industryPlaceholder": "Selecciona tu industria",
  "company.profile.employeeCount": "Número de Empleados",
  "company.profile.employeeCountPlaceholder": "p. ej., 25",
  "company.profile.employeeCountHelp":
    "El número total de empleados de tu empresa. Describe tu negocio y puede diferir del número de licencias de KinShield en tu plan. Se usa para tu informe de preparación para seguro cibernético.",
  "company.profile.companySize": "Rango de Tamaño de la Empresa",
  "company.profile.disableEmailAnalysis":
    "Desactivar el Análisis de Correos Entrantes",
  "company.profile.disableEmailAnalysisHelp":
    "Cuando está activado, los correos reenviados a KinShield por los miembros de tu organización no se analizan ni se envían a ningún proveedor de IA. Actívalo para desactivar la función de análisis de estafas por reenvío de correos en toda la organización.",
  "company.profile.disableEmailAnalysisOn": "Desactivado",
  "company.profile.disableEmailAnalysisOff": "Activado",
  "company.profile.ownerOnlyNote":
    "Solo el propietario de la cuenta puede actualizar el perfil de la empresa.",
  "company.profile.editButton": "Editar Perfil de la Empresa",
  "company.profile.saveButton": "Guardar Cambios",
  "company.profile.saving": "Guardando...",
  "company.profile.cancelButton": "Cancelar",
  "company.profile.saveSuccess":
    "Perfil de la empresa actualizado correctamente.",
  "company.profile.errorNameRequired":
    "El nombre de la empresa es obligatorio.",
  "company.profile.errorEmployeeCount":
    "El número de empleados debe ser un número entero de al menos 1.",
  "company.profile.errorSave":
    "No se pudo actualizar el perfil de la empresa. Inténtalo de nuevo.",

  // ── Account Profile page (account owner: Account + Company + User) ──
  "accountProfile.title": "Perfil de la Cuenta",
  "accountProfile.subtitle":
    "Administra tu cuenta, empresa y perfil personal en un solo lugar",
  "accountProfile.menuLabel": "Perfil de la Cuenta",
  "accountProfile.account.title": "Cuenta",
  "accountProfile.account.description":
    "Identidad, propiedad y uso de tu cuenta de un vistazo",
  "accountProfile.account.accountName": "Nombre de la Cuenta",
  "accountProfile.account.accountId": "ID de la Cuenta",
  "accountProfile.account.accountIdHelp":
    "Indica este ID de la Cuenta cuando contactes con soporte.",
  "accountProfile.account.accountType": "Tipo de Cuenta",
  "accountProfile.account.typeBusiness": "Empresa (Corporativa)",
  "accountProfile.account.typeIndividual": "Individual / Familiar",
  "accountProfile.account.owner": "Administrador de la Cuenta",
  "accountProfile.account.ownerYou": "Tú",
  "accountProfile.account.status": "Estado",
  "accountProfile.account.statusActive": "Activa",
  "accountProfile.account.statusInactive": "Inactiva",
  "accountProfile.account.statusDelinquent": "Pago pendiente",
  "accountProfile.account.memberSince": "Miembro Desde",
  "accountProfile.account.seats": "Asientos en Uso",
  "accountProfile.account.seatsOf": "de",
  "accountProfile.account.manageMembers": "Administrar miembros",
  "accountProfile.account.plan": "Plan Actual",
  "accountProfile.account.manageBilling": "Administrar en Suscripción y Pago",
  "accountProfile.account.notProvided": "No proporcionado",
  "accountProfile.userProfile.title": "Perfil de Usuario",
  "accountProfile.userProfile.description":
    "Consulta y actualiza tu perfil personal",
  "accountProfile.userMgmt.title": "Administración de Usuarios",
  "accountProfile.userMgmt.description":
    "Consulta, agrega y administra los miembros de tu cuenta",

  // ── Theme labels ───────────────────────────────────────────────────
  "theme.light": "Tema claro",
  "theme.dark": "Tema oscuro",
  "theme.auto": "Tema automático",
  "theme.switchBack": "Volver a tu cuenta",
  "header.theme.light": "Claro",
  "header.theme.dark": "Oscuro",
  "header.theme.auto": "Auto",
  "header.lang.english": "Inglés",
  "header.lang.spanish": "Español",
  "header.lang.toggleTitle": "Cambiar idioma",

  // ── Settings page ──────────────────────────────────────────────────
  "settings.title": "Configuración",
  "settings.subtitle":
    "Administra las preferencias de tu cuenta y la configuración de seguridad",
  "settings.stats.mfaStatus": "Estado de MFA",
  "settings.stats.theme": "Tema",
  "settings.stats.notifications": "Notificaciones",
  "settings.stats.language": "Idioma",
  "settings.section.security.title": "Seguridad de la cuenta",
  "settings.section.security.subtitle":
    "Autenticación multifactor, contraseña y seguridad de inicio de sesión",
  "settings.section.notifications.title": "Notificaciones y anuncios",
  "settings.section.notifications.subtitle":
    "Preferencias de correo, SMS, notificaciones en la app y anuncios",
  "settings.section.appearance.title": "Apariencia y pantalla",
  "settings.section.appearance.subtitle":
    "Tema, diseño del panel y preferencias de interfaz",
  "settings.section.localization.title": "Idioma y región",
  "settings.section.localization.subtitle":
    "Preferencias de idioma, zona horaria y formato de fecha",
  "settings.section.privacy.title": "Privacidad y uso compartido",
  "settings.section.privacy.subtitle":
    "Controla el uso compartido de datos, la visibilidad del perfil y el acceso del propietario de cuenta",
  "settings.section.organization.title": "Organización",
  "settings.section.organization.subtitle":
    "Controles para toda la organización disponibles para los propietarios de cuenta",

  // ── Organization sub-section ─────────────────────────────────
  "organization.dataGovernance.heading": "Gobernanza de datos",
  "organization.disableEmailAnalysis.title":
    "Desactivar el Análisis de Correos Entrantes",
  "organization.disableEmailAnalysis.desc":
    "Cuando está activado, los correos reenviados a KinShield por los miembros de tu organización no se analizan ni se envían a ningún proveedor de IA. Actívalo para desactivar la función de análisis de estafas por reenvío de correos en toda la organización.",
  "organization.error.load":
    "No se pudieron cargar los ajustes de la organización. Inténtalo de nuevo más tarde.",
  "organization.error.save":
    "No se pudieron actualizar los ajustes de la organización. Inténtalo de nuevo.",

  // ── Localization sub-section ───────────────────────────────────────
  "localization.heading": "Idioma y región",
  "localization.language": "Idioma",
  "localization.timezone": "Zona horaria",
  "localization.dateFormat": "Formato de fecha",
  "localization.lang.enUS": "Inglés (EE. UU.)",
  "localization.lang.enGB": "Inglés (Reino Unido)",
  "localization.lang.es": "Español",
  "localization.lang.fr": "Francés",
  "localization.lang.de": "Alemán",
  "localization.tz.eastern": "Hora del Este (ET)",
  "localization.tz.central": "Hora Central (CT)",
  "localization.tz.mountain": "Hora de la Montaña (MT)",
  "localization.tz.pacific": "Hora del Pacífico (PT)",

  // ── Privacy & Sharing sub-section ─────────────────────────────────
  "privacy.ownerAccess.heading": "Acceso del propietario de cuenta",
  "privacy.ownerAccess.desc":
    "Controla si el propietario de tu cuenta puede ver tu panel y los resultados de análisis en modo de solo lectura.",
  "privacy.ownerAccess.error.load":
    "Error al cargar la configuración de consentimiento",
  "privacy.ownerAccess.error.save":
    "Error al guardar la preferencia de consentimiento. Inténtalo de nuevo.",
  "privacy.ownerAccess.dashboard.title": "Permitir visualización del panel",
  "privacy.ownerAccess.dashboard.desc":
    "Permite que el propietario de la cuenta vea tu panel y puntuaciones de seguridad en modo de solo lectura",
  "privacy.ownerAccess.dashboard.ariaLabel":
    "Permitir que el propietario de la cuenta vea tu panel",
  "privacy.ownerAccess.analysis.title":
    "Permitir visualización de resultados de análisis",
  "privacy.ownerAccess.analysis.desc":
    "Permite que el propietario de la cuenta vea tus resultados de detección de estafas (correo, imagen, SMS, redes sociales, URL) en modo de solo lectura",
  "privacy.ownerAccess.analysis.ariaLabel":
    "Permitir que el propietario de la cuenta vea tus resultados de análisis",
  "privacy.ownerAccess.reports.title":
    "Permitir visualización de informes de progreso semanal",
  "privacy.ownerAccess.reports.desc":
    "Permite que el propietario de la cuenta vea tus informes de progreso semanal de Shield en modo de solo lectura",
  "privacy.ownerAccess.reports.ariaLabel":
    "Permitir que el propietario de la cuenta vea tus informes de progreso semanal",
  "privacy.dataSharing.heading": "Compartir datos",
  "privacy.dataSharing.analytics.title": "Análisis de uso",
  "privacy.dataSharing.analytics.desc":
    "Ayuda a mejorar nuestro servicio compartiendo datos de uso anónimos",
  "privacy.dataSharing.marketing.title": "Comunicaciones de marketing",
  "privacy.dataSharing.marketing.desc":
    "Recibe actualizaciones sobre nuevas funciones y servicios",
  "privacy.profileVisibility.heading": "Visibilidad del perfil",
  "privacy.profileVisibility.label": "Visibilidad del perfil",
  "privacy.profileVisibility.private": "Privado - Solo tú puedes ver tu perfil",
  "privacy.profileVisibility.team": "Equipo - Visible para tu organización",
  "privacy.profileVisibility.public":
    "Público - Visible para todos los usuarios",

  // ── SidePanel / Navigation menu items ──────────────────────────────
  // Keys mirror the English `name` strings used as routing identifiers.
  "Member User Info": "Información del usuario",
  "Groups & Organizations": "Grupos y organizaciones",
  "Resend Verification Key": "Reenviar clave de verificación",
  "Account & Billing": "Suscripción y pago",
  "Simulation Profile": "Perfil de simulación",
  "Simulation Messages": "Mensajes de simulación",
  "Recent Simulations": "Simulaciones recientes",
  "Shield Reports": "Informes de Shield",
  "Cyber-Insurance Readiness": "Preparación para Ciberseguro",
  "Export Data": "Exportar datos",
  "Admin Dashboard": "Panel de administración",
  "Scam Reports": "Informes de estafas",
  "Send Email": "Enviar correo",
  "Send SMS": "Enviar SMS",
  "Generate Blogs": "Generar blogs",
  "Generate Simulation Data": "Generar datos de simulación",
  "Manage Announcements": "Administrar anuncios",
  "Create Simulation Schedule": "Crear programación de simulación",
  "Execute Simulation Schedule": "Ejecutar programación de simulación",
  "Demo Users": "Usuarios demo",
  "Event Demo Seats": "Plazas de demo para eventos",
  "Forwarded Emails": "Correos reenviados",
  "Manual Entry": "Entrada manual",
  "Forwarded Texts": "Mensajes reenviados",
  "Manual Input": "Entrada manual",
  "Text Conversation": "Conversación de texto",
  "Business/Person Check": "Verificar negocio o persona",
  "Social Media Posts": "Publicaciones en redes sociales",
  "Verify a Picture": "Verificar una imagen",
  "Check a Phone Number": "Verificar un número de teléfono",
  "Training Center": "Centro de entrenamiento",
  "Alerts Intelligence Hub": "Centro de inteligencia de alertas",
  "Quick Scan": "Escaneo rápido",
  "Screenshot Analysis": "Análisis de captura de pantalla",
  "Phone Number Check": "Verificación de número telefónico",
  "QR Code Check": "Verificación de código QR",

  // SidePanel section group headers
  "nav.section.scamDetector": "Detector de estafas",
  "nav.section.email": "Análisis de correo",
  "nav.section.text": "Análisis de texto",
  "nav.section.social": "Protección social",
  "nav.section.moreTools": "Más herramientas",
  "nav.section.user": "Usuario",
  "nav.section.account": "Cuenta",
  "nav.section.simulations": "Simulaciones",
  "nav.section.reports": "Informes",
  "nav.section.platformAdmin": "Administrador de plataforma",
  "nav.section.demo": "Demostración",
  "nav.section.education": "Recursos",

  // SidePanel literal-string keys
  "User Management": "Gestión de usuarios",
  "Scam Detector": "Detector de estafas",
  "Email Analysis": "Análisis de correo",
  "Text/SMS Analysis": "Análisis de texto/SMS",
  "Social Media": "Redes sociales",
  "More Tools": "Más herramientas",
  Simulations: "Simulaciones",
  "Trainings & Alerts": "Capacitación y alertas",
  Account: "Cuenta",
  Reports: "Informes",
  "Platform Admin": "Administrador de plataforma",
  Demo: "Demostración",
  "nav.signInPrompt": "Inicia sesión para ver los menús",

  // ── Header (desktop & mobile) ──────────────────────────────────────
  "header.backToMain": "Volver al sitio principal",
  "header.dashboard": "Panel",
  "header.viewingAs": "Viendo como:",
  "header.switchBack": "Volver",
  "header.users": "Perfil del usuario",
  "header.userProfile": "Perfil del usuario",
  "header.activityMonitoring": "Monitoreo de actividad del usuario",
  "header.gettingStarted": "Primeros pasos",

  // ── Email Analysis (Forwarded Emails) page ────────────────────────
  "email.page.title": "Verificar un correo sospechoso",
  "email.page.subtitle":
    "Descubre si un correo es una estafa \u2014 reenv\u00edalo o p\u00e9galo aqu\u00ed.",
  "email.page.forwardTo": "Reenv\u00eda correos sospechosos a",
  "email.page.forwardSuffix":
    "y visualiza tus an\u00e1lisis de amenazas automatizados aqu\u00ed",
  "email.page.manualLink": "\u00bfPrefieres escribirlo t\u00fa mismo?",
  "email.page.manualLinkAction": "Analizar manualmente",
  "email.disclaimer":
    "El an\u00e1lisis de correo proporcionado es solo con fines educativos. Para conocer los t\u00e9rminos completos y las limitaciones, consulta nuestros",

  // Entry panel (how to check an email)
  "email.start.recommended": "Recomendado",
  "email.start.forward.title": "Reenv\u00edanoslo",
  "email.start.forward.why":
    "Lo m\u00e1s preciso \u2014 podemos ver enlaces ocultos y qui\u00e9n lo envi\u00f3 realmente.",
  "email.start.forward.step1":
    "Abre el correo sospechoso. No hagas clic en enlaces ni archivos adjuntos.",
  "email.start.forward.step2": "Reenv\u00edalo a:",
  "email.start.forward.step3": "Reenv\u00edalo desde {email}",
  "email.start.forward.step3Fallback":
    "Reenv\u00edalo desde el correo con el que inicias sesi\u00f3n",
  "email.start.forward.step3Detail":
    "As\u00ed sabemos que es tuyo. Los correos reenviados desde otras direcciones no se pueden verificar.",
  "email.start.forward.helpLabel":
    "Paso a paso para Gmail, Outlook, Apple Mail y Yahoo",
  "email.start.forward.sentBtn": "Acabo de reenviarlo",
  "email.start.waiting.title": "Esperando tu correo\u2026",
  "email.start.waiting.body":
    "Normalmente tarda menos de un minuto. Tu resultado se abrir\u00e1 aqu\u00ed autom\u00e1ticamente \u2014 si sales, te avisaremos.",
  "email.start.waiting.cancel": "No lo envi\u00e9 \u2014 cancelar",
  "email.start.timeout.title": "A\u00fan no lo hemos recibido",
  "email.start.timeout.body":
    "Aseg\u00farate de enviarlo a {address} desde {email}.",
  "email.start.timeout.bodyFallback":
    "Aseg\u00farate de enviarlo a {address} desde el correo con el que inicias sesi\u00f3n.",
  "email.start.timeout.retry": "Seguir esperando",
  "email.start.timeout.dismiss": "Dejar de esperar",
  "email.start.paste.title": "C\u00f3pialo y p\u00e9galo aqu\u00ed",
  "email.start.paste.why":
    "Obt\u00e9n una respuesta de inmediato. Copia el remitente, el asunto y el mensaje en un formulario breve.",
  "email.start.paste.btn": "Pegar un correo",
  "email.start.compact.forward": "Verifica otro correo \u2014 reenv\u00edalo a",
  "email.start.compact.sentBtn": "Acabo de reenviar uno",
  "email.start.compact.or": "o",
  "email.start.compact.paste": "Quiero pegarlo aqu\u00ed",

  // Mobile header
  "email.mobile.title": "An\u00e1lisis de correo",
  "email.mobile.subtitle":
    "Reenv\u00eda correos sospechosos para el an\u00e1lisis autom\u00e1tico de amenazas",
  "email.mobile.forwardLabel": "Reenviar correos a",
  "email.mobile.copied": "\u00a1Copiado!",
  "email.mobile.copy": "Copiar",
  "email.mobile.backToList": "Volver a la lista",

  // Results list
  "email.results.heading": "Historial de an\u00e1lisis",
  "email.results.showing":
    "Mostrando los \u00faltimos {count} an\u00e1lisis \u2022 Haz clic en cualquier elemento para ver los detalles",
  "email.results.refreshTitle": "Actualizar resultados de an\u00e1lisis",

  // Empty states
  "email.empty.title": "A\u00fan no hay an\u00e1lisis",
  "email.empty.forwardTo": "Reenv\u00eda un correo sospechoso a",
  "email.empty.toGetStarted": "para comenzar.",
  "email.empty.checkResults": "Verificar resultados",
  "email.empty.analyzeManuallyBtn": "Analizar un mensaje manualmente",
  "email.empty.noResults.title":
    "No se encontraron resultados de an\u00e1lisis",
  "email.empty.noResults.description":
    "Ning\u00fan an\u00e1lisis de correo coincide con tus criterios de b\u00fasqueda actuales. Reenv\u00eda correos sospechosos a security@kinprotect.com y visualiza tus an\u00e1lisis de amenazas automatizados aqu\u00ed",

  // Status badges
  "email.badge.scam": "ESTAFA",
  "email.badge.safe": "SEGURO",
  "email.badge.legitimate": "LEG\u00cdTIMO",

  // Card / list item labels
  "email.card.from": "De",
  "email.card.fromLabel": "De:",
  "email.card.subject": "Asunto",
  "email.card.subjectLabel": "Asunto:",
  "email.card.unknown": "Desconocido",
  "email.card.noSubject": "Sin asunto",
  "email.card.deleteTitle": "Eliminar an\u00e1lisis",
  "email.card.delete": "Eliminar",

  // Detail view
  "email.detail.back": "Volver a los resultados",
  "email.detail.scamDetected": "ESTAFA DETECTADA",
  "email.detail.noThreats": "NO SE DETECTARON AMENAZAS",
  "email.detail.scamSubtitle":
    "Este correo contiene contenido sospechoso o fraudulento",
  "email.detail.safeSubtitle": "Este correo parece ser leg\u00edtimo",
  "email.detail.shareIndividual":
    "Disponible para planes Familiar o Familiar Extendido",
  "email.detail.shareFamily": "Compartir esta alerta con familiares",
  "email.detail.share": "Compartir",

  // Share button props
  "email.share.threatType": "Estafa por correo",
  "email.share.threatTitle": "Estafa por correo detectada",

  // Section headings (desktop detail panel)
  "email.section.analysisDetails": "Detalles del an\u00e1lisis",
  "email.section.analysisDetailsSubtitle":
    "Lo que encontramos sobre este correo",
  "email.section.whatToDo": "\u00bfQu\u00e9 deber\u00edas hacer?",
  "email.section.riskFactors": "Factores de riesgo",
  "email.section.explanation": "Explicaci\u00f3n",
  "email.section.attachments": "Archivos adjuntos",
  "email.section.emailAttachments": "Archivos adjuntos del correo",
  "email.section.noAnalysisSelected": "Ning\u00fan an\u00e1lisis seleccionado",
  "email.section.noAnalysisSelectedDesc":
    "Selecciona un correo de la lista para ver los resultados detallados del an\u00e1lisis aqu\u00ed.",

  // Attachment labels
  "email.attachment.risk.high": "alto",
  "email.attachment.risk.medium": "medio",
  "email.attachment.risk.low": "bajo",
  "email.attachment.highRisk": "Tipo de archivo de alto riesgo",
  "email.attachment.mediumRisk": "Tipo de archivo de riesgo medio",
  "email.attachment.lowRisk": "Tipo de archivo generalmente seguro",
  "email.attachment.warningLabel": "Advertencia:",
  "email.attachment.warningText":
    "Nunca abras archivos adjuntos de fuentes no confiables. Los archivos ejecutables (.exe, .bat, .com, .scr) son especialmente peligrosos y deben tratarse con extrema precauci\u00f3n.",

  // Error & quota messages
  "email.error.tryAgain": "Intentar de nuevo",
  "email.error.notAuthenticated": "Usuario no autenticado",
  "email.error.loadFailed":
    "No se pudieron cargar los an\u00e1lisis de correo. Intenta de nuevo.",
  "email.error.notFound":
    "No se pudo encontrar el an\u00e1lisis de correo solicitado.",
  "email.error.loadNotifFailed":
    "No se pudo cargar el an\u00e1lisis de correo solicitado.",
  "email.error.deleteFailed":
    "No se pudo eliminar el an\u00e1lisis. Intenta de nuevo.",
  "email.quota.title": "L\u00edmite diario alcanzado",
  "email.quota.body":
    "Has alcanzado tu l\u00edmite diario de 25 an\u00e1lisis de correo. Tu cuota se restablecer\u00e1 ma\u00f1ana.",
  "email.analysisDisabled.title":
    "El an\u00e1lisis de correo est\u00e1 desactivado para tu organizaci\u00f3n",
  "email.analysisDisabled.body":
    "El administrador de tu cuenta ha desactivado el an\u00e1lisis de correos reenviados. Los correos que reenv\u00edes a security@kinprotect.com no se analizar\u00e1n ni se enviar\u00e1n a ning\u00fan proveedor de IA. Comun\u00edcate con tu administrador si crees que deber\u00eda estar activado.",

  // ── Manual Email Scan page ─────────────────────────────────────────────
  "manual.page.title": "An\u00e1lisis manual de correo",
  "manual.page.subtitle":
    "Copia y pega el contenido del correo para analizar posibles intentos de phishing y estafas usando detecci\u00f3n con IA",
  "manual.loading.title": "Analizando correo...",
  "manual.loading.subtitle":
    "Por favor espera mientras verificamos posibles estafas e intentos de phishing",
  "manual.error.generic": "Ocurri\u00f3 un error al analizar el correo",

  // Form
  "manual.form.heading": "Contenido del correo",
  "manual.form.senderLabel": "Direcci\u00f3n de correo del remitente",
  "manual.form.subjectLabel": "Asunto del correo",
  "manual.form.bodyLabel": "Cuerpo del correo",
  "manual.form.bodyPlaceholder": "Pega el contenido del correo aqu\u00ed...",
  "manual.form.contextLabel": "Contexto (opcional)",
  "manual.form.contextPlaceholder":
    "\u00bfAlg\u00fan detalle o situaci\u00f3n adicional que agregar?",
  "manual.form.analyzing": "Analizando...",
  "manual.form.analyzeBtn": "Analizar correo",
  "manual.form.clearBtn": "Limpiar",

  // Results
  "manual.results.heading": "Detalles del an\u00e1lisis",
  "manual.results.safeSubtitle": "Este correo parece ser seguro",
  "manual.results.scamType": "Tipo de estafa:",
  "manual.results.noAnalysis": "Sin an\u00e1lisis disponible",
  "manual.results.noAnalysisDesc":
    "Proporciona el contenido del correo para ver los resultados detallados aqu\u00ed.",

  // Mobile-specific
  "manual.results.viewResults": "Ver resultados del an\u00e1lisis",
  "manual.results.backToForm": "Volver al formulario",
  "manual.results.appearsLegitimate": "PARECE LEG\u00cdTIMO",
  "manual.results.newScan": "Nuevo an\u00e1lisis",
  "manual.results.noAnalysisMobile":
    "Env\u00eda un correo para ver los resultados aqu\u00ed.",
  "manual.results.goToForm": "Ir al formulario",

  // ── Dashboard Simple ───────────────────────────────────────────────
  "dashboard.welcome": "Bienvenido",
  "dashboard.subtitle": "Tu panel de protecci\u00f3n contra estafas",

  // Setup Progress toggle button
  "dashboard.setupProgress.show": "Mostrar progreso de configuraci\u00f3n",
  "dashboard.setupProgress.hide": "Ocultar progreso de configuraci\u00f3n",
  "dashboard.setupProgress.showAriaLabel":
    "Mostrar progreso de configuraci\u00f3n de la cuenta",
  "dashboard.setupProgress.hideAriaLabel":
    "Ocultar progreso de configuraci\u00f3n de la cuenta",
  "dashboard.setupProgress.label": "Progreso de configuraci\u00f3n",

  // Pin-as-default (shared with GettingStarted)
  "dashboard.pin.alreadyDefault": "Esta ya es tu p\u00e1gina predeterminada",
  "dashboard.pin.setDefault":
    "Establecer esta p\u00e1gina como p\u00e1gina de inicio predeterminada al iniciar sesi\u00f3n",
  "dashboard.pin.confirmed": "\u00a1P\u00e1gina predeterminada actualizada!",
  "dashboard.pin.isDefault": "Esta es tu p\u00e1gina predeterminada",
  "dashboard.pin.makeDefault": "Establecer como mi p\u00e1gina predeterminada",

  // Footer
  "dashboard.footer.helpText":
    "\u00bfNecesitas ayuda? Nuestro equipo de soporte est\u00e1 aqu\u00ed para ayudarte con cualquier pregunta.",
  "dashboard.footer.contactInfo":
    "\ud83d\udcde Llamar a soporte: 1-301-237-7843 | \ud83d\udce7 Correo: support@kinprotect.com",

  // ── Getting Started page ───────────────────────────────────────────
  "gs.header.title": "Primeros pasos con KinShield",
  "gs.header.subtitle":
    "Esto es lo que KinShield puede hacer por ti. Elige cualquier opci\u00f3n a continuaci\u00f3n para comenzar \u2014 no hay elecci\u00f3n incorrecta.",
  "gs.recommended": "Recomendado",

  // Category headers
  "gs.category.protect.title": "Prot\u00e9gete",
  "gs.category.protect.subtitle":
    "Verifica mensajes, llamadas y enlaces sospechosos",
  "gs.category.protect.moreHint":
    "N\u00fameros de tel\u00e9fono, enlaces, capturas de pantalla, publicaciones en redes sociales y conversaciones",
  "gs.more.show": "Mostrar {count} formas m\u00e1s de verificar",
  "gs.more.hide": "Mostrar menos opciones",
  "gs.category.learn.title": "Aprende y entr\u00e9nate",
  "gs.category.learn.subtitle": "Fortalece tus defensas con conocimiento",
  "gs.learn.simulationNote.before":
    "Recibir\u00e1s autom\u00e1ticamente simulaciones de estafas seguras seg\u00fan tu ",
  "gs.learn.simulationNote.link": "Configuraci\u00f3n de simulaci\u00f3n",
  "gs.learn.simulationNote.after": ".",
  "gs.category.monitor.title": "Tu estado de seguridad",
  "gs.category.monitor.subtitle":
    "Ve c\u00f3mo te est\u00e1s desempe\u00f1ando",
  "gs.category.family.title": "Protege a tu familia",
  "gs.category.family.subtitle":
    "Extiende la protecci\u00f3n a las personas que te importan",
  "gs.category.account.title": "Tu cuenta",
  "gs.category.account.subtitle": "Administra tu plan y preferencias",

  // Protect Yourself items
  "gs.item.email.label": "Verificar un correo sospechoso",
  "gs.item.email.description":
    "Reenv\u00eda cualquier correo sospechoso a security@kinprotect.com y luego ven aqu\u00ed para ver el an\u00e1lisis de IA.",
  "gs.item.text.label": "Verificar un mensaje de texto sospechoso",
  "gs.item.text.description":
    "Reenv\u00eda textos sospechosos al +1-888-899-5004 y consulta el an\u00e1lisis aqu\u00ed.",
  "gs.item.phone.label": "Buscar un n\u00famero de tel\u00e9fono",
  "gs.item.phone.description":
    "Comprueba si un n\u00famero de tel\u00e9fono est\u00e1 asociado a estafadores conocidos.",
  "gs.item.url.label": "Verificar si un enlace es seguro",
  "gs.item.url.description":
    "Pega una URL para detectar phishing, malware o fraude.",
  "gs.item.screenshot.label":
    "Subir una captura de pantalla para an\u00e1lisis",
  "gs.item.screenshot.description":
    "Sube una imagen para detectar estafas de soporte t\u00e9cnico, facturas falsas y m\u00e1s.",
  "gs.item.social.label": "Verificar una publicaci\u00f3n en redes sociales",
  "gs.item.social.description":
    "Analiza una publicaci\u00f3n en redes sociales en busca de indicios de estafa.",
  "gs.item.conversation.label": "Analizar una conversaci\u00f3n de texto",
  "gs.item.conversation.description":
    "Pega un hilo de conversaci\u00f3n completo para detectar manipulaci\u00f3n o patrones de fraude.",

  // Learn & Train items
  "gs.item.training.label": "Iniciar entrenamiento de seguridad",
  "gs.item.training.description":
    "Lecciones breves y cuestionarios sobre c\u00f3mo detectar y evitar estafas.",
  "gs.item.simulationprofile.label": "Configura tu perfil de simulaci\u00f3n",
  "gs.item.simulationprofile.description":
    "Configura tu perfil de simulaci\u00f3n para recibir ejercicios de phishing personalizados seg\u00fan tu nivel de riesgo.",
  "gs.item.alerts.label": "Explorar el Centro de inteligencia de alertas",
  "gs.item.alerts.description":
    "Consulta alertas reales de estafas por categor\u00eda, nivel de amenaza y tendencias recientes.",

  // Monitor items
  "gs.item.score.label": "Ver tu puntuaci\u00f3n de seguridad",
  "gs.item.score.description":
    "Una puntuaci\u00f3n personal basada en tu entrenamiento, simulaciones e interacci\u00f3n.",
  "gs.item.risk.label": "Revisar tu evaluaci\u00f3n de riesgos",
  "gs.item.risk.description":
    "Comprende tu nivel de riesgo seg\u00fan tu comportamiento de seguridad.",

  // Family items
  "gs.item.adduser.label": "Agregar un familiar",
  "gs.item.adduser.description":
    "Invita a un familiar para que reciba la misma protecci\u00f3n que t\u00fa.",
  "gs.item.manageusers.label": "Administrar usuarios de tu cuenta",
  "gs.item.manageusers.description":
    "Visualiza, edita o elimina usuarios de tu grupo.",

  // Account items
  "gs.item.plan.label": "Ver tu plan de suscripci\u00f3n",
  "gs.item.plan.description":
    "Consulta tu plan actual o actualiza para obtener m\u00e1s funciones.",
  "gs.item.settings.label": "Personalizar tu configuraci\u00f3n",
  "gs.item.settings.description":
    "Preferencias de notificaciones, tema, contrase\u00f1a y autenticaci\u00f3n de dos factores.",

  // Footer
  "gs.footer.title": "\u00bfBuscas algo m\u00e1s?",
  "gs.footer.chatbotTip.prefix": "Pregunta a",
  "gs.footer.chatbotTip.scoutName": "Scout",
  "gs.footer.chatbotTip.middle": "nuestro",
  "gs.footer.chatbotTip.dpgName": "Guardia Personal Digital",
  "gs.footer.chatbotTip.suffix":
    "chatbot, para obtener ayuda \u2014 solo haz clic en la burbuja de chat en la esquina inferior derecha.",
  "gs.footer.chatWithScout.title": "Chat con Scout",
  "gs.footer.contactSupport.title": "Contactar soporte",
  "gs.footer.contactSupport.description":
    "Comunícate con nuestro equipo de soporte para preguntas más complejas o ayuda con tu cuenta.",
  "gs.footer.contactSupport.button": "Contactar soporte",

  // Install App Panel
  "installApp.description":
    "Instala KinShield como una aplicación independiente en este dispositivo para acceso rápido y una experiencia nativa.",
  "installApp.alreadyInstalled":
    "KinShield ya está instalado como aplicación en tu dispositivo.",
  "installApp.shortcutRemoved":
    "KinShield ya está instalado \u2014 parece que el acceso directo fue eliminado.",
  "installApp.restoreShortcut": "Restaurar acceso directo",
  "installApp.install": "Instalar",
  "installApp.installTitle": "Instalar KinShield en este dispositivo",
  "installApp.installTitleFallback":
    "Instalar KinShield (abre la guía de configuración si el aviso del navegador no está disponible)",
  "installApp.addToHomepage": "Añadir a la pantalla de inicio",

  // ── Common additions ───────────────────────────────────────────────
  "common.retry": "Reintentar",
  "common.unableToLoad": "\u26a0\ufe0f No se pudo cargar. Intenta de nuevo.",

  // ── Today's Focus card ─────────────────────────────────────────────
  "dashboard.todaysFocus.label": "Enfoque de hoy",
  "dashboard.todaysFocus.nextSafetyLesson":
    "Tu pr\u00f3xima lecci\u00f3n de seguridad",
  "dashboard.todaysFocus.trainingOf":
    "Capacitaci\u00f3n {completed} de {total}",
  "dashboard.todaysFocus.startNow": "Comenzar ahora \u2192",
  "dashboard.todaysFocus.startFirstTitle":
    "Comienza tu primera lecci\u00f3n de seguridad",
  "dashboard.todaysFocus.startFirstSub":
    "Aprende a detectar estafas antes de que lleguen a ti",
  "dashboard.todaysFocus.getStarted": "Empezar \u2192",
  "dashboard.todaysFocus.allCompleteTitle":
    "\u00a1Todas las capacitaciones completadas \u2014 Excelente trabajo!",
  "dashboard.todaysFocus.allCompleteSub":
    "Has terminado todas las lecciones disponibles. \u00a1Vuelve pronto para ver nuevas!",
  "dashboard.todaysFocus.viewProgress": "Ver tu progreso \u2192",
  "dashboard.todaysFocus.continueTitle":
    "Contin\u00faa tu capacitaci\u00f3n de seguridad",
  "dashboard.todaysFocus.completedOf":
    "Has completado {completed} de {total} lecciones",
  "dashboard.todaysFocus.continueBtn": "Continuar capacitaci\u00f3n \u2192",
  "dashboard.todaysFocus.buildSkillsTitle":
    "Desarrolla tus habilidades de protecci\u00f3n contra estafas",
  "dashboard.todaysFocus.buildSkillsSub":
    "Completa las lecciones de seguridad para mantenerte protegido en l\u00ednea",

  // ── Safety Score card (SafetyScoreCard) ────────────────────────────
  "dashboard.safetyScore.title":
    "\ud83d\udee1\ufe0f \u00bfQu\u00e9 tan seguro est\u00e1s?",
  "dashboard.safetyScore.lastDays": "\u00daltimos 30 d\u00edas",
  "dashboard.safetyScore.excellent": "Excelente",
  "dashboard.safetyScore.strong": "S\u00f3lido",
  "dashboard.safetyScore.good": "Bueno",
  "dashboard.safetyScore.buildingUp": "Creciendo \ud83c\udf31",
  "dashboard.safetyScore.goodStart":
    "\u00a1Buen comienzo \u2014 sigue as\u00ed!",
  "dashboard.safetyScore.justStarted": "Reci\u00e9n comenzando",
  "dashboard.safetyScore.explain80":
    "\u00a1Excelente trabajo! Tus h\u00e1bitos seguros te mantienen bien protegido.",
  "dashboard.safetyScore.explain60":
    "\u00a1Buen progreso! Unas pocas elecciones m\u00e1s aumentar\u00e1n tu protecci\u00f3n.",
  "dashboard.safetyScore.explain40":
    "\u00a1Est\u00e1s creando buenos h\u00e1bitos! Completa una lecci\u00f3n para subir tu nivel.",
  "dashboard.safetyScore.explain0":
    "\u00a1Empecemos! Completa una lecci\u00f3n de capacitaci\u00f3n para desarrollar tu protecci\u00f3n.",
  "dashboard.safetyScore.cta60":
    "Revisa una lecci\u00f3n para mantenerte alerta \u2192",
  "dashboard.safetyScore.cta0":
    "Completa 1 capacitaci\u00f3n para mejorar \u2192",
  "dashboard.safetyScore.scoreComponents": "Componentes de puntuaci\u00f3n",
  "dashboard.safetyScore.comp.appParticipation": "Participaci\u00f3n en la app",
  "dashboard.safetyScore.comp.emailSafety": "Seguridad de correo",
  "dashboard.safetyScore.comp.textSafety": "Seguridad de texto",
  "dashboard.safetyScore.comp.training": "Capacitaci\u00f3n",
  "dashboard.safetyScore.noDataYet": "Sin datos a\u00fan",
  "dashboard.safetyScore.active": "Activo \u2713",
  "dashboard.safetyScore.daysActive": "{success} de {total} d\u00edas activo",
  "dashboard.safetyScore.noTestsYet": "Sin pruebas a\u00fan",
  "dashboard.safetyScore.avoidedOf": "{success} de {total} evitados",
  "dashboard.safetyScore.noneAvailable": "Ninguno disponible",
  "dashboard.safetyScore.allComplete": "Todo completo \u2713",
  "dashboard.safetyScore.doneOf": "{success} de {total} completados",

  // ── Simple Score card (ScoreCard) ────────────────────────────
  "dashboard.scoreCard.titleProtection":
    "\u00bfQu\u00e9 tan seguro est\u00e1s?",
  "dashboard.scoreCard.titleSafety": "Tu puntuaci\u00f3n de seguridad",
  "dashboard.scoreCard.excellentProtection": "Protecci\u00f3n excelente",
  "dashboard.scoreCard.goodProtection": "Buena protecci\u00f3n",
  "dashboard.scoreCard.needsImprovement": "Necesita mejorar",
  "dashboard.scoreCard.highRisk": "Riesgo alto",
  "dashboard.scoreCard.someRisk": "Algo de riesgo",
  "dashboard.scoreCard.lowRisk": "Riesgo bajo",
  "dashboard.scoreCard.adviceShieldGood":
    "\u00a1Excelente trabajo! Tus h\u00e1bitos de seguridad en l\u00ednea te est\u00e1n protegiendo bien. \u00a1Sigue as\u00ed!",
  "dashboard.scoreCard.adviceShieldWarning":
    "\u00a1Lo est\u00e1s haciendo bien! Un poco m\u00e1s de pr\u00e1ctica fortalecer\u00e1 tus habilidades de seguridad.",
  "dashboard.scoreCard.adviceShieldDanger":
    "Reci\u00e9n est\u00e1s comenzando \u2014 completa algunos ejercicios para desarrollar tu protecci\u00f3n.",
  "dashboard.scoreCard.adviceRiskGood":
    "\u00a1Excelente! Est\u00e1s demostrando un comportamiento seguro en l\u00ednea. Sigue las mejores pr\u00e1cticas.",
  "dashboard.scoreCard.adviceRiskWarning":
    "Ten cuidado. Algunos de tus h\u00e1bitos en l\u00ednea podr\u00edan ponerte en riesgo. Revisa los consejos de seguridad.",
  "dashboard.scoreCard.adviceRiskDanger":
    "Importante: Tus actividades en l\u00ednea muestran mayor riesgo. Completa la capacitaci\u00f3n para mantenerte seguro.",
  "dashboard.scoreCard.noteLabel": "Nota:",
  "dashboard.scoreCard.usingSampleData": "Usando datos de muestra -",

  // ── Training Progress card (TrainingProgress) ────────────────
  "dashboard.training.title": "\ud83d\udcda Progreso de capacitaci\u00f3n",
  "dashboard.training.getStarted": "Empezar",
  "dashboard.training.noLessons": "A\u00fan no hay lecciones asignadas",
  "dashboard.training.allDone": "\ud83c\udfc6 \u00a1Todo listo!",
  "dashboard.training.completedAll": "Completaste las {total} lecciones",
  "dashboard.training.lessonOf": "Lecci\u00f3n {lesson} de {total}",
  "dashboard.training.requiredTraining": "Capacitaci\u00f3n requerida",
  "dashboard.training.nextReady":
    "Tu pr\u00f3xima lecci\u00f3n est\u00e1 lista",
  "dashboard.training.explain0":
    "\u00a1Comienza tu primera lecci\u00f3n para desarrollar conciencia sobre estafas!",
  "dashboard.training.explain90":
    "\u00a1Sobresaliente! Est\u00e1s bien preparado para detectar estafas.",
  "dashboard.training.explain80":
    "\u00a1Excelente trabajo! Tu capacitaci\u00f3n te mantiene seguro.",
  "dashboard.training.explain70":
    "\u00a1Buen progreso! Sigue adelante para mantenerte protegido.",
  "dashboard.training.explain60":
    "\u00a1Buen trabajo! M\u00e1s lecciones fortalecer\u00e1n tus habilidades.",
  "dashboard.training.explain50":
    "\u00a1Vas por buen camino! Cada lecci\u00f3n te hace m\u00e1s seguro.",
  "dashboard.training.explainDefault":
    "Todo experto fue principiante alguna vez \u2014 \u00a1sigue adelante!",
  "dashboard.training.continueBtn": "Continuar capacitaci\u00f3n \u2192",
  "dashboard.training.required": "Requerida",
  "dashboard.training.recommended": "Recomendada",
  "dashboard.training.quizzesTaken": "Cuestionarios",
  "dashboard.training.avgScore": "Prom. puntuaci\u00f3n",
  "dashboard.training.badges": "Insignias",
  "dashboard.training.areasToggle": "\u00c1reas de capacitaci\u00f3n ({count})",

  // ── Scam Text Simulations (TextSimulations) ───────────────
  "dashboard.textSims.title": "\ud83d\udcf1 Simulaciones de textos de estafa",
  "dashboard.textSims.scamsAvoided": "Textos de estafa simulados evitados",
  "dashboard.textSims.noTestsYet": "A\u00fan sin pruebas",
  "dashboard.textSims.explain0":
    "Pruebas de seguridad de texto pr\u00f3ximamente para protegerte de mensajes de estafa.",
  "dashboard.textSims.explainPerfect":
    "\u00a1Perfecto! Evitaste todos los intentos de estafa por texto.",
  "dashboard.textSims.explain80":
    "\u00a1Gran conciencia! Sigue alerta ante textos sospechosos.",
  "dashboard.textSims.explain60":
    "\u00a1Buen progreso! Recuerda verificar los remitentes desconocidos.",
  "dashboard.textSims.explainLow":
    "\u00a1Ten cuidado! No hagas clic en enlaces de textos inesperados.",
  "dashboard.textSims.avoided": "Evitados",
  "dashboard.textSims.clicked": "Clicados",
  "dashboard.textSims.total": "Total",
  "dashboard.textSims.showTests": "Mostrar {count} pruebas",
  "dashboard.textSims.textTest": "Prueba de texto",

  // ── Phishing Email Simulations (EmailSimulations) ─────────────────────
  "dashboard.emailSims.title":
    "\u2709\ufe0f Simulaciones de correos de phishing",
  "dashboard.emailSims.phishingAvoided":
    "Correos de phishing simulados evitados",
  "dashboard.emailSims.noTestsYet": "A\u00fan sin pruebas",
  "dashboard.emailSims.explain0":
    "Pruebas de seguridad de correo pr\u00f3ximamente para protegerte del phishing.",
  "dashboard.emailSims.explainPerfect":
    "\u00a1Perfecto! Evitaste todos los intentos de phishing por correo.",
  "dashboard.emailSims.explain80":
    "\u00a1Gran conciencia! Sigue vigilando los correos sospechosos.",
  "dashboard.emailSims.explain60":
    "\u00a1Buen progreso! Verifica cuidadosamente las direcciones de los remitentes.",
  "dashboard.emailSims.explainLow":
    "\u00a1Mant\u00e9n la alerta! No hagas clic en enlaces de correos inesperados.",
  "dashboard.emailSims.avoided": "Evitados",
  "dashboard.emailSims.clicked": "Clicados",
  "dashboard.emailSims.total": "Total",
  "dashboard.emailSims.showTests": "Mostrar {count} pruebas",
  "dashboard.emailSims.emailTest": "Prueba de correo",

  // ── Recent Activities card (RecentActivities) ────────────────
  "dashboard.recentActivities.title": "Actividades recientes",
  "dashboard.recentActivities.keepItUp": "\ud83d\udcc8 \u00a1S\u00edguela!",
  "dashboard.recentActivities.keepItUpDesc":
    "Esto es lo que has estado haciendo para mantenerte seguro en l\u00ednea.",
  "dashboard.recentActivities.activity1":
    "Capacitaci\u00f3n de seguridad de correo completada",
  "dashboard.recentActivities.activity2":
    "Prueba de seguridad de llamadas telef\u00f3nicas aprobada",
  "dashboard.recentActivities.activity3":
    "Recordatorio: Completa la capacitaci\u00f3n de mensajes de texto",
  "dashboard.recentActivities.activity4":
    "Insignia de campe\u00f3n de seguridad obtenida",
  "dashboard.recentActivities.today": "Hoy",
  "dashboard.recentActivities.yesterday": "Ayer",
  "dashboard.recentActivities.twoDaysAgo": "Hace 2 d\u00edas",
  "dashboard.recentActivities.oneWeekAgo": "Hace 1 semana",
  "dashboard.recentActivities.viewAll": "Ver todas las actividades",

  // ── Recommendations card (Recommendations) ───────────────────
  "dashboard.recommendations.title": "Recomendado para ti",
  "dashboard.recommendations.loading": "Cargando recomendaciones...",
  "dashboard.recommendations.greatJob": "\ud83c\udf89 \u00a1Excelente trabajo!",
  "dashboard.recommendations.allComplete":
    "\u00a1Has completado todas las capacitaciones disponibles!",
  "dashboard.recommendations.browseAll": "Ver todas las capacitaciones",
  "dashboard.recommendations.personalTips":
    "\ud83d\udca1 Consejos personalizados:",
  "dashboard.recommendations.based":
    "Seg\u00fan tu progreso, aqu\u00ed hay algunos temas \u00fatiles para explorar.",
  "dashboard.recommendations.recommended": "Recomendado",
  "dashboard.recommendations.suggested": "Sugerido",
  "dashboard.recommendations.optional": "Opcional",
  "dashboard.recommendations.continue": "Continuar",
  "dashboard.recommendations.startLearning": "Empezar a aprender",
  "dashboard.recommendations.seeAll":
    "Ver todas las opciones de capacitaci\u00f3n",

  // ── Forwarded Text/SMS Analysis (SmsAnalysisSimple) ──────────────────────
  "sms.page.title": "Verificar un mensaje de texto sospechoso",
  "sms.page.subtitle":
    "Descubre si un mensaje es una estafa \u2014 reenv\u00edalo o p\u00e9galo aqu\u00ed.",
  "sms.page.subtitlePrefix":
    "Reenvía mensajes de texto sospechosos a KinShield (",
  "sms.page.subtitleSuffix":
    ") y visualiza tus análisis de amenazas automatizados aquí",
  "sms.page.manualLink": "¿Prefieres escribirlo tú mismo?",
  "sms.page.manualLinkAction": "Analizar manualmente",
  "sms.disclaimer":
    "El análisis de SMS proporcionado es solo con fines educativos. Para conocer los términos completos y las limitaciones, consulta nuestros",

  // Entry panel (how to check a text message)
  "sms.start.recommended": "Recomendado",
  "sms.start.forward.title": "Reenv\u00edanoslo",
  "sms.start.forward.why":
    "Lo m\u00e1s preciso \u2014 vemos el mensaje exacto, con todos sus enlaces.",
  "sms.start.forward.step1":
    "Abre el mensaje sospechoso. No toques ning\u00fan enlace ni respondas.",
  "sms.start.forward.step2": "Reenv\u00edalo a:",
  "sms.start.forward.step3": "Reenv\u00edalo desde {phone}",
  "sms.start.forward.step3Fallback":
    "Reenv\u00edalo desde el n\u00famero de tel\u00e9fono de tu cuenta",
  "sms.start.forward.step3Detail":
    "As\u00ed sabemos que es tuyo. Los mensajes reenviados desde otros n\u00fameros no se pueden verificar.",
  "sms.start.forward.helpLabel": "Paso a paso para iPhone y Android",
  "sms.start.forward.sentBtn": "Acabo de reenviarlo",
  "sms.start.waiting.title": "Esperando tu mensaje\u2026",
  "sms.start.waiting.body":
    "Normalmente tarda menos de un minuto. Tu resultado se abrir\u00e1 aqu\u00ed autom\u00e1ticamente.",
  "sms.start.waiting.cancel": "No lo envi\u00e9 \u2014 cancelar",
  "sms.start.timeout.title": "A\u00fan no lo hemos recibido",
  "sms.start.timeout.body":
    "Aseg\u00farate de enviarlo a {number} desde {phone}.",
  "sms.start.timeout.bodyFallback":
    "Aseg\u00farate de enviarlo a {number} desde el n\u00famero de tel\u00e9fono de tu cuenta.",
  "sms.start.timeout.retry": "Seguir esperando",
  "sms.start.timeout.dismiss": "Dejar de esperar",
  "sms.start.paste.title": "C\u00f3pialo y p\u00e9galo aqu\u00ed",
  "sms.start.paste.why":
    "Obt\u00e9n una respuesta de inmediato. Copia el mensaje (y el n\u00famero del remitente, si lo tienes) en un formulario breve.",
  "sms.start.paste.btn": "Pegar un mensaje",
  "sms.start.compact.forward": "Verifica otro mensaje \u2014 reenv\u00edalo a",
  "sms.start.compact.sentBtn": "Acabo de reenviar uno",
  "sms.start.compact.or": "o",
  "sms.start.compact.paste": "Quiero pegarlo aqu\u00ed",

  // Mobile header
  "sms.mobile.title": "Análisis de SMS",
  "sms.mobile.subtitle":
    "Reenvía mensajes de texto sospechosos para el análisis automático de amenazas",
  "sms.mobile.forwardLabel": "Reenviar textos a",
  "sms.mobile.backToList": "Volver a la lista",
  "sms.mobile.copied": "¡Copiado!",
  "sms.mobile.copy": "Copiar",

  // Results list
  "sms.results.heading": "Historial de análisis",
  "sms.results.showingSingular":
    "Mostrando {count} análisis de SMS \u2022 Haz clic en cualquier elemento para ver los detalles",
  "sms.results.showingPlural":
    "Mostrando {count} análisis de SMS \u2022 Haz clic en cualquier elemento para ver los detalles",
  "sms.results.noSmsFound": "No se encontraron análisis de SMS",
  "sms.results.refreshTitle": "Actualizar resultados de análisis",

  // Empty states
  "sms.empty.noAnalysisFound": "No se encontraron análisis de SMS",
  "sms.empty.forwardToKinShield":
    "Reenvía mensajes de texto sospechosos a KinShield (+1-888-899-5004) para ver los análisis aquí",
  "sms.empty.noAnalysesYet": "Aún no hay análisis",
  "sms.empty.getStarted":
    "Reenvía un mensaje de texto sospechoso al +1-888-899-5004 para comenzar.",
  "sms.empty.checkResults": "Verificar resultados",
  "sms.empty.analyzeManuallyBtn": "Analizar un mensaje manualmente",

  // Status badges
  "sms.badge.scam": "ESTAFA",
  "sms.badge.safe": "SEGURO",
  "sms.badge.legitimate": "LEGÍTIMO",

  // Card labels
  "sms.card.message": "Mensaje",
  "sms.card.deleteTitle": "Eliminar análisis",
  "sms.card.delete": "Eliminar",

  // Confidence descriptions
  "sms.confidence.veryLikelyScam": "Muy probable que sea estafa ({pct}%)",
  "sms.confidence.likelyScam": "Probable estafa ({pct}%)",
  "sms.confidence.possiblyScam": "Posible estafa ({pct}%)",
  "sms.confidence.veryLikelySafe": "Muy probable que sea seguro ({pct}%)",
  "sms.confidence.likelySafe": "Probable que sea seguro ({pct}%)",
  "sms.confidence.possiblySafe": "Posiblemente seguro ({pct}%)",

  // Detail view
  "sms.detail.back": "Volver a los resultados",
  "sms.detail.scamDetected": "ESTAFA DETECTADA",
  "sms.detail.noThreats": "NO SE DETECTARON AMENAZAS",
  "sms.detail.scamSubtitle":
    "Este mensaje contiene contenido sospechoso o fraudulento",
  "sms.detail.safeSubtitle": "Este mensaje parece ser seguro",
  "sms.detail.shareIndividual":
    "Disponible para planes Familiar o Familiar Extendido",
  "sms.detail.shareFamily": "Compartir esta alerta con familiares",
  "sms.detail.share": "Compartir",

  // Share button props
  "sms.share.threatType": "Estafa por SMS",
  "sms.share.threatTitle": "Estafa por SMS detectada",

  // Threat flags
  "sms.flag.suspiciousLinks": "Vínculos sospechosos",
  "sms.flag.urgentLanguage": "Lenguaje urgente",
  "sms.flag.financialThreats": "Amenazas financieras",

  // Section headings (detail panel)
  "sms.section.analysisDetails": "Detalles del análisis",
  "sms.section.analysisDetailsSubtitle": "Lo que encontramos sobre este SMS",
  "sms.section.whatToDo": "¿Qué deberías hacer?",
  "sms.section.explanation": "Explicación",
  "sms.section.noSmsSelected": "Ningún SMS seleccionado",
  "sms.section.noSmsSelectedDesc":
    "Selecciona un SMS de la lista para ver los resultados detallados del análisis aquí.",

  // Original message
  "sms.message.viewOriginal": "Ver mensaje original",
  "sms.message.show": "Mostrar contenido del mensaje",
  "sms.message.hide": "Ocultar contenido del mensaje",
  "sms.message.noContent": "No hay contenido de mensaje disponible.",

  // Errors & quota
  "sms.error.tryAgain": "Intentar de nuevo",
  "sms.error.notAuthenticated": "Usuario no autenticado",
  "sms.error.loadFailed":
    "No se pudieron cargar los análisis de SMS. Intenta de nuevo.",
  "sms.error.notFound": "No se pudo encontrar el análisis de SMS solicitado.",
  "sms.error.loadNotifFailed":
    "No se pudo cargar el análisis de SMS solicitado.",
  "sms.error.deleteFailed":
    "No se pudo eliminar el análisis de SMS. Intenta de nuevo.",
  "sms.quota.title": "Límite diario alcanzado",
  "sms.quota.body":
    "Has alcanzado tu límite diario de 25 análisis de SMS. Tu cuota se restablecerá mañana.",

  // ── Manual Text/SMS Analysis (TextMessageAnalysis) ───────────────────────
  "textAnalysis.page.title": "Análisis manual de texto/SMS",
  "textAnalysis.page.subtitle":
    "Analiza números de teléfono y mensajes de texto para detectar posibles estafas y amenazas de seguridad",

  // Loading
  "textAnalysis.loading.title": "Analizando mensaje de texto...",
  "textAnalysis.loading.subtitle":
    "Por favor espera mientras verificamos posibles estafas",

  // Form
  "textAnalysis.form.heading": "Entradas",
  "textAnalysis.form.phoneLabel": "Número de teléfono",
  "textAnalysis.form.phonePlaceholder": "Ingresar número de teléfono",
  "textAnalysis.form.invalidPhone": "Número de teléfono inválido",
  "textAnalysis.form.messageLabel": "Contenido del mensaje",
  "textAnalysis.form.messagePlaceholder":
    "Pega o escribe el contenido del mensaje de texto a analizar...",
  "textAnalysis.form.charCount": "{count}/1000 caracteres",
  "textAnalysis.form.contextLabel": "Contexto (opcional)",
  "textAnalysis.form.contextPlaceholder":
    "¿Algún detalle o situación adicional que agregar?",
  "textAnalysis.form.analyzing": "Analizando...",
  "textAnalysis.form.analyzeBtn": "Analizar contenido",
  "textAnalysis.form.clearBtn": "Limpiar",

  // Mobile form section header
  "textAnalysis.mobile.formSection": "Entradas del mensaje de texto",

  // Results
  "textAnalysis.results.heading": "Detalles del análisis",
  "textAnalysis.results.whatToDo": "¿Qué deberías hacer?",
  "textAnalysis.results.explanation": "Explicación",
  "textAnalysis.results.scamType": "Tipo de estafa:",
  "textAnalysis.results.completedOn": "Análisis completado el",
  "textAnalysis.results.noAnalysis": "Sin análisis disponible",
  "textAnalysis.results.noAnalysisDesc":
    "Proporciona las entradas para ver los resultados detallados aquí.",

  // Mobile results
  "textAnalysis.mobile.viewResults": "Ver resultados del análisis",
  "textAnalysis.mobile.backToForm": "Volver al formulario",
  "textAnalysis.mobile.noThreats": "NO SE DETECTARON AMENAZAS",
  "textAnalysis.mobile.riskDetectedSuffix": "RIESGO DETECTADO",
  "textAnalysis.mobile.shareIndividual":
    "Disponible para planes Familiar o Familiar Extendido",
  "textAnalysis.mobile.shareFamily": "Compartir esta alerta con familiares",
  "textAnalysis.mobile.share": "Compartir",
  "textAnalysis.mobile.newScan": "Nuevo análisis",
  "textAnalysis.mobile.noResults.title": "Aún no hay resultados",
  "textAnalysis.mobile.noResults.desc":
    "Ingresa los datos del mensaje para ver los resultados del análisis aquí.",
  "textAnalysis.mobile.goToForm": "Ir al formulario",

  // Phone analysis section
  "textAnalysis.phone.title": "Análisis de número de teléfono",
  "textAnalysis.phone.riskLevel": "Nivel de riesgo",
  "textAnalysis.phone.summary": "Resumen",
  "textAnalysis.phone.analysis": "Análisis",
  "textAnalysis.phone.whatToDo": "¿Qué deberías hacer?",
  "textAnalysis.phone.dbResults":
    "Resultados de verificación en base de datos ({count})",
  "textAnalysis.phone.foundInDb": "Encontrado en base de datos",
  "textAnalysis.phone.notFound": "No encontrado",
  "textAnalysis.phone.reports": "Informes",
  "textAnalysis.phone.category": "Categoría",
  "textAnalysis.phone.lastReported": "Último reporte",
  "textAnalysis.phone.status": "Estado",

  // Text/SMS analysis section
  "textAnalysis.textSection.title": "Análisis de texto/SMS",
  "textAnalysis.textSection.riskLevel": "Nivel de riesgo",
  "textAnalysis.textSection.confidence": "Confianza",
  "textAnalysis.textSection.category": "Categoría",
  "textAnalysis.textSection.scamType": "Tipo de estafa",
  "textAnalysis.textSection.riskScore": "Puntuación de riesgo",
  "textAnalysis.textSection.analysis": "Análisis",
  "textAnalysis.textSection.explanation": "Explicación",
  "textAnalysis.textSection.whatToDo": "¿Qué deberías hacer?",
  "textAnalysis.textSection.reasoning": "Razonamiento",

  // Errors
  "textAnalysis.error.emptyInput":
    "Por favor ingresa un número de teléfono y/o mensaje de texto para analizar.",
  "textAnalysis.error.timeout":
    "Se agotó el tiempo de espera. Por favor revisa tu conexión e intenta de nuevo.",
  "textAnalysis.error.server":
    "Error del servidor ({status}): {text}. Intenta de nuevo.",
  "textAnalysis.error.network":
    "Error de red. Por favor revisa tu conexión a internet e intenta de nuevo.",
  "textAnalysis.error.generic":
    "Error de análisis. Por favor intenta de nuevo.",

  // ─── Text Conversation Analysis ───────────────────────────────────────────
  "textConv.page.title": "Análisis de conversación de texto",
  "textConv.page.subtitle":
    "Verifica tu conversación de texto para detectar señales de alerta de estafas. Introduce tus mensajes en orden para detectar patrones sospechosos como estafas románticas, fraude de inversión y robo de identidad.",
  "textConv.mobile.subtitle":
    "Verifica tu conversación de texto para detectar señales de alerta de estafas y patrones sospechosos",
  "textConv.loading.title": "Analizando historial de conversación...",
  "textConv.loading.subtitle":
    "Por favor espera mientras verificamos posibles patrones de estafa",
  "textConv.error.missingPhone": "Por favor introduce un número de teléfono.",
  "textConv.error.noMessages":
    "Por favor introduce al menos un mensaje para analizar.",
  "textConv.error.timeout":
    "La solicitud expiró. Por favor verifica tu conexión e intenta de nuevo.",
  "textConv.error.server":
    "Error del servidor ({status}): {text}. Intenta de nuevo.",
  "textConv.error.network":
    "Error de red. Por favor revisa tu conexión a internet e intenta de nuevo.",
  "textConv.error.generic": "Error de análisis. Por favor intenta de nuevo.",
  "textConv.tab.input": "Entrada",
  "textConv.tab.results": "Resultados",
  "textConv.form.heading": "Ingresa los detalles de la conversación",
  "textConv.form.sectionTitle": "Historial de mensajes",
  "textConv.form.subtitle":
    "Ingresa tu conversación de texto para verificar patrones de estafa",
  "textConv.form.conversationInputs": "Entradas de conversación",
  "textConv.form.senderInfo": "Info del remitente",
  "textConv.form.messagesLabel":
    "Mensajes (haz clic en los iconos para marcar el remitente)",
  "textConv.form.messagesTapLabel":
    "Mensajes (toca los iconos para marcar el remitente)",
  "textConv.form.addMessage": "Agregar otro mensaje",
  "textConv.form.analyzeBtn": "Analizar conversación",
  "textConv.form.clearAll": "Limpiar todo",
  "textConv.form.fromThem": "De ellos",
  "textConv.form.fromYou": "De ti",
  "textConv.form.them": "Ellos",
  "textConv.form.you": "Tú",
  "textConv.form.theirMessage": "Su mensaje...",
  "textConv.form.yourMessage": "Tu mensaje...",
  "textConv.form.remove": "Eliminar",
  "textConv.form.invalid": "Inválido",
  "textConv.results.heading": "Resultados de detección de estafas",
  "textConv.results.conversationHeading":
    "Resultados del análisis de conversación",
  "textConv.results.overallRisk": "Evaluación general de riesgo",
  "textConv.results.riskType": "Tipo de riesgo",
  "textConv.results.detailedBreakdown": "Desglose detallado",
  "textConv.results.conversationPattern":
    "Análisis de patrones de conversación",
  "textConv.results.recommendations": "Recomendaciones",
  "textConv.results.detectedThreats": "Amenazas detectadas",
  "textConv.results.riskIndicators": "Indicadores de riesgo",
  "textConv.results.recommendationLabel": "Recomendación",
  "textConv.empty.readyTitle": "Listo para analizar",
  "textConv.empty.readyDesc":
    "Introduce tu conversación de mensajes a la izquierda para verificar patrones de estafa y obtener recomendaciones de seguridad.",
  "textConv.empty.noResultsTitle": "Listo para verificar mensajes",
  "textConv.empty.noResultsDesc":
    "Completa la pestaña de entrada con tu conversación, luego regresa aquí para ver el análisis de seguridad.",
  "textConv.empty.noResultsMobile":
    "Envía tu conversación para ver los resultados del análisis aquí.",
  "textConv.risk.highRecommendation":
    "Extrema precaución y evita responder a este mensaje.",
  "textConv.risk.mediumRecommendation":
    "Procede con precaución y verifica al remitente antes de tomar cualquier acción.",
  "textConv.risk.lowRecommendation":
    "Esto parece seguro, pero mantente siempre alerta.",
  "textConv.risk.unknownRecommendation":
    "No se puede determinar la seguridad por falta de información suficiente. Usa tu mejor criterio.",

  // ── Phone Number Scam Check ────────────────────────────────────────
  "phoneCheck.error.enterPhone": "Por favor introduce un número de teléfono",
  "phoneCheck.error.invalidPhone":
    "Por favor introduce un número de teléfono válido para el país seleccionado",
  "phoneCheck.error.incompletePhone":
    "Por favor introduce un número de teléfono completo para el país seleccionado",
  "phoneCheck.error.generic":
    "Ocurrió un error al analizar el número de teléfono",

  // ── Image Upload ──────────────────────────────────────────────────
  "imageUpload.header": "Subir imagen para análisis",
  "imageUpload.quota.title": "Límite diario alcanzado",
  "imageUpload.quota.body":
    "Has alcanzado tu límite diario de 25 cargas de imágenes. Tu cuota se restablecerá mañana.",
  "imageUpload.paste.title": "Pegar captura de pantalla aquí",
  "imageUpload.paste.hint": "Toma una captura de pantalla, luego presiona",
  "imageUpload.paste.helpTitle": "Cómo tomar una captura de pantalla",
  "imageUpload.paste.howTo":
    "\ud83d\udcf8 Cómo capturar y pegar una captura de pantalla:",
  "imageUpload.paste.winTab": "\ud83e\ude9f Windows",
  "imageUpload.paste.macTab": "\ud83c\udf4e Mac",
  "imageUpload.paste.win.step1label": "Paso 1 - Capturar:",
  "imageUpload.paste.win.step1press": "Presiona",
  "imageUpload.paste.win.step1detail":
    "La pantalla se oscurecerá y aparecerá una cruz",
  "imageUpload.paste.win.step2label": "Paso 2 - Seleccionar:",
  "imageUpload.paste.win.step2action":
    "Haz clic y arrastra para seleccionar el área que deseas capturar",
  "imageUpload.paste.win.step2detail":
    "La captura de pantalla ahora está copiada en tu portapapeles",
  "imageUpload.paste.win.step3label": "Paso 3 - Pegar:",
  "imageUpload.paste.win.step3action":
    "Asegúrate de estar en esta página, luego presiona",
  "imageUpload.paste.win.step3detail":
    "Tu captura de pantalla aparecerá automáticamente",
  "imageUpload.paste.mac.step1label": "Paso 1 - Capturar:",
  "imageUpload.paste.mac.step1press": "Presiona",
  "imageUpload.paste.mac.step1detail": "Aparecerá el cursor en forma de cruz",
  "imageUpload.paste.mac.step2label": "Paso 2 - Seleccionar:",
  "imageUpload.paste.mac.step2action":
    "Haz clic y arrastra para seleccionar el área",
  "imageUpload.paste.mac.step2detail":
    "La captura de pantalla se guarda en tu escritorio",
  "imageUpload.paste.mac.step3label": "Paso 3 - Copiar:",
  "imageUpload.paste.mac.step3action":
    'Haz clic derecho en la imagen de la captura y selecciona "Copiar" o "Guardar en portapapeles"',
  "imageUpload.paste.mac.step3detailA": "O usa",
  "imageUpload.paste.mac.step3detailB":
    "para copiar directamente al portapapeles",
  "imageUpload.paste.mac.step4label": "Paso 4 - Pegar:",
  "imageUpload.paste.mac.step4action":
    "Asegúrate de estar en esta página, luego presiona",
  "imageUpload.paste.mac.step4detail":
    "Tu captura de pantalla aparecerá automáticamente",
  "imageUpload.browse.title": "Haz clic para buscar archivos",
  "imageUpload.browse.hint1": "O arrastra y suelta un archivo de imagen aquí",
  "imageUpload.browse.hint2":
    "JPEG, PNG, GIF, WebP, BMP, TIFF, SVG (máx. 10 MB)",
  "imageUpload.divider": "O",
  "imageUpload.preview.alt": "Vista previa",
  "imageUpload.context.label": "Contexto (opcional)",
  "imageUpload.context.placeholder":
    "¿Algún detalle o situación adicional que agregar? (p. ej. 'Recibí esta imagen en un mensaje de un remitente desconocido') Cuanto más contexto proporciones, mejor podremos analizar el riesgo.",
  "imageUpload.analyzeBtn": "Analizar",
  "imageUpload.status.starting": "Iniciando carga...",
  "imageUpload.status.complete": "¡Carga completa! Analizando...",
  "imageUpload.whatNext.title": "¿Qué pasa a continuación?",
  "imageUpload.whatNext.step1":
    "Tu imagen se sube de forma segura a nuestro almacenamiento en la nube",
  "imageUpload.whatNext.step2":
    "El análisis de seguridad verifica el malware y contenido inapropiado",
  "imageUpload.whatNext.step3":
    "La IA analiza la imagen en busca de indicadores de estafa mediante visión artificial",
  "imageUpload.whatNext.step4":
    "Recibes un informe detallado con evaluación de riesgos y recomendaciones",
  "imageUpload.security.badge":
    "Tus imágenes están cifradas y almacenadas de forma segura",
  "imageUpload.error.selectFile": "Por favor selecciona un archivo primero",
  "imageUpload.error.invalidType":
    "Tipo de archivo no válido. Por favor sube una imagen (JPEG, PNG, GIF, WebP, BMP, TIFF, SVG)",
  "imageUpload.error.tooLarge":
    "Archivo demasiado grande. El tamaño máximo es 10 MB",
  "imageUpload.error.quotaExceeded":
    "Límite diario de carga alcanzado (25/día). Tu cuota se restablecerá mañana.",
  "imageUpload.error.uploadFailed":
    "Error al cargar. Por favor intenta de nuevo.",
  "imageUpload.toast.pasted":
    "¡Captura de pantalla pegada desde el portapapeles!",
  // ── Notifications ────────────────────────────────────────────────
  "notification.title": "Notificaciones",
  "notification.subtitle":
    "Mant\u00e9nte al d\u00eda con la actividad de tu cuenta y los resultados de an\u00e1lisis de correo",
  "notification.loading": "Cargando notificaciones...",
  "notification.empty": "Sin notificaciones",
  "notification.empty.subtitle":
    "\u00a1Est\u00e1s al d\u00eda! Nada que mostrar aqu\u00ed.",
  "notification.markAllRead": "Marcar todo como le\u00eddo",
  "notification.markAllAsRead.tooltip": "Marcar todo como le\u00eddo",
  "notification.markAsRead": "Marcar como le\u00eddo",
  "notification.dismiss": "Descartar notificaci\u00f3n",
  "notification.viewAll": "Ver todas las notificaciones",
  "notification.viewDetails": "Ver detalles",
  "notification.count.one": "1 notificaci\u00f3n",
  "notification.count.many": "{count} notificaciones",
  "notification.stat.total": "Total",
  "notification.stat.unread": "Sin leer",
  "notification.stat.emailAnalysis": "An\u00e1lisis de correo",
  "notification.stat.status": "Estado",
  "notification.sections.all.title": "Todas las notificaciones",
  "notification.sections.all.subtitle":
    "Ve todas tus notificaciones en un solo lugar",
  "notification.sections.unread.title": "Sin leer",
  "notification.sections.unread.subtitle":
    "Notificaciones que a\u00fan no has le\u00eddo",
  "notification.sections.emailAnalysis.title": "An\u00e1lisis de correo",
  "notification.sections.emailAnalysis.subtitle":
    "Resultados y alertas de an\u00e1lisis de seguridad de correo",
  "notification.sections.system.title": "Sistema",
  "notification.sections.system.subtitle":
    "Notificaciones del sistema y de la cuenta",
  "notification.verificationFailed.message":
    "\u26a0\ufe0f El elemento puede haber sido eliminado. \u00bfDeseas eliminar esta notificaci\u00f3n?",
  "notification.verificationFailed.confirmDelete": "S\u00ed, eliminar",
  "notification.verificationFailed.keepIt": "No, conservarla",
  "notification.unreadCount.one": "1 notificaci\u00f3n sin leer",
  "notification.unreadCount.many": "{count} notificaciones sin leer",
  "notification.tapToMarkAllRead": "Toca para marcar todo como le\u00eddo",
  "notification.markAll": "Marcar todo",
  "notification.allCaughtUp": "\u00a1Est\u00e1s al d\u00eda!",
  "notification.noItemsToShow": "No hay notificaciones que mostrar.",
  "notification.checkForUpdates": "Buscar actualizaciones",

  // ── Account & Billing (AccountBilling.js) ─────────────────────────
  "ab.title": "Suscripción y pago",
  "ab.subtitle": "Administra tu cuenta, suscripción y pagos en un solo lugar",
  "ab.loading": "Cargando cuenta y facturación...",
  "ab.loading.subscription": "Cargando suscripción...",
  "ab.loading.processing": "Procesando tu suscripción...",
  "ab.refreshTitle": "Actualizar todos los datos",
  "ab.error.unableToLoad": "No se pudo cargar la información de la cuenta.",
  "ab.error.failedToLoad":
    "Error al cargar la información de la cuenta. Por favor intenta de nuevo más tarde.",
  "ab.stat.account": "Suscripción",
  "ab.stat.myAccount": "Mi cuenta",
  "ab.stat.noPlan": "Sin plan activo",
  "ab.stat.status": "Estado",
  "ab.stat.users": "Usuarios",
  "ab.stat.usersUnlimited": "{count} (ilimitado)",
  "ab.stat.manageBtn": "Administrar \u2192",
  "ab.stat.manageTitle": "Administrar usuarios",
  "ab.stat.memberSince": "Próximo pago",
  "ab.status.active": "Activo",
  "ab.status.inactive": "Inactivo",
  "ab.status.lapsed": "Vencido",
  "ab.status.noSub": "Sin suscripción",
  "ab.status.cancelling": "Cancelando",
  "ab.status.gracePeriod": "Período de gracia",
  "ab.status.downgradePending": "Degradación pendiente",
  "ab.badge.freeTrial": "Prueba gratuita",
  "ab.badge.freeTrialTitle": "Estás en una prueba gratuita",
  "ab.section.subscription.title": "Suscripción",
  "ab.section.subscription.subtitle":
    "Administra tu plan, funciones y configuración de renovación",
  "ab.section.payment.title": "Métodos de pago",
  "ab.section.payment.subtitle":
    "Administra tus tarjetas guardadas y opciones de pago",
  "ab.section.history.title": "Historial de facturación",
  "ab.section.history.subtitle":
    "Ver transacciones pasadas y actividad de pagos",
  "ab.cycle.monthly": "Mensual",
  "ab.cycle.yearly": "Anual",
  "ab.cycle.autoRenew": " \u2022 Renovación automática",
  "ab.status.cancelled": "Cancelado",
  "ab.status.expired": "Expirado",
  "ab.plan.currentPlan": "Plan actual",
  "ab.downgrade.scheduled": "Degradación programada",
  "ab.downgrade.switchNotice":
    "Tu plan cambiará a {name}{price} el {date}. Mantienes acceso completo a tu plan actual hasta entonces.",
  "ab.downgrade.nextBillingDate": "tu próxima fecha de facturación",
  "ab.downgrade.cancelling": "Cancelando...",
  "ab.downgrade.keepCurrentPlan": "Mantener plan actual",
  "ab.promo.validateError":
    "No se pudo validar el código de promoción. Por favor, inténtalo de nuevo.",
  "ab.promo.scheduled.title": "Código de promoción programado",
  "ab.promo.scheduled.codePrefix": "El código",
  "ab.promo.scheduled.discountSuffix": "({pct}% de descuento)",
  "ab.promo.scheduled.effectOn":
    "entrará en vigencia el {date}. Tu período actual no se verá afectado.",
  "ab.promo.scheduled.nextBillingDate": "tu próxima fecha de facturación",
  "ab.promo.scheduled.cancelling": "Cancelando\u2026",
  "ab.promo.scheduled.remove": "Eliminar promo programada",
  "ab.promo.apply.toggle": "¿Tienes un código de promoción?",
  "ab.promo.apply.label":
    "Aplicar un código de promoción a tu próximo ciclo de facturación",
  "ab.promo.apply.placeholder": "Ingresar código de promoción",
  "ab.promo.apply.checking": "Verificando\u2026",
  "ab.promo.apply.check": "Verificar",
  "ab.promo.apply.applying": "Aplicando...",
  "ab.promo.apply.applyBtn": "Aplicar código de promoción",
  "ab.promo.apply.cancelTitle": "Cancelar",
  // eslint-disable-next-line no-template-curly-in-string
  "ab.promo.apply.newPrice":
    "Nuevo precio: ${price}/{cycle} a partir de tu próxima fecha de facturación.",
  "ab.promo.trial.noticeTitle": "Código de promoción guardado para tu prueba",
  "ab.promo.trial.noticeBody":
    "El código {code}{discount} se aplicará automáticamente cuando termine tu prueba y se convierta en un plan de pago. No se te cobrará durante la prueba.",
  "ab.promo.trial.remove": "Eliminar promo guardada",
  "ab.promo.trial.toggle":
    "¿Tienes un código de promoción para cuando termine tu prueba?",
  "ab.promo.trial.label":
    "Guarda un código de promoción ahora — se aplica automáticamente cuando tu prueba se convierte en un plan de pago",
  "ab.promo.trial.hint":
    "No se te cobrará durante la prueba. Cuando termine, necesitarás un método de pago registrado y el descuento se aplicará a tu primer cobro.",
  "ab.promo.trial.applyBtn": "Guardar código de promoción",
  "ab.promo.trial.applying": "Guardando\u2026",
  // eslint-disable-next-line no-template-curly-in-string
  "ab.promo.trial.newPrice":
    "Después de tu prueba, pagarás ${price}/{cycle} (sin cargo durante la prueba).",
  "ab.promo.trial.savedNote":
    "El código {code} está guardado y se aplicará a este cobro.",

  // ── Trial Reminder Modal ──
  "trialReminder.title": "⏰ Recordatorio de periodo de prueba",
  "trialReminder.messagePrefix": "Tu periodo de prueba terminará en",
  "trialReminder.day": "día",
  "trialReminder.days": "días",
  "trialReminder.dateLabel": "La prueba termina el:",
  "trialReminder.ownerInfo":
    "Para continuar disfrutando de acceso ininterrumpido a todas las funciones, configura métodos de pago antes de que expire tu prueba.",
  "trialReminder.memberInfo":
    "Por favor, alerta al propietario de la cuenta para que configure métodos de pago antes de que expire la prueba y puedas seguir disfrutando de todas las funciones sin interrupciones.",
  "trialReminder.btnPayments": "Ver métodos de pago",
  "trialReminder.btnRemind": "Recuérdame más tarde",
  "trialReminder.btnAlert": "Alertar al propietario de la cuenta",

  "ab.planDetails.toggle": "Funciones y beneficios del plan",
  "ab.planDetails.limitsHeading": "Límites del plan",
  "ab.planDetails.users": "Miembros protegidos",
  "ab.planDetails.simsPerMonth": "Simulaciones de estafa/mes",
  "ab.planDetails.reportsPerMonth": "Informes de seguridad/mes",
  "ab.planDetails.featuresHeading": "Funciones incluidas",
  "ab.planDetails.scamDetection": "Detección de estafas en tiempo real",
  "ab.planDetails.phishingSimulations": "Simulaciones de phishing y SMS",
  "ab.planDetails.scamAlerts": "Alertas de estafas y amenazas",
  "ab.planDetails.advancedAnalytics": "Análisis de amenazas de estafa",
  "ab.planDetails.customTraining": "Escenarios de entrenamiento personalizados",
  "ab.planDetails.prioritySupport": "Atención al cliente prioritaria",
  "ab.action.chooseDifferentPlan": "Elegir un plan diferente",
  "ab.action.changePlan": "Cambiar plan",
  "ab.action.addPaymentMethod": "Agregar método de pago",
  "ab.action.closeError": "Cerrar error",
  "ab.action.cancelSubscription": "Cancelar suscripción",
  "ab.action.resubscribing": "Resuscribiendo...",
  "ab.action.resubscribe": "Resuscribirse",
  "ab.action.converting": "Convirtiendo...",
  "ab.action.convertToFull": "Convertir a suscripción completa",
  "ab.action.browsePlans": "Ver planes",
  "ab.cancellationPending":
    "El acceso continúa hasta el final del período de facturación",
  "ab.noSubscription.title": "Sin suscripción activa",
  "ab.noSubscription.subtitle": "Elige un plan para comenzar con KinShield.",
  "ab.setupJourney.return": "Continuar configurando tu cuenta",
  "ab.renewal.label.gracePeriodEnds": "El período de gracia termina",
  "ab.renewal.sublabel.addPayment":
    "Agrega un método de pago para mantener tu suscripción activa",
  "ab.renewal.label.accessUntil": "Acceso hasta",
  "ab.renewal.sublabel.willNotRenew":
    "La suscripción no se renovará al finalizar la prueba",
  "ab.renewal.label.trialEnds": "La prueba termina",
  "ab.renewal.sublabel.autoRenews": "Se renueva automáticamente el {date}",
  "ab.renewal.label.nextRenewal": "Próxima renovación",
  "ab.renewal.sublabel.cancellationScheduled":
    "Cancelación programada \u2014 sin cargos adicionales",
  "ab.renewal.date.endOfBillingPeriod": "Fin del período de facturación",
  "ab.renewal.sublabel.subscriptionCancelled":
    "Suscripción cancelada \u2014 sin cargos adicionales",
  "ab.renewal.sublabel.planSwitchesTo":
    "El plan cambia a {name}{price} en la renovación",
  "ab.access.untilEndOfTrial":
    "Acceso completo a las funciones hasta el fin de la prueba",
  "ab.access.until": "Acceso completo a las funciones hasta {date}",
  "ab.modal.planChange.titleDowngrade": "¿Degradar el plan de suscripción?",
  "ab.modal.planChange.titleChange": "¿Cambiar el plan de suscripción?",
  "ab.modal.planChange.messageDowngrade":
    "Por favor revisa los detalles a continuación antes de confirmar tu degradación.",
  "ab.modal.planChange.messageChange":
    "¿Estás seguro de que deseas cambiar tu plan de suscripción?",
  "ab.modal.planChange.labelCurrent": "Actual",
  "ab.modal.planChange.labelNew": "Nuevo",
  "ab.modal.planChange.noteCurrent":
    "El plan actual permanece activo hasta {date}",
  "ab.modal.planChange.noteNewRate":
    "El nuevo plan y la tarifa entran en vigor en tu próxima renovación",
  "ab.modal.planChange.noteDiscountWarning":
    "Tu descuento del {pct}% puede no aplicarse al nuevo plan",
  "ab.modal.planChange.noteUpgradeImmediate":
    "Mejora inmediata \u2014 puede aplicarse un cargo prorrateado",
  "ab.modal.planChange.noteChangeImmediate":
    "Actualizado inmediatamente \u2014 sin cargos adicionales",
  "ab.modal.planChange.noteDiscountMayNotCarry":
    "Tu descuento del {pct}% puede no transferirse",
  "ab.modal.planChange.confirmDowngrade": "Confirmar degradación",
  "ab.modal.planChange.confirmChange": "Cambiar plan",
  "ab.modal.planChange.cancelKeep": "Mantener plan actual",
  "ab.modal.cancel.title": "¿Cancelar suscripción?",
  "ab.modal.cancel.messageDefault":
    "¿Estás seguro de que deseas cancelar tu suscripción?",
  "ab.modal.cancel.messageTrial":
    "¿Estás seguro de que deseas cancelar? Tu acceso de prueba continuará hasta que finalice el período de prueba \u2014 no se te cobrará.",
  "ab.modal.cancel.confirmText": "Cancelar suscripción",
  "ab.modal.cancel.cancelText": "Mantener suscripción",
  "ab.modal.cancel.reasonPlaceholder":
    "Ayúdanos a mejorar: ¿Por qué cancelas? (opcional)",
  "ab.modal.cancel.keepFeaturesUntil":
    "\u2705 Mantendrás acceso completo a las funciones de {plan} hasta {date}",
  "ab.modal.cancel.keepTrialFeatures":
    "\u2705 Mantendrás acceso completo a las funciones de {plan} hasta que tu prueba termine el {date}",
  "ab.modal.cancel.noTrialCharges":
    "\ud83d\udcb3 No se aplicarán cargos \u2014 tu prueba es gratuita",
  "ab.modal.cancel.willLoseAccess":
    "\ud83deb48 Después de esa fecha, perderás acceso a todas las funciones de pago incluyendo simulaciones, informes, acceso API y espacios adicionales de usuario",
  "ab.modal.cancel.noBillingAfter":
    "\ud83d\udcb3 No habrá más cargos tras la cancelación",
  "ab.modal.cancel.canResubscribe":
    "\ud83d\udd04 Puedes resuscribirte en cualquier momento desde esta página",
  "ab.modal.cancel.noSubText":
    "Puedes resuscribirte en cualquier momento desde esta página.",
  "ab.modal.convert.title": "¿Convertir a suscripción completa?",
  "ab.modal.convert.message":
    "Estás a punto de convertir tu prueba gratuita en una suscripción de pago.",
  "ab.modal.convert.keepFeatures":
    "\u2705 Conservarás todas las funciones de tu {plan} actual",
  "ab.modal.convert.chargeInfo": `\ud83d\udcb3 Se cobrará \${amount}/{period} a tu método de pago inmediatamente`,
  "ab.modal.convert.autoRenewEnabled":
    "\ud83d\udd04 La renovación automática seguirá activada",
  "ab.modal.convert.autoRenewDisabled":
    "\ud83d\udd04 La renovación automática seguirá desactivada",
  "ab.modal.convert.cancelAnytime":
    "\u2753 Puedes cancelar en cualquier momento desde esta página",
  "ab.modal.convert.confirmText": "Convertir a pago",
  "ab.modal.convert.cancelText": "Mantener prueba",
  "ab.daysLeft": "{n}d restantes",

  // ── Billing issue banner ───────────────────────────────────────────
  "ab.billing.title": "No pudimos procesar tu pago",
  "ab.billing.failedOn": "Tu pago más reciente falló el {date}.",
  "ab.billing.failedNoDate": "Tu pago más reciente falló.",
  "ab.billing.retryOn": "Intentaremos de nuevo el {date}.",
  "ab.billing.updateOrPayNow":
    "Actualiza tu método de pago o paga ahora para mantener tu suscripción activa.",
  "ab.billing.payNow": "Pagar ahora",
  "ab.billing.updatePaymentMethod": "Actualizar método de pago",
  "ab.grace.title": "Período de gracia activo",
  "ab.grace.message.withDate":
    "El acceso completo continúa hasta {date}. Agrega un método de pago para evitar interrupciones del servicio.",
  "ab.grace.message.noDate":
    "El acceso completo está disponible temporalmente. Agrega un método de pago para evitar interrupciones del servicio.",
  "ab.grace.nonOwner.withDate":
    "El acceso completo continúa hasta {date}. No tienes permisos para administrar la facturación de esta cuenta. Contacta al propietario de la cuenta para agregar o actualizar un método de pago antes de que termine el período de gracia.",
  "ab.grace.nonOwner.noDate":
    "El acceso completo está disponible temporalmente. No tienes permisos para administrar la facturación de esta cuenta. Contacta al propietario de la cuenta para agregar o actualizar un método de pago lo antes posible.",
  "ab.grace.cta.updatePayment": "Actualizar método de pago",

  // ── Misc missing keys ─────────────────────────────────────────────
  "ab.period.month": "mes",
  "ab.period.year": "año",
  "ab.price.off": "{pct}% de descuento",
  "ab.modal.planChange.users": "{n} usuarios",
  "ab.downgrade.fallbackPlan": "un plan inferior",
  "ab.badge.downgradePendingTitle": "Degradación pendiente",
  "ab.modal.cancel.endFallback": "fin del período de facturación",
  "selectedPlan.promo.remove": "Eliminar",

  // ── Payment Methods (PaymentMethods.js) ────────────────────────────
  "paymentMethods.addSuccess": "\u00a1Método de pago agregado exitosamente!",
  "paymentMethods.addFailed": "Error al agregar el método de pago",
  "paymentMethods.deletedSuccess": "Método de pago eliminado exitosamente",
  "paymentMethods.newDefaultSet":
    "{brand} \u2022\u2022\u2022\u2022{last4} ahora es tu método de pago predeterminado",
  "paymentMethods.defaultUpdated": "Método de pago predeterminado actualizado",
  "paymentMethods.loadFailed": "Error al cargar los métodos de pago",
  "paymentMethods.deleteFailed": "Error al eliminar el método de pago",
  "paymentMethods.setDefaultFailed":
    "Error al establecer el método de pago predeterminado",
  "paymentMethods.form.cardholderName": "Nombre del titular",
  "paymentMethods.form.nameOnCard": "Nombre en la tarjeta",
  "paymentMethods.form.cardNumber": "Número de tarjeta",
  "paymentMethods.form.expiration": "Fecha de vencimiento",
  "paymentMethods.form.cvv": "CVV",
  "paymentMethods.form.zipCode": "Código postal",
  "paymentMethods.form.setDefault":
    "Establecer como método de pago predeterminado",
  "paymentMethods.form.setDefaultRequired":
    "(obligatorio para la primera tarjeta)",
  "paymentMethods.form.adding": "Agregando...",
  "paymentMethods.form.continue": "Continuar",
  "paymentMethods.savedMethods": "Métodos de pago guardados",
  "paymentMethods.addNewCard": "Agregar nueva tarjeta",
  "paymentMethods.addNewTitle": "Agregar nuevo método de pago",
  "paymentMethods.activateInfo":
    "Por favor agrega un método de pago para activar tu plan.",
  "paymentMethods.noMethods": "Aún no hay métodos de pago guardados.",
  "paymentMethods.noMethodsHint": "Agrega un método de pago para comenzar.",
  "paymentMethods.expires": "Vence {month}/{year}",
  "paymentMethods.default": "Predeterminado",
  "paymentMethods.setAsDefault": "Establecer como predeterminado",
  "paymentMethods.remove": "Eliminar",
  "paymentMethods.activateSubscription": "Activar suscripción",
  "paymentMethods.autoPaySettings": "Configuración de pago automático",
  "paymentMethods.autoPayLabel": "Habilitar pagos automáticos",
  "paymentMethods.autoPayHelp":
    "Cobrar automáticamente a tu método de pago predeterminado cuando venzan los pagos",
  "paymentMethods.delete.title": "Eliminar método de pago",
  "paymentMethods.delete.message":
    "¿Estás seguro de que deseas eliminar este método de pago?",
  "paymentMethods.delete.details": "Esta acción no se puede deshacer.",
  "paymentMethods.delete.confirm": "Eliminar",
  "paymentMethods.setupJourney.return": "Continuar configurando tu cuenta",
  // ── PaymentMethods.js — keys used by the updated component ─────────
  "paymentMethods.loading": "Cargando métodos de pago...",
  "paymentMethods.savedTitle": "Métodos de pago guardados",
  "paymentMethods.addTitle": "Agregar nuevo método de pago",
  "paymentMethods.activatePlanInfo":
    "Por favor agrega un método de pago para activar tu plan.",
  "paymentMethods.noMethodsTitle": "Aún no hay métodos de pago guardados.",
  "paymentMethods.noMethodsSubtitle": "Agrega un método de pago para comenzar.",
  "paymentMethods.form.namePlaceholder": "Nombre en la tarjeta",
  "paymentMethods.form.expDate": "Fecha de vencimiento",
  "paymentMethods.form.cancel": "Cancelar",
  "paymentMethods.card.expires": "Vence {month}/{year}",
  "paymentMethods.card.default": "Predeterminado",
  "paymentMethods.card.setDefault": "Establecer como predeterminado",
  "paymentMethods.card.remove": "Eliminar",
  "paymentMethods.autoPayTitle": "Configuración de pago automático",
  "paymentMethods.autoPayEnable": "Habilitar pagos automáticos",
  "paymentMethods.autoPayDescription":
    "Cobrar automáticamente a tu método de pago predeterminado cuando se deban pagos",
  "paymentMethods.deleteModal.title": "Eliminar método de pago",
  "paymentMethods.deleteModal.message":
    "¿Estás seguro de que deseas eliminar este método de pago?",
  "paymentMethods.deleteModal.details": "Esta acción no se puede deshacer.",
  "paymentMethods.deleteModal.confirmText": "Eliminar",
  "paymentMethods.deleteModal.cancelText": "Cancelar",
  "paymentMethods.toast.added": "¡Método de pago agregado exitosamente!",
  "paymentMethods.toast.addFailed": "Error al agregar el método de pago",
  "paymentMethods.toast.deleted": "Método de pago eliminado exitosamente",
  "paymentMethods.toast.defaultSet":
    "{brand} ••••{last4} es ahora tu método de pago predeterminado",
  "paymentMethods.toast.loadFailed": "Error al cargar los métodos de pago",
  "paymentMethods.toast.deleteFailed": "Error al eliminar el método de pago",
  "paymentMethods.toast.defaultUpdated":
    "Método de pago predeterminado actualizado",
  "paymentMethods.toast.defaultFailed":
    "Error al establecer el método de pago predeterminado",
  "paymentMethods.stripeUnavailable":
    "Los pagos no están disponibles temporalmente. Nuestro equipo ha sido notificado. Por favor intenta más tarde o contacta a soporte.",

  // ── Payment History (PaymentHistory.js) ────────────────────────────
  "paymentHistory.loadFailed": "Error al cargar el historial de pagos",
  "paymentHistory.reconcileFailed":
    "Error al actualizar el historial de facturación desde Stripe",
  "paymentHistory.loading": "Cargando historial de pagos...",
  "paymentHistory.description.monthly": "Suscripción mensual",
  "paymentHistory.transactionHistory": "Historial de transacciones",
  "paymentHistory.refresh.label": "Actualizar historial de facturación",
  "paymentHistory.refresh.loading": "Actualizando...",
  "paymentHistory.refresh.title": "Obtener pagos faltantes de Stripe",
  "paymentHistory.filter.dateRange": "Filtrar por rango de fechas",
  "paymentHistory.filter.allTime": "Todo el tiempo",
  "paymentHistory.filter.last30": "Últimos 30 días",
  "paymentHistory.filter.last3Months": "Últimos 3 meses",
  "paymentHistory.filter.last6Months": "Últimos 6 meses",
  "paymentHistory.filter.lastYear": "Último año",
  "paymentHistory.filter.status": "Filtrar por estado",
  "paymentHistory.filter.all": "Todos",
  "paymentHistory.filter.completed": "Completado",
  "paymentHistory.filter.processing": "Procesando",
  "paymentHistory.filter.pending": "Pendiente",
  "paymentHistory.filter.failed": "Fallido",
  "paymentHistory.table.date": "Fecha",
  "paymentHistory.table.description": "Descripción",
  "paymentHistory.table.amount": "Monto",
  "paymentHistory.table.amountShort": "Mto.",
  "paymentHistory.table.method": "Método",
  "paymentHistory.table.status": "Estado",
  "paymentHistory.noHistory": "No hay historial de pagos disponible.",
  "paymentHistory.noMatch": "No hay pagos que coincidan con tus filtros.",
  "paymentHistory.reconcile.recovered":
    "{count} pago(s) faltante(s) recuperado(s)",
  "paymentHistory.reconcile.upToDate":
    "El historial de facturación ya está actualizado",
  // ── PaymentHistory.js — keys used by the updated component ─────────
  "paymentHistory.title": "Historial de transacciones",
  "paymentHistory.refreshLabel": "Actualizar historial de facturación",
  "paymentHistory.refreshing": "Actualizando...",
  "paymentHistory.refreshTitle": "Obtener pagos faltantes de Stripe",
  "paymentHistory.filter.dateLabel": "Filtrar por rango de fechas",
  "paymentHistory.filter.statusLabel": "Filtrar por estado",
  "paymentHistory.filter.last90": "Últimos 3 meses",
  "paymentHistory.filter.last180": "Últimos 6 meses",
  "paymentHistory.filter.last365": "Último año",
  "paymentHistory.toast.loadFailed": "Error al cargar el historial de pagos",
  "paymentHistory.toast.recovered": "{count} pago(s) faltante(s) recuperado(s)",
  "paymentHistory.toast.upToDate":
    "El historial de facturación ya está actualizado",
  "paymentHistory.toast.refreshFailed":
    "Error al actualizar el historial de facturación desde Stripe",

  // ── Subscription Actions (useSubscriptionActions.js) ──────────────────────
  "subscriptionActions.toast.selectPlanFirst":
    "Por favor selecciona un plan primero.",
  "subscriptionActions.toast.addPaymentToActivate":
    "Por favor agrega un método de pago para activar tu plan.",
  "subscriptionActions.toast.planChanged":
    "\u00a1Tu plan ha sido cambiado exitosamente a {plan}!",
  "subscriptionActions.toast.planActivated":
    "\u00a1{plan} activado exitosamente!",
  "subscriptionActions.toast.changePlanFailed":
    "Error al cambiar de plan. Por favor intenta de nuevo.",
  "subscriptionActions.toast.activatePlanFailed":
    "Error al activar el plan. Por favor intenta de nuevo.",
  "subscriptionActions.toast.addPaymentToSetup":
    "Agrega un método de pago para continuar la configuración de tu suscripción.",
  "subscriptionActions.toast.addPaymentToConvert":
    "Por favor agrega un método de pago para convertir tu prueba.",
  "subscriptionActions.toast.trialConverted":
    "\u00a1Convertido exitosamente a la suscripción completa de {plan}!",
  "subscriptionActions.toast.trialConvertFailed":
    "Error al convertir la prueba. Por favor intenta de nuevo.",
  "subscriptionActions.toast.cancelled":
    "Suscripción cancelada. Seguirás teniendo acceso completo hasta el final de tu período de facturación actual \u2014 no se realizarán más cargos.",
  "subscriptionActions.toast.cancelFailed":
    "Error al cancelar la suscripción. Por favor intenta de nuevo.",
  "subscriptionActions.toast.reactivated":
    "\u00a1Suscripción reactivada! Tu plan está activo nuevamente.",
  "subscriptionActions.toast.downgradeCancelled":
    "Degradación pendiente cancelada. Conservarás tu plan actual.",
  "subscriptionActions.toast.enterPromoCode":
    "Por favor ingresa un código de promoción.",
  "subscriptionActions.toast.promoScheduled":
    "Código de promoción programado. Tomará efecto en tu próxima fecha de facturación.",
  "subscriptionActions.toast.promoScheduleFailed":
    "Error al programar el código de promoción. Por favor intenta de nuevo.",
  "subscriptionActions.toast.promoCancelled":
    "Código de promoción programado cancelado.",
  "subscriptionActions.toast.promoCancelFailed":
    "Error al cancelar el código de promoción programado. Por favor intenta de nuevo.",

  // ── Payment Overview (PaymentOverview.js) ──────────────────────────────────
  "paymentOverview.toast.loadFailed": "Error al cargar la información de pago",

  // ── Service Plans (ServicePlans.js) ────────────────────────────────
  "servicePlans.loading": "Cargando planes de servicio...",
  "servicePlans.errorTitle": "Error al cargar los planes",
  "servicePlans.tryAgain": "Intentar de nuevo",
  "servicePlans.modalTitle": "Elige tu plan de servicio",
  "servicePlans.pageTitle": "Elige tu plan",
  "servicePlans.pageSubtitle":
    "Selecciona el plan perfecto para tus necesidades de seguridad",
  "servicePlans.featuresTitle": "Funciones incluidas:",
  "servicePlans.badge.popular": "Más popular",
  "servicePlans.badge.current": "Plan actual",
  "servicePlans.badge.switching": "Cambiando el",
  "servicePlans.badge.soon": "pronto",
  "servicePlans.btn.current": "Plan actual",
  "servicePlans.btn.downgradePending": "Degradación pendiente",
  "servicePlans.btn.upgrade": "Mejorar",
  "servicePlans.btn.downgrade": "Degradar",
  "servicePlans.btn.selected": "Seleccionado",
  "servicePlans.btn.select": "Seleccionar plan",
  "servicePlans.btn.back": "Atrás",
  "servicePlans.btn.goBack": "Volver",
  "servicePlans.price.free": "Gratis",
  "servicePlans.price.perMonth": "/mes",
  "servicePlans.price.perYear": "/año",
  "servicePlans.price.orPrefix": "o",
  "servicePlans.toast.upgraded": "Actualizado a",
  "servicePlans.toast.downgraded": "Degradado a",
  "servicePlans.error.loadFailed": "Error al cargar los planes de servicio",

  // Plan names and descriptions
  "servicePlans.plan.individual.name": "Individual",
  "servicePlans.plan.individual.desc":
    "Ideal para personas con necesidades completas de capacitaci\u00f3n en concienciaci\u00f3n sobre seguridad.",
  "servicePlans.plan.family.name": "Familiar",
  "servicePlans.plan.family.desc":
    "Ideal para familias que desean proteger su hogar.",
  "servicePlans.plan.extended_family.name": "Familia Extendida",
  "servicePlans.plan.extended_family.desc":
    "Protecci\u00f3n completa para familias m\u00e1s grandes o grupos peque\u00f1os con funciones avanzadas y soporte prioritario.",
  "servicePlans.plan.smb-10.name": "Equipo (hasta 10)",
  "servicePlans.plan.smb-10.desc":
    "Capacitaci\u00f3n en concienciaci\u00f3n sobre seguridad y protecci\u00f3n contra estafas para equipos peque\u00f1os de hasta 10 empleados.",
  "servicePlans.plan.smb-25.name": "Equipo (hasta 25)",
  "servicePlans.plan.smb-25.desc":
    "Capacitaci\u00f3n en concienciaci\u00f3n sobre seguridad y protecci\u00f3n contra estafas para equipos peque\u00f1os de hasta 25 empleados.",
  "servicePlans.plan.smb-50.name": "Empresa (hasta 50)",
  "servicePlans.plan.smb-50.desc":
    "Protecci\u00f3n y capacitaci\u00f3n integral para empresas en crecimiento de hasta 50 empleados.",
  "servicePlans.plan.smb-75.name": "Empresa (hasta 75)",
  "servicePlans.plan.smb-75.desc":
    "Protecci\u00f3n y capacitaci\u00f3n integral para empresas de hasta 75 empleados.",
  "servicePlans.plan.smb-100.name": "Empresa Plus (hasta 100)",
  "servicePlans.plan.smb-100.desc":
    "Programa completo de concienciaci\u00f3n sobre seguridad para organizaciones de hasta 100 empleados.",

  // Plan feature bullets
  "servicePlans.feature.covers1User": "Cubre 1 usuario",
  "servicePlans.feature.realtimeScam":
    "An\u00e1lisis de estafas en tiempo real para correos, mensajes de texto y publicaciones en redes sociales",
  "servicePlans.feature.screenshotScan":
    "Escaneo de capturas de pantalla e im\u00e1genes para detectar contenido fraudulento",
  "servicePlans.feature.phoneVerify":
    "Verificaci\u00f3n de n\u00fameros de tel\u00e9fono para identificar llamadas de estafadores",
  "servicePlans.feature.textConvo":
    "An\u00e1lisis de conversaciones de texto para detectar estafas rom\u00e1nticas y de inversi\u00f3n prolongadas",
  "servicePlans.feature.bizCheck":
    "Verificaci\u00f3n de negocios o personas para comprobar su legitimidad antes de transacciones",
  "servicePlans.feature.simPhishing":
    "Ataques de phishing simulados por correo y texto",
  "servicePlans.feature.trendingAlerts":
    "Alertas sobre estafas en tendencia y amenazas actuales",
  "servicePlans.feature.trainings":
    "Capacitaciones interactivas, cuestionarios y consejos pr\u00e1cticos de seguridad",
  "servicePlans.feature.monthlyReports":
    "Informes mensuales completos sobre el progreso",
  "servicePlans.feature.freeTrial":
    "Prueba gratuita de 30 d\u00edas con acceso completo a todas las funciones",
  "servicePlans.feature.covers3Users": "Cubre hasta 3 usuarios",
  "servicePlans.feature.everythingIndividual": "Todo lo del Plan Individual",
  "servicePlans.feature.advancedSimCustom":
    "Personalizaci\u00f3n avanzada de escenarios de simulaci\u00f3n",
  "servicePlans.feature.advancedThreatSim":
    "Simulaciones de amenazas avanzadas con niveles de dificultad personalizables",
  "servicePlans.feature.realWorldScenarios":
    "Escenarios de simulaci\u00f3n basados en ataques del mundo real",
  "servicePlans.feature.moreUsage": "Mayor uso que el Plan Individual",
  "servicePlans.feature.weeklyReports":
    "Informes de simulaci\u00f3n semanales/mensuales",
  "servicePlans.feature.referralBonus": "Bono de referidos",
  "servicePlans.feature.covers6Users": "Cubre hasta 6 usuarios",
  "servicePlans.feature.everythingFamily": "Todo lo del Plan Familiar",
  "servicePlans.feature.myScamShield":
    "Incluye \u2018Mi Escudo Antifraude\u2019 (funci\u00f3n futura)",
  "servicePlans.feature.highestUsage": "Los l\u00edmites de uso m\u00e1s altos",
  "servicePlans.feature.prioritySupport": "Soporte al cliente prioritario",
  "servicePlans.feature.indepthReports":
    "Informes detallados de simulaci\u00f3n semanales/mensuales",
  "servicePlans.feature.earlyAccess":
    "Acceso anticipado a nuevas funciones y actualizaciones",
  "servicePlans.feature.coversUpTo10Employees": "Cubre hasta 10 empleados",
  "servicePlans.feature.coversUpTo25Employees": "Cubre hasta 25 empleados",
  "servicePlans.feature.coversUpTo50Employees": "Cubre hasta 50 empleados",
  "servicePlans.feature.coversUpTo75Employees": "Cubre hasta 75 empleados",
  "servicePlans.feature.coversUpTo100Employees": "Cubre hasta 100 empleados",
  "servicePlans.feature.everythingTeam": "Todo lo de los planes de Equipo",
  "servicePlans.feature.adminDashboard":
    "Panel de administraci\u00f3n centralizado para gestionar tu equipo",
  "servicePlans.feature.teamReports":
    "Informes de simulaci\u00f3n de phishing y progreso para todo el equipo",
  "servicePlans.feature.auditLogs":
    "Registros de auditor\u00eda para cumplimiento y supervisi\u00f3n",
  "servicePlans.feature.freeTrial14": "Prueba gratuita de 14 d\u00edas",

  // ── Choose Plans (ChoosePlans.js) ───────────────────────────────────
  "choosePlans.title": "Elige tu plan",
  "choosePlans.description":
    "Selecciona el plan que mejor te proteja a ti y a tu familia y disfruta de una protecci\u00f3n de seguridad mejorada.",
  "choosePlans.btn.start": "Empieza a fortalecer tu escudo",

  "selectedPlan.title.new": "\ud83c\udfaf Plan seleccionado",
  "selectedPlan.title.change": "\ud83d\udd04 Vista previa del cambio de plan",
  "selectedPlan.alert.youHaveSelected":
    "Has seleccionado un plan para activar:",
  "selectedPlan.alert.downgrade": "\u2b07\ufe0f Degradación de plan:",
  "selectedPlan.alert.upgrade": "\u2b06\ufe0f Mejora de plan:",
  "selectedPlan.alert.change": "\ud83d\udd04 Cambio de plan:",
  "selectedPlan.alert.downgradeNote":
    "Tu plan actual permanece activo hasta el final de tu período de facturación. El nuevo plan y los precios entran en vigor en tu próxima renovación.",
  "selectedPlan.table.currentPlan": "Plan actual",
  "selectedPlan.table.newPlan": "Nuevo plan",
  "selectedPlan.table.current": "Actual",
  "selectedPlan.table.new": "Nuevo",
  "selectedPlan.table.planName": "Nombre del plan",
  "selectedPlan.table.price": "Precio",
  "selectedPlan.table.maxUsers": "Máx. usuarios",
  "selectedPlan.table.free": "Gratis",
  "selectedPlan.price.afterTrial": " después del período de prueba",
  "selectedPlan.promo.toggle":
    "\ud83c\udff7\ufe0f ¿Tienes un código de promoción?",
  "selectedPlan.promo.label": "Código de promoción",
  "selectedPlan.promo.removeTitle": "Eliminar código de promoción",
  "selectedPlan.promo.placeholder": "Ingresa el código (p. ej., SPRING40)",
  "selectedPlan.promo.checking": "Verificando...",
  "selectedPlan.promo.apply": "Aplicar",
  "selectedPlan.promo.cancelTitle": "Cancelar",
  "selectedPlan.trial.unavailablePaidActive":
    "Prueba no disponible - tienes una suscripción de pago activa",
  "selectedPlan.trial.unavailableAlreadyUsed":
    "Prueba no disponible - ya usaste tu prueba gratuita",
  "selectedPlan.trial.endDate": "La prueba terminará el {date}",
  "selectedPlan.trial.notExtended":
    " (igual que la prueba actual, no se extiende)",
  "selectedPlan.trial.cancelRiskFree":
    "Cancela sin riesgo antes de que termine la prueba",
  "selectedPlan.trial.availabilityNote":
    "(período de prueba de 30 días disponible)",
  "selectedPlan.toggle.fullSubscription": "Suscripción completa",
  "selectedPlan.toggle.continueWithTrial": "Continuar con el período de prueba",
  "selectedPlan.toggle.startWithTrial": "Comenzar con el período de prueba",
  "selectedPlan.toggle.autoRenewLabel": "¿Renovación automática?",
  "selectedPlan.toggle.autoRenewMessage":
    "La suscripción se renovará automáticamente",
  "selectedPlan.btn.activating": "\ud83d\udd04 Activando...",
  "selectedPlan.btn.changingPlan": "\ud83d\udd04 Cambiando plan...",
  "selectedPlan.btn.activatePlan": "\ud83d\ude80 Activar plan",
  "selectedPlan.btn.subscribePlan": "\ud83d\ude80 Suscribirse al nuevo plan",
  "selectedPlan.btn.chooseDifferent": "\ud83d\udd04 Elegir un plan diferente",
  "selectedPlan.btn.cancelSelection": "\u274c Cancelar selección",
  "selectedPlan.btn.cancelChange": "\u274c Cancelar cambio",
  "selectedPlan.details.subscriptionDetails":
    "\ud83d\udccb Detalles de la suscripción",
  "selectedPlan.details.downgradeDetails":
    "\ud83d\udccb Detalles de la degradación",
  "selectedPlan.details.upgradeDetails": "\ud83d\udccb Detalles de la mejora",
  "selectedPlan.details.planChangeDetails":
    "\ud83d\udccb Detalles del cambio de plan",
  "selectedPlan.details.activatedImmediately":
    "Activado inmediatamente tras la confirmación",
  "selectedPlan.details.cancelAnytime":
    "Cancela en cualquier momento desde tu configuración de suscripción",
  "selectedPlan.details.includesTrial":
    "Incluye {days} días de prueba gratuita",
  "selectedPlan.details.upgradedImmediately":
    "Mejorado inmediatamente tras la confirmación",
  "selectedPlan.details.updatedImmediately":
    "Actualizado inmediatamente tras la confirmación",
  "selectedPlan.details.proratedCharge":
    "Es posible que se cobre un monto prorrateado por la mejora",
  "selectedPlan.details.noAdditionalCharges":
    "No se aplicarán cargos adicionales",
  "selectedPlan.details.discountMayNotApply":
    "Tu descuento actual del {pct}% puede no aplicarse al nuevo plan.",
  "selectedPlan.details.discountMayNotCarry":
    "Tu descuento actual del {pct}% puede no transferirse a menos que aplique la misma promoción. Usa el campo de código de promoción si tienes uno.",
  "selectedPlan.details.promoApplied": "Código aplicado \u2705",
  "selectedPlan.details.usePromoHint":
    " Usa el campo de código de promoción si tienes uno.",
  "selectedPlan.downgrade.fullAccessNote":
    "Acceso completo a todas las funciones actuales incluyendo {users} espacios de usuario",
  "selectedPlan.downgrade.keepUntil": "Ahora \u2014 {date}",
  "selectedPlan.downgrade.afterDate": "Después del {date}",
  "selectedPlan.free": "Gratis",
  "selectedPlan.afterTrial": "después de que termine el período de prueba",
  "selectedPlan.details.includesDiscount": "(incluye descuento)",
  "selectedPlan.downgrade.keepYourPlan": "Mantén tu",
  "selectedPlan.downgrade.plan": "plan",
  "selectedPlan.downgrade.at": "a",
  "selectedPlan.downgrade.switchTo": "Cambiar a",

  // ── Confirmation Modal (ConfirmationModal.js) ─────────────────────
  "confirmModal.reasonLabel": "Motivo (opcional)",
  "confirmModal.processing": "Procesando...",

  // ── Member User Info (MemberUserInfo.js & sub-components) ──────────
  // Page header
  "usermgmt.title": "Gestión de usuarios",
  "usermgmt.subtitle":
    "Crear, actualizar y administrar información de usuarios miembro",
  "usermgmt.activityMonitoring.title": "Monitoreo de actividad del usuario",
  "usermgmt.activityMonitoring.subtitle":
    "Ver el panel, análisis e informes semanales de los usuarios miembro",
  "usermgmt.activityMonitoring.selectPrompt":
    "Selecciona un miembro de la lista para ver su actividad",
  "usermgmt.activityMonitoring.label.dashboard": "Panel",
  "usermgmt.activityMonitoring.label.analyses": "Análisis",
  "usermgmt.activityMonitoring.label.reports": "Informes de progreso semanal",

  // Stat cards
  "usermgmt.stats.selectedUser": "Usuario seleccionado",
  "usermgmt.stats.none": "Ninguno",
  "usermgmt.stats.totalUsers": "Total de usuarios",
  "usermgmt.stats.yourRole": "Tu rol",
  "usermgmt.stats.plan": "Plan",
  "usermgmt.stats.planActive": "Activo",
  "usermgmt.stats.planNone": "Ninguno",

  // Banner
  "usermgmt.banner.viewingEditing": "Viendo / Editando ",
  "usermgmt.banner.backToMyProfile": "Volver a mi perfil",

  // Roles
  "usermgmt.role.platformAdmin": "Administrador de plataforma",
  "usermgmt.role.accountAdmin": "Administrador de cuenta",
  "usermgmt.role.groupAdmin": "Administrador de grupo",
  "usermgmt.role.member": "Miembro",

  // Section titles & subtitles
  "usermgmt.section.list.title": "Lista de usuarios",
  "usermgmt.section.list.subtitle":
    "Ver y administrar todos los usuarios de tu cuenta",
  "usermgmt.section.create.title": "Crear usuario",
  "usermgmt.section.create.subtitle":
    "Agregar un nuevo usuario miembro al sistema",
  "usermgmt.section.update.title": "Actualizar información de usuario",
  "usermgmt.section.update.subtitle":
    "Modificar la información de un usuario existente",
  "usermgmt.section.details.title": "Detalles adicionales",
  "usermgmt.section.details.subtitle":
    "Administra información adicional del perfil del usuario. Esta información es opcional y puede usarse para segmentación, análisis o propósitos de simulación personalizada.",
  "usermgmt.section.resend.title": "Reenviar verificación",
  "usermgmt.section.resend.subtitle":
    "Reenviar el enlace de verificación por correo a usuarios cuyo enlace ha expirado.",

  // Toast messages
  "usermgmt.toast.activated": "{firstName} {lastName} activado",
  "usermgmt.toast.deactivated": "{firstName} {lastName} desactivado",
  "usermgmt.toast.activateSuccess": "Usuario activado correctamente",
  "usermgmt.toast.deactivateSuccess": "Usuario desactivado correctamente",
  "usermgmt.toast.phoneUpdated":
    "¡Número de teléfono actualizado correctamente!",
  "usermgmt.toast.phoneVerifyRefreshError":
    "Verificación de teléfono completada, pero hubo un error al actualizar los datos. Por favor, recarga la página.",
  "usermgmt.toast.resendSuccess": "Correo de verificación reenviado a {email}",
  "usermgmt.toast.resendFailed":
    "No se pudo reenviar el correo de verificación.",

  // Error messages
  "usermgmt.error.noPermission":
    "No tienes permiso para modificar este usuario.",
  "usermgmt.error.authRequired":
    "Autenticación requerida. Por favor, inicia sesión nuevamente.",
  "usermgmt.error.updateStatusFailed":
    "No se pudo actualizar el estado del usuario. Inténtalo de nuevo.",
  "usermgmt.error.selectUserToDelete":
    "Por favor, selecciona al menos un usuario para eliminar.",
  "usermgmt.error.ownerNotDeleted":
    "Los propietarios de cuenta no serán eliminados.",
  "usermgmt.error.deleteFailed":
    "No se pudieron eliminar los usuarios. Inténtalo de nuevo.",
  "usermgmt.error.noUserSelected":
    "Ningún usuario seleccionado. Por favor, selecciona un usuario de la Lista de usuarios.",
  "usermgmt.error.loadDetailsFailed":
    "No se pudieron cargar los detalles del usuario. Inténtalo de nuevo.",
  "usermgmt.error.loadUserFailed":
    "No se pudieron cargar los datos del usuario.",
  "usermgmt.error.userNotFound": "Usuario no encontrado.",
  "usermgmt.error.refreshFailed":
    "No se pudieron actualizar los datos del usuario.",
  "usermgmt.error.uuidNotFound":
    "UUID de usuario no encontrado. Por favor, recarga la página.",
  "usermgmt.error.noSubscription":
    "No se puede crear usuario: no se encontró suscripción activa. Por favor, activa un plan de suscripción.",
  "usermgmt.error.atLimit":
    "No se puede crear usuario: límite de usuarios de suscripción alcanzado. Por favor, mejora tu plan.",
  "usermgmt.error.createFailed": "No se pudo crear el usuario",
  "usermgmt.error.unauthorized":
    "No autorizado para crear nuevo usuario miembro",
  "usermgmt.error.createError": "Ocurrió un error al crear el usuario",
  "usermgmt.error.updateFailed": "No se pudo actualizar el usuario",
  "usermgmt.error.updateError": "Ocurrió un error al actualizar el usuario",
  "usermgmt.error.saveDetailsFailed":
    "No se pudieron guardar los detalles del usuario",
  "usermgmt.error.saveDetailsError":
    "Ocurrió un error al guardar los detalles del usuario",
  "usermgmt.error.loadUnverifiedFailed":
    "No se pudieron cargar los usuarios no verificados.",

  // Validation
  "usermgmt.validation.firstNameRequired": "El nombre es obligatorio",
  "usermgmt.validation.lastNameRequired": "El apellido es obligatorio",
  "usermgmt.validation.emailRequired": "El correo electrónico es obligatorio",
  "usermgmt.validation.emailInvalid":
    "Por favor, ingresa una dirección de correo válida",
  "usermgmt.validation.phoneRequired": "El número de teléfono es obligatorio",
  "usermgmt.validation.phoneInvalid":
    "El número de teléfono debe estar en formato E.164 (p. ej., +1234567890)",
  "usermgmt.validation.groupRequired": "Por favor, selecciona un grupo",

  // Create section subscription info
  "usermgmt.create.loadingSubscription":
    "Cargando información de suscripción...",
  "usermgmt.create.noSubscription": "¡Sin suscripción activa!",
  "usermgmt.create.noSubscriptionMsg":
    "Se requiere un plan de suscripción activo para crear usuarios.",
  "usermgmt.create.activateSubscription": "Activar suscripción",
  "usermgmt.create.contactSupport": "o contacta con soporte para comenzar.",
  "usermgmt.create.atLimit": "¡Límite de usuarios alcanzado!",
  "usermgmt.create.atLimitMsg":
    "Tu plan actual permite hasta {maxUsers} usuarios.",
  "usermgmt.create.atLimitUpgrade":
    "Por favor, mejora tu suscripción para agregar más usuarios, o contacta con soporte para obtener ayuda.",
  "usermgmt.create.upgradeSubscription": "Mejorar suscripción",
  "usermgmt.create.highestTier":
    "Estás en el nivel de suscripción más alto. Contacta con soporte para hablar sobre soluciones empresariales personalizadas para usuarios adicionales.",
  "usermgmt.create.nearLimit": "¡Acercándose al límite de usuarios!",
  "usermgmt.create.nearLimitMsg":
    "Puedes crear {remaining} usuario(s) más antes de alcanzar el límite de {maxUsers} usuarios de tu plan.",
  "usermgmt.create.nearLimitConsider":
    "Considera mejorar tu plan de suscripción para acomodar más usuarios.",
  "usermgmt.create.viewSubscriptions": "Ver suscripciones",
  "usermgmt.create.continueSetup": "Continuar configurando tu cuenta",

  // Security warning boxes
  "usermgmt.warning.phoneConflict.title":
    "Conflicto de verificación de número de teléfono",
  "usermgmt.warning.phoneConflict.details":
    "Este número de teléfono está siendo utilizado actualmente para verificación por otro usuario. Por favor, prueba con un número diferente o contacta con soporte si crees que esto es un error.",
  "usermgmt.warning.emailRequired.title": "Verificación de correo requerida",
  "usermgmt.warning.emailRequired.message":
    "Correo de verificación enviado a tu nueva dirección de correo.",
  "usermgmt.warning.emailRequired.details":
    "Por favor, revisa tu bandeja de entrada y haz clic en el enlace de verificación para completar el cambio de correo.",
  "usermgmt.warning.emailConflict.title":
    "Conflicto de verificación de correo electrónico",
  "usermgmt.warning.emailConflict.details":
    "Esta dirección de correo está siendo utilizada actualmente para verificación por otro usuario. Por favor, prueba con una dirección diferente o contacta con soporte si crees que esto es un error.",
  "usermgmt.warning.securityRequired.title":
    "Verificación de seguridad requerida",
  "usermgmt.warning.securityRequired.message":
    "Por tu seguridad, por favor cambia tu correo y número de teléfono por separado.",
  "usermgmt.warning.securityRequired.details":
    "Cada cambio requiere verificación individual para garantizar la seguridad de la cuenta. Tus valores han sido revertidos a la configuración original.",

  // Update success toasts
  "usermgmt.toast.updateSelf":
    "Tu información ha sido actualizada correctamente.",
  "usermgmt.toast.updateOther":
    "La información de {firstName} ha sido actualizada correctamente.",
  "usermgmt.toast.detailsSelf":
    "Tus detalles han sido actualizados correctamente.",
  "usermgmt.toast.detailsOther":
    "Los detalles de {firstName} han sido actualizados correctamente.",

  // Phone change request toast
  "usermgmt.toast.phoneChangePending":
    "¡Solicitud de cambio de teléfono enviada! Por favor, ingresa el código de verificación enviado a tu nuevo número de teléfono.",

  // Resend verification panel
  "usermgmt.resend.infoText":
    "Si el enlace de verificación por correo de un usuario miembro ha expirado, puedes reenviarlo aquí. Esto restablecerá su estado de verificación y enviará un correo nuevo con un enlace válido por 24 horas.",
  "usermgmt.resend.loading": "Cargando usuarios no verificados…",
  "usermgmt.resend.allVerified":
    "Todos los usuarios miembro han verificado su correo. No se requiere ninguna acción.",
  "usermgmt.resend.countFound":
    "{count} usuario(s) miembro no verificado(s) encontrado(s)",
  "usermgmt.resend.group": "Grupo:",
  "usermgmt.resend.unverified": "No verificado",
  "usermgmt.resend.sending": "Enviando…",
  "usermgmt.resend.resendEmail": "Reenviar correo",
  "usermgmt.resend.refreshList": "Actualizar lista",

  // Delete confirmation modal
  "usermgmt.modal.deleteTitle": "Eliminar usuarios",
  "usermgmt.modal.deleteMessage":
    "¿Estás seguro de que deseas eliminar {count} usuario(s)?",
  "usermgmt.modal.deleteSingleMessage":
    "¿Estás seguro de que deseas eliminar este usuario?",
  "usermgmt.modal.deleteDetails":
    "Esta acción no se puede deshacer. Los datos del usuario serán eliminados permanentemente del sistema.",

  // Deactivate confirmation modal
  "usermgmt.modal.deactivateTitle": "Desactivar usuario",
  "usermgmt.modal.deactivateMessage":
    "¿Estás seguro de que deseas desactivar este usuario?",
  "usermgmt.modal.deactivateDetails":
    "El usuario perderá acceso al sistema. Puedes reactivarlo más adelante.",
  "usermgmt.modal.deactivateConfirm": "Desactivar",

  // UserForm
  "usermgmt.form.basicInfo": "Información básica",
  "usermgmt.form.noSelectedUser":
    "Ningún usuario seleccionado. Por favor, asegúrate de haber seleccionado un usuario de la Lista de usuarios.",
  "usermgmt.form.firstName": "Nombre",
  "usermgmt.form.lastName": "Apellido",
  "usermgmt.form.emailAddress": "Dirección de correo electrónico",
  "usermgmt.form.phoneNumber": "Número de teléfono",
  "usermgmt.form.verifyPhoneChange": "Verificar cambio de teléfono",
  "usermgmt.form.assignGroup": "Asignar a grupo",
  "usermgmt.form.loadingGroups": "Cargando grupos...",
  "usermgmt.form.errorLoadingGroups": "Error al cargar grupos:",
  "usermgmt.form.selectGroup": "Selecciona un grupo...",
  "usermgmt.form.groupRequired":
    "Por favor, selecciona un grupo para el nuevo usuario",
  "usermgmt.form.creating": "Creando...",
  "usermgmt.form.updating": "Actualizando...",
  "usermgmt.form.createUser": "Crear usuario",
  "usermgmt.form.updateUser": "Actualizar usuario",
  "usermgmt.form.changePending": "Cambio pendiente de verificación",
  "usermgmt.form.verified": "Verificado",
  "usermgmt.form.notVerified": "No verificado",
  "common.retrying": "Reintentando...",

  // UserDetails
  "usermgmt.details.city": "Ciudad",
  "usermgmt.details.cityPlaceholder": "Ingresa tu ciudad",
  "usermgmt.details.state": "Estado",
  "usermgmt.details.statePlaceholder": "Ingresa tu estado",
  "usermgmt.details.country": "País",
  "usermgmt.details.countryPlaceholder": "Ingresa tu país",
  "usermgmt.details.ageRange": "Rango de edad",
  "usermgmt.details.ageRangePlaceholder": "Ingresa tu rango de edad",
  "usermgmt.details.selectAgeRange": "Selecciona rango de edad",
  "usermgmt.details.profession": "Profesión",
  "usermgmt.details.professionPlaceholder": "Ingresa tu profesión",
  "usermgmt.details.hobbies": "Pasatiempos",
  "usermgmt.details.hobbiesPlaceholder": "Ingresa tu pasatiempo",
  "usermgmt.details.hobbyPlaceholder": "Ingresa tu pasatiempo",
  "usermgmt.details.additionalInfo": "Información adicional",
  "usermgmt.details.additionalInfoPlaceholder":
    "Ingresa cualquier información adicional sobre ti",
  "usermgmt.details.saving": "Guardando...",
  "usermgmt.details.saveDetails": "Guardar detalles",

  // UserCard — actions dropdown
  "usermgmt.card.actionsBtn": "Acciones",
  "usermgmt.card.configSimProfile": "Configurar perfil de simulación",
  "usermgmt.card.monitorActivity": "Monitorear actividad",

  // UserCard
  "usermgmt.card.selectUserTitle": "Seleccionar usuario",
  "usermgmt.card.selectForDeletion": "Seleccionar usuario para eliminar",
  "usermgmt.card.accountAdmin": "Administrador de cuenta",
  "usermgmt.card.accountAdminBadge": "Administrador de cuenta",
  "usermgmt.card.unverified": "No verificado",
  "usermgmt.card.active": "Activo",
  "usermgmt.card.inactive": "Inactivo",
  "usermgmt.card.deactivateTooltip":
    "Los usuarios desactivados mantienen acceso de inicio de sesión pero no pueden usar las funciones de la aplicación",
  "usermgmt.card.activateTooltip": "Activar usuario",
  "usermgmt.card.deactivateTitle": "Desactivar usuario",
  "usermgmt.card.deactivate": "Desactivar",
  "usermgmt.card.activate": "Activar",
  "usermgmt.card.viewProfile": "Editar perfil",
  "usermgmt.card.edit": "Editar",
  "usermgmt.card.delete": "Eliminar",
  "usermgmt.card.group": "Grupo:",

  // UserListControls
  "usermgmt.controls.selectUserHelp":
    "Selecciona un usuario para ver y modificar su información, detalles personales, configuración de simulación y más.",
  "usermgmt.controls.deactivatedNote":
    "Nota: Los usuarios desactivados mantienen acceso de inicio de sesión pero no pueden usar las funciones de la aplicación.",
  "usermgmt.controls.filterLabel": "Filtrar usuarios:",
  "usermgmt.controls.filterAll": "Todos los usuarios",
  "usermgmt.controls.filterActive": "Usuarios activos",
  "usermgmt.controls.filterInactive": "Usuarios inactivos",
  "usermgmt.controls.filterUnverified": "Usuarios no verificados",
  "usermgmt.controls.refreshTitle": "Actualizar lista de usuarios",
  "usermgmt.controls.userListLabel": "Lista de usuarios",
  "usermgmt.controls.filterGroupLabel": "Filtrar usuarios por estado",

  // BulkActions
  "usermgmt.bulk.title": "Acciones en lote ({count} seleccionados)",
  "usermgmt.bulk.deleteSelected": "Eliminar seleccionados",
  "usermgmt.bulk.ownerNote":
    "Nota: Los propietarios de cuenta no pueden eliminarse y serán excluidos.",

  // UserList
  "usermgmt.list.loading": "Cargando usuarios...",
  "usermgmt.list.noUsers": "No se encontraron usuarios.",

  // PhoneChangeVerificationPopup
  "usermgmt.phone.title": "Verificar cambio de número de teléfono",
  "usermgmt.phone.greeting": "¡Hola, {firstName}!",
  "usermgmt.phone.prompt":
    "Por favor, verifica el cambio de tu número de teléfono ingresando el código de verificación enviado a:",
  "usermgmt.phone.fallbackPhone": "tu nuevo número de teléfono",
  "usermgmt.phone.codeLabel": "Código de verificación",
  "usermgmt.phone.codePlaceholder": "Ingresa el código de 6 dígitos",
  "usermgmt.phone.verifying": "Verificando...",
  "usermgmt.phone.verifyBtn": "Verificar cambio de teléfono",
  "usermgmt.phone.sending": "Enviando...",
  "usermgmt.phone.resendCode": "Reenviar código",
  "usermgmt.phone.cancelChange": "Cancelar cambio de teléfono",
  "usermgmt.phone.note":
    'Nota: Si no recibes el SMS, por favor verifica que tu teléfono puede recibir mensajes de texto y prueba el botón "Reenviar código".',
  "usermgmt.phone.attempts": "Intentos: {current} / {max}",
  "usermgmt.phone.noPendingChange":
    "No se encontró ningún cambio de teléfono pendiente. Por favor, inicia una nueva solicitud de cambio de teléfono desde la configuración de tu cuenta.",
  "usermgmt.phone.verificationExpired":
    "Tu verificación de teléfono ha expirado. Por favor, inicia una nueva solicitud de cambio de teléfono desde la configuración de tu cuenta.",
  "usermgmt.phone.loadStatusFailed":
    "No se pudo cargar el estado de verificación. Inténtalo de nuevo.",
  "usermgmt.phone.enterCode": "Por favor, ingresa el código de verificación.",
  "usermgmt.phone.invalidCode":
    "Código de verificación inválido. Inténtalo de nuevo.",
  "usermgmt.phone.noPendingChangeShort":
    "No se encontró ningún cambio de teléfono pendiente. Por favor, inicia una nueva solicitud de cambio de teléfono.",
  "usermgmt.phone.verificationFailed":
    "Verificación fallida. Inténtalo de nuevo.",
  "usermgmt.phone.noPendingPhone":
    "No se encontró ningún número de teléfono pendiente. Por favor, inicia una nueva solicitud de cambio de teléfono.",
  "usermgmt.phone.sendFailed":
    "No se pudo enviar el código de verificación. Inténtalo más tarde.",
  "usermgmt.phone.resendFailed":
    "No se pudo reenviar el código de verificación. Inténtalo de nuevo.",
  "usermgmt.phone.cancelFailed":
    "No se pudo cancelar el cambio de teléfono. Inténtalo de nuevo.",
  "usermgmt.phone.cancelSuccess":
    "La solicitud de cambio de teléfono ha sido cancelada.",
  "usermgmt.phone.codeSent":
    "Se ha enviado un nuevo código de verificación a tu número de teléfono pendiente.",
  "usermgmt.phone.verifySuccess":
    "¡Verificación de cambio de teléfono completada correctamente! Tu número de teléfono ha sido actualizado.",

  // PhoneChangeVerificationPopup — alias/renamed keys used in updated component
  "usermgmt.phone.modalTitle": "Verificar cambio de número de teléfono",
  "usermgmt.phone.hello": "¡Hola, {firstName}!",
  "usermgmt.phone.userFallback": "allí",
  "usermgmt.phone.instructions":
    "Por favor, verifica el cambio de tu número de teléfono ingresando el código de verificación enviado a:",
  "usermgmt.phone.yourNewPhone": "tu nuevo número de teléfono",
  "usermgmt.phone.cancelBtn": "Cancelar cambio de teléfono",
  "usermgmt.phone.smsNote":
    'Nota: Si no recibes el SMS, por favor verifica que tu teléfono puede recibir mensajes de texto y prueba el botón "Reenviar código".',
  "usermgmt.phone.sendCodeFailed":
    "No se pudo enviar el código de verificación. Inténtalo más tarde.",
  "usermgmt.phone.verifyFailed": "Verificación fallida. Inténtalo de nuevo.",

  // UserList
  "usermgmt.list.noUsersFound": "No se encontraron usuarios.",
  "usermgmt.list.noMembers": "No se encontraron miembros.",

  // User list column headers (MemberUserInfo_v2)
  "usermgmt.list.col.name": "Nombre",
  "usermgmt.list.col.email": "Correo electrónico",
  "usermgmt.list.col.phone": "Teléfono",
  "usermgmt.list.col.role": "Rol",
  "usermgmt.list.col.status": "Estado",
  "usermgmt.list.col.actions": "Acciones",

  // UserCard consent chips
  "usermgmt.card.selected": "seleccionado",
  "usermgmt.card.selectedAriaLabel": "Seleccionado",
  "usermgmt.card.dashboard": "Panel",
  "usermgmt.card.dashboardSharedTitle": "Panel: Compartido con el propietario",
  "usermgmt.card.dashboardPrivateTitle": "Panel: Privado",
  "usermgmt.card.analysis": "Análisis",
  "usermgmt.card.analysisSharedTitle":
    "Análisis: Compartido con el propietario",
  "usermgmt.card.analysisPrivateTitle": "Análisis: Privado",
  "usermgmt.card.reports": "Informes",
  "usermgmt.card.reportsSharedTitle": "Informes: Compartido con el propietario",
  "usermgmt.card.reportsPrivateTitle": "Informes: Privado",
  "usermgmt.card.resendVerificationTitle":
    "Reenviar correo de verificación a {email}",
  "usermgmt.card.deleteTitle": "Eliminar {firstName} {lastName}",

  // UserCard
  "usermgmt.card.activateTitle": "Activar usuario",

  // Activate modal
  "usermgmt.modal.activateTitle": "Activar usuario",
  "usermgmt.modal.activateMessage":
    "¿Estás seguro de que deseas activar este usuario?",
  "usermgmt.modal.activateDetails":
    "Activar este usuario restaurará su acceso a la plataforma.",
  "usermgmt.modal.activateConfirm": "Activar",

  // Delete success toast
  "usermgmt.toast.deleteSuccess": "Usuario eliminado correctamente.",

  // Activity tab
  "usermgmt.activity.thisMember": "este miembro",
  "usermgmt.activity.intro":
    "Revisa el Panel, los análisis de estafas y los informes semanales de progreso de {name}.",
  "usermgmt.activity.dashboard": "Acceso al panel",
  "usermgmt.activity.consentGranted": "Consentimiento otorgado",
  "usermgmt.activity.consentNotGranted": "Consentimiento no otorgado",
  "usermgmt.activity.viewDashboardTitle": "Ver el panel de {name}",
  "usermgmt.activity.noAccessTitle": "{name} no ha otorgado acceso al panel",
  "usermgmt.activity.viewDashboard": "Ver panel",
  "usermgmt.activity.noAccess": "Sin acceso otorgado",
  "usermgmt.activity.analyses": "Análisis",
  "usermgmt.activity.analysesDetail": "Acceso a análisis",
  "usermgmt.activity.viewAnalysesTitle": "Ver los análisis de {name}",
  "usermgmt.activity.viewAnalyses": "Ver análisis",
  "usermgmt.activity.reports": "Acceso a informes de progreso semanal",
  "usermgmt.activity.readOnly": "Solo lectura",
  "usermgmt.activity.viewReportsTitle": "Ver los informes de {name}",
  "usermgmt.activity.viewReports": "Ver informes",
  "usermgmt.activity.backToUserMgmt": "Volver a gestión de usuarios",

  // Member analyses panel
  "usermgmt.analyses.backToActivities": "Volver a actividades",
  "usermgmt.analyses.panelTitle": "Análisis guardados de {name}",
  "usermgmt.analyses.readOnly": "Solo lectura",
  "usermgmt.analyses.tabEmails": "Correos reenviados",
  "usermgmt.analyses.tabTexts": "Textos reenviados",
  "usermgmt.analyses.tabSocialMedia": "Redes sociales",
  "usermgmt.analyses.tabImages": "Imágenes subidas",
  "usermgmt.analyses.loading": "Cargando {type}…",
  "usermgmt.analyses.retry": "Reintentar",
  "usermgmt.analyses.empty": "No se encontraron {type} guardados para {name}.",
  "usermgmt.analyses.loadFailed":
    "Error al cargar los análisis. Por favor, inténtalo de nuevo.",
  "usermgmt.analyses.pageOf": "Página {page} de {total}",
  "usermgmt.analyses.previous": "Anterior",
  "usermgmt.analyses.next": "Siguiente",
  "usermgmt.analyses.untitled": "Análisis sin título",
  "usermgmt.analyses.scamDetected": "Estafa detectada",
  "usermgmt.analyses.appearsSafe": "Parece seguro",
  "usermgmt.analyses.smsScamDetected": "Estafa SMS detectada",
  "usermgmt.analyses.smsAnalyzed": "SMS reenviado analizado",
  "usermgmt.analyses.emailScamDetected": "Estafa de correo detectada",
  "usermgmt.analyses.emailAnalyzed": "Correo reenviado analizado",
  "usermgmt.analyses.imageScamDetected": "Estafa de imagen detectada",
  "usermgmt.analyses.imageAnalyzed": "Imagen subida analizada",
  "usermgmt.analyses.socialMediaScamDetected":
    "Estafa en redes sociales detectada",
  "usermgmt.analyses.socialMediaAnalyzed": "Redes sociales analizadas",
  "usermgmt.analyses.detailTitle.email": "Análisis de correo reenviado",
  "usermgmt.analyses.detailTitle.sms": "Análisis de texto reenviado",
  "usermgmt.analyses.detailTitle.image": "Análisis de imagen subida",
  "usermgmt.analyses.detailTitle.socialMedia": "Análisis de redes sociales",
  "usermgmt.analyses.verdict.analysis": "Análisis",
  "usermgmt.analyses.verdict.riskFactors": "Factores de riesgo",
  "usermgmt.analyses.verdict.recommendations": "Recomendaciones",
  "usermgmt.analyses.verdict.submittedContent": "Contenido enviado",
  "usermgmt.analyses.verdict.risk": "Riesgo {level}",

  // NewMemberInfoModal
  "usermgmt.newMember.title": "Miembro Creado",
  "usermgmt.newMember.subtitle": "Cuenta configurada para",
  "usermgmt.newMember.closeAriaLabel": "Cerrar",
  "usermgmt.newMember.intro":
    "Esto es lo que el nuevo miembro necesita hacer antes de poder iniciar sesión. Si te hacen preguntas, usa los pasos a continuación para guiarlos.",
  "usermgmt.newMember.stepsAriaLabel": "Pasos de incorporación del miembro",
  "usermgmt.newMember.step1.title":
    "Revisar el correo electrónico para obtener un enlace de verificación",
  "usermgmt.newMember.step1.body.sentTo":
    "Se envió un correo electrónico de verificación a",
  "usermgmt.newMember.step1.body.checkInbox": ". El miembro debe revisar su",
  "usermgmt.newMember.step1.body.inbox": "bandeja de entrada",
  "usermgmt.newMember.step1.body.alsoCheck": "— y también su carpeta de",
  "usermgmt.newMember.step1.body.spamJunk": "spam / correo no deseado",
  "usermgmt.newMember.step1.body.suffix": "si no lo ve en un par de minutos.",
  "usermgmt.newMember.step2.title": "Hacer clic en el enlace de verificación",
  "usermgmt.newMember.step2.body":
    "Dentro del correo electrónico hay un enlace de verificación de un solo clic. El miembro debe hacer clic en él — confirma su dirección de correo electrónico y lo lleva automáticamente a la página de configuración de contraseña.",
  "usermgmt.newMember.step3.title": "Configurar una contraseña",
  "usermgmt.newMember.step3.body":
    "En la página de configuración de contraseña, el miembro elige una contraseña segura. Esta es la contraseña que usará cada vez que inicie sesión. El enlace es de un solo uso y expirará, por lo que deben hacerlo rápidamente.",
  "usermgmt.newMember.step4.title":
    "Iniciar sesión y comenzar a usar la aplicación",
  "usermgmt.newMember.step4.body":
    "Una vez configurada la contraseña, el miembro puede iniciar sesión con su dirección de correo electrónico y la contraseña que acaba de crear. No se requiere ninguna acción adicional de tu parte.",
  "usermgmt.newMember.tip.title":
    "Si el miembro nunca recibe el correo electrónico",
  "usermgmt.newMember.tip.body.goto": "Ve a la",
  "usermgmt.newMember.tip.body.userList": "Lista de usuarios",
  "usermgmt.newMember.tip.body.findMember":
    ", encuentra al miembro (aparecerá como",
  "usermgmt.newMember.tip.body.unverified": "no verificado",
  "usermgmt.newMember.tip.body.openMenu": "), abre su menú de acciones y elige",
  "usermgmt.newMember.tip.body.resendAction": "Reenviar correo de verificación",
  "usermgmt.newMember.tip.body.howMany":
    "Puedes hacer esto tantas veces como sea necesario.",
  "usermgmt.newMember.gotIt": "Entendido",

  // Member dashboard panel
  "usermgmt.dashboard.panelTitle": "Panel de {name}",

  // Member reports panel
  "usermgmt.reports.panelTitle": "Informes de {name}",
  "usermgmt.reports.loading": "Cargando informes…",
  "usermgmt.reports.loadFailed":
    "Error al cargar los informes. Por favor, inténtalo de nuevo.",
  "usermgmt.reports.empty": "No se encontraron informes para {name}.",
  "usermgmt.reports.colType": "Tipo",
  "usermgmt.reports.colPeriod": "Período",
  "usermgmt.reports.colScore": "Puntuación",
  "usermgmt.reports.colCreated": "Creado",
  "usermgmt.reports.typePersonal": "Personal",
  "usermgmt.reports.backToList": "Volver a informes",
  "usermgmt.reports.detailLoadFailed":
    "Error al cargar el informe. Por favor, inténtalo de nuevo.",

  // ── Informe de preparación para ciberseguro ────────────────────────
  "ci.pageTitle": "Preparación para Ciberseguro",
  "ci.pageSubtitle":
    "Una instantánea compartible y lista para suscripción de la postura de seguridad de la capa humana de tu organización.",

  // Vista de lista
  "ci.list.title": "Informes generados",
  "ci.list.subtitle":
    "Informes de preparación a nivel de cuenta que puedes compartir con tu aseguradora o corredor durante la suscripción y las renovaciones.",
  "ci.list.newReport": "+ Nuevo informe",
  "ci.list.emptyTitle": "Aún no hay informes.",
  "ci.list.emptyBody":
    "Genera tu primer informe de preparación para ciberseguro para ver cómo se compara tu organización en los controles de capa humana que más importan a los aseguradores.",
  "ci.list.generate": "Generar informe",
  "ci.list.colPeriod": "Período",
  "ci.list.colReadiness": "Preparación",
  "ci.list.colLevel": "Nivel",
  "ci.list.colSeats": "Licencias",
  "ci.list.colGenerated": "Generado",
  "ci.action.view": "Ver",
  "ci.action.pdf": "PDF",
  "ci.action.delete": "Eliminar",
  "ci.action.deleting": "Eliminando…",

  // Vista de generación
  "ci.back": "← Volver a informes",
  "ci.generate.title": "Nuevo informe de preparación para ciberseguro",
  "ci.generate.intro":
    "KinShield mide automáticamente tus controles de capa humana (MFA, capacitación en concientización de seguridad, resultados de simulaciones de phishing y participación ante amenazas) en todas las licencias inscritas. Confirma los controles administrativos a continuación para completar el panorama que esperan los aseguradores.",
  "ci.generate.period": "Período del informe",
  "ci.generate.attestTitle": "Atestaciones de controles administrativos",
  "ci.generate.attestIntro":
    "Estos controles no son medidos por KinShield. Marca los que tu organización tiene implementados; agrega notas para dar contexto. Las respuestas honestas hacen que el informe sea más útil para tu aseguradora.",
  "ci.generate.notesPlaceholder": "Notas opcionales",
  "ci.generate.cancel": "Cancelar",
  "ci.generate.generating": "Generando…",
  "ci.generate.submit": "Generar informe",

  // Opciones de período del informe
  "ci.period.30": "Últimos 30 días",
  "ci.period.60": "Últimos 60 días",
  "ci.period.90": "Últimos 90 días",
  "ci.period.180": "Últimos 180 días",
  "ci.period.365": "Últimos 12 meses",

  // Atestaciones de controles administrativos
  "ci.attest.edr.label": "Detección y respuesta de endpoints / Antivirus",
  "ci.attest.edr.help":
    "EDR o antivirus está implementado en los endpoints de la empresa.",
  "ci.attest.backups.label": "Copias de seguridad regulares y probadas",
  "ci.attest.backups.help":
    "Los datos críticos se respaldan regularmente y las restauraciones se prueban.",
  "ci.attest.patching.label": "Gestión de parches y vulnerabilidades",
  "ci.attest.patching.help":
    "Los sistemas operativos y el software se parchean de manera oportuna.",
  "ci.attest.email_filtering.label": "Seguridad de correo / Filtrado de spam",
  "ci.attest.email_filtering.help":
    "El correo entrante se filtra contra spam, malware y phishing.",
  "ci.attest.incident_response.label": "Plan de respuesta a incidentes",
  "ci.attest.incident_response.help":
    "Existe un plan documentado de respuesta a incidentes y se revisa.",
  "ci.attest.access_control.label": "Controles de acceso de privilegio mínimo",
  "ci.attest.access_control.help":
    "El acceso se otorga con privilegios mínimos y se revisa.",

  // Vista de detalle
  "ci.detail.companyFallback": "Empresa",
  "ci.detail.enrolledSeats": "licencias inscritas",
  "ci.detail.employees": "empleados",
  "ci.detail.controlsTitle": "Controles de seguridad de capa humana",
  "ci.detail.noData": "SIN DATOS",
  "ci.control.mfa": "Autenticación multifactor",
  "ci.control.training": "Capacitación en concientización de seguridad",
  "ci.control.phishing": "Resultados de simulación de phishing",
  "ci.control.threats": "Detección y participación ante amenazas",
  "ci.metric.mfaCoverage": "Cobertura de MFA",
  "ci.metric.seatsWithMfa": "Licencias con MFA",
  "ci.metric.membersTrained": "Miembros capacitados",
  "ci.metric.completionRate": "Tasa de finalización",
  "ci.metric.trainingsCompleted": "Capacitaciones completadas",
  "ci.metric.simulationsDelivered": "Simulaciones enviadas",
  "ci.metric.clickRate": "Tasa de clics",
  "ci.metric.reportRate": "Tasa de reporte",
  "ci.metric.analysesPerformed": "Análisis realizados",
  "ci.metric.scamsDetected": "Estafas / amenazas detectadas",
  "ci.metric.seatsOf": "de",
  "ci.detail.attestTitle": "Atestaciones de controles administrativos",
  "ci.detail.colControl": "Control",
  "ci.detail.colAttested": "Atestado",
  "ci.detail.colNotes": "Notas",
  "ci.yes": "Sí",
  "ci.no": "No",
  "ci.detail.recommendations": "Recomendaciones",
  "ci.detail.frameworkTitle": "Mapeo de marcos",
  "ci.detail.colFramework": "Marco",
  "ci.detail.colControlId": "Control",
  "ci.detail.colName": "Nombre",
  "ci.detail.colEvidence": "Evidencia de KinShield",
  "ci.detail.downloadPdf": "Descargar PDF",
  "ci.detail.preparing": "Preparando…",

  // Mensajes de recomendación ({code} corresponde a Recommendation.code del backend)
  "ci.rec.mfa":
    "Aplica la autenticación multifactor en las {seats} licencia(s) restante(s): la MFA es el control que la mayoría de las aseguradoras exige para otorgar cobertura.",
  "ci.rec.training":
    "Aumenta la finalización de la capacitación en concientización de seguridad a al menos el 80% de las licencias; asigna los módulos requeridos con fechas límite para cerrar la brecha.",
  "ci.rec.phishingNoData":
    "Ejecuta simulaciones de phishing en todo el equipo: las tasas de clic y de reporte son un insumo principal para la suscripción y demuestran una reducción activa del riesgo.",
  "ci.rec.phishingClickRate":
    "Reduce la tasa de clics de phishing (actualmente {rate}%) por debajo del 5% mediante capacitación de seguimiento dirigida a quienes reinciden.",
  "ci.rec.attestation":
    "Completa la sección de autoatestación (EDR, copias de seguridad, parches, plan de respuesta a incidentes) para que el informe esté listo para enviar a tu corredor.",
  "ci.rec.strong":
    "Postura sólida en todos los controles medidos: mantén la MFA aplicada y conserva tu ritmo de capacitación y simulaciones para sostener condiciones de renovación favorables.",

  // Etiquetas de nivel de preparación
  "ci.level.EXCELLENT": "Excelente",
  "ci.level.STRONG": "Sólida",
  "ci.level.MODERATE": "Moderada",
  "ci.level.NEEDS_IMPROVEMENT": "Necesita mejorar",

  // Resumen principal
  "ci.summary.default":
    "{company} tiene una puntuación de preparación para ciberseguro de {score}/100 ({level}), basada en {seats} licencias protegidas. La MFA está habilitada en el {mfaPct}% de las licencias, el {trainingPct}% ha completado la capacitación en concientización de seguridad y la tasa de clics de las simulaciones de phishing es {clickRate}.",
  "ci.summary.clickNotMeasured": "aún no medida",

  // Filas del mapeo de marcos
  "ci.fw.nist_ac.name": "Gestión de identidad y control de acceso (MFA)",
  "ci.fw.nist_ac.evidence":
    "Cobertura de MFA en todas las licencias de la cuenta",
  "ci.fw.nist_at.name": "Concientización y capacitación en seguridad",
  "ci.fw.nist_at.evidence":
    "Finalización de la capacitación en concientización de seguridad por licencia",
  "ci.fw.nist_cm.name": "Monitoreo continuo de seguridad",
  "ci.fw.nist_cm.evidence":
    "Resultados de simulaciones de phishing y actividad de detección de estafas",
  "ci.fw.cis_6.name": "Gestión del control de acceso",
  "ci.fw.cis_6.evidence":
    "Habilitación de autenticación multifactor y desglose por método",
  "ci.fw.cis_14.name":
    "Capacitación en concientización y habilidades de seguridad",
  "ci.fw.cis_14.evidence":
    "Finalización de la capacitación más tasas de clic/reporte de simulaciones de phishing",

  // Notificaciones / confirmaciones
  "ci.toast.loadFailed":
    "No se pudieron cargar los informes. Por favor, inténtalo de nuevo.",
  "ci.toast.generated": "Informe de preparación para ciberseguro generado.",
  "ci.toast.generateFailed":
    "No se pudo generar el informe. Por favor, inténtalo de nuevo.",
  "ci.toast.openFailed": "No se pudo abrir el informe.",
  "ci.toast.pdfUnavailable": "El PDF aún no está disponible para este informe.",
  "ci.toast.pdfFailed": "No se pudo descargar el PDF.",
  "ci.confirm.delete":
    "¿Eliminar este informe permanentemente? Esto no se puede deshacer.",
  "ci.toast.deleted": "Informe eliminado.",
  "ci.toast.deleteFailed": "No se pudo eliminar el informe.",

  // BulkActions
  "usermgmt.bulk.ownersNote":
    "Nota: Los propietarios de la cuenta no se pueden eliminar y serán excluidos.",

  // Roster panel (left sidebar — search + add member button)
  "usermgmt.roster.listLabel": "Lista de miembros",
  "usermgmt.roster.newMemberInfoBtn":
    "Qué esperar después de agregar un miembro",
  "usermgmt.roster.backToList": "Volver a la lista de usuarios",
  "usermgmt.roster.searchPlaceholder": "Buscar miembros...",
  "usermgmt.roster.addMember": "Agregar miembro",

  // Add Member slide-in sheet
  "usermgmt.sheet.addMemberTitle": "Agregar miembro",
  "usermgmt.sheet.slotsUsed": "espacios usados",
  "usermgmt.sheet.seatsUsed": "espacios usados",
  "usermgmt.sheet.seatsAvailable": "disponibles",

  // Detail panel (right panel header)
  "usermgmt.detail.nowViewing": "Viendo ahora",

  // Tabs
  "usermgmt.tab.profile": "Perfil",
  "usermgmt.tab.additionalDetails": "Detalles adicionales",
  "usermgmt.tab.activity": "Actividades",
  "usermgmt.tab.detailTabsLabel": "Pestañas de detalle del miembro",
  "usermgmt.tab.profileTabsLabel": "Pestañas de perfil",

  // Actions
  "usermgmt.action.saveChanges": "Guardar cambios",

  // Detail panel
  "usermgmt.detail.emptyState":
    "Selecciona un miembro de la lista para ver y editar su perfil.",

  // My Profile (non-admin solo view)
  "usermgmt.myProfile.title": "Mi perfil",
  "usermgmt.myProfile.subtitle":
    "Gestiona tu información personal y preferencias",

  // ── Security Settings ──────────────────────────────────────────────
  "security.mfa.title": "Autenticación multifactor",
  "security.mfa.demoUnavailable":
    "La configuración de MFA no está disponible en el modo de demostración.",
  "security.mfa.enabled": "Autenticación de dos factores activada",
  "security.mfa.methodLabel": "Método:",
  "security.mfa.phoneLabel": "Teléfono",
  "security.mfa.methodSms": "SMS",
  "security.mfa.methodEmail": "Correo",
  "security.mfa.regenBackupCodes": "Regenerar códigos de respaldo",
  "security.mfa.disable": "Desactivar MFA",
  "security.mfa.disableConfirmTitle":
    "¿Desactivar la autenticación de dos factores?",
  "security.mfa.disableConfirmText": "Introduce tu contraseña para confirmar:",
  "security.mfa.passwordPlaceholder": "Contraseña",
  "security.mfa.viewChangeMethods": "Ver o cambiar el método de autenticación",
  "security.mfa.availableMethods": "Métodos de autenticación disponibles",
  "security.mfa.smsMethod": "Mensaje de texto SMS",
  "security.mfa.smsDescription": "Recibe códigos por SMS",
  "security.mfa.emailMethod": "Correo electrónico",
  "security.mfa.emailDescription": "Recibe códigos por correo electrónico",
  "security.mfa.activeBadge": "Activo",
  "security.mfa.appMethod": "Aplicación de autenticación",
  "security.mfa.appDescription": "Usa una aplicación de autenticación",
  "security.mfa.setupDescription":
    "Agrega una capa adicional de seguridad al requerir un código de verificación al iniciar sesión",
  "security.mfa.enabledSuccess":
    "¡Autenticación de dos factores activada exitosamente!",
  "security.mfa.disabledSuccess":
    "Autenticación de dos factores desactivada exitosamente",
  "security.mfa.enterPassword": "Por favor introduce tu contraseña",
  "security.mfa.disableFailed": "No se pudo desactivar MFA",
  "security.mfa.regenPasswordPrompt":
    "Introduce tu contraseña para regenerar los códigos de respaldo:",
  "security.mfa.regenSuccess": "Códigos de respaldo regenerados exitosamente",
  "security.mfa.regenFailed":
    "No se pudieron regenerar los códigos de respaldo",
  "security.password.title": "Seguridad de contraseña",
  "security.password.change": "Cambiar contraseña",

  // ── Notifications Settings ─────────────────────────────────────────
  "notifications.email.title": "Notificaciones de correo",
  "notifications.email.securityTitle": "Anuncios de seguridad",
  "notifications.email.securityDesc":
    "Notificaciones de seguridad importantes, incluidos cambios de contraseña y MFA y alertas de nuevos inicios de sesión o dispositivos",
  "notifications.email.trainingTitle": "Recordatorios de entrenamiento",
  "notifications.email.trainingDesc":
    "Actualizaciones y recordatorios de entrenamiento de simulación",
  "notifications.email.weeklyTitle": "Informes semanales",
  "notifications.email.weeklyDesc":
    "Resumen semanal de tu progreso en entrenamiento de seguridad",
  "notifications.sms.title": "Comunicaciones por SMS",
  "notifications.sms.loading": "Cargando preferencias de SMS...",
  "notifications.sms.consentTitle": "Estado de consentimiento de SMS",
  "notifications.sms.enabledFor":
    "Las notificaciones SMS están activadas para {phone}",
  "notifications.sms.notOptedIn":
    "No te has inscrito para recibir notificaciones por SMS",
  "notifications.sms.noPhone":
    "No se encontró número de teléfono en tu perfil. Por favor agrega un número de teléfono para activar las notificaciones SMS.",
  "notifications.sms.enableTitle": "Activar notificaciones SMS",
  "notifications.sms.enableDesc":
    "Inscríbete para recibir anuncios de seguridad y notificaciones importantes por SMS en {phone}. Puedes darte de baja en cualquier momento.",
  "notifications.sms.optInButton": "Inscribirme en notificaciones SMS",
  "notifications.sms.optOutConfirm":
    "¿Dar de baja las notificaciones SMS? Ya no recibirás ningún mensaje de texto de KinShield.",
  "notifications.sms.optInConfirm":
    "¿Autorizar recibir notificaciones SMS de KinShield? Pueden aplicarse tarifas estándar de mensajes y datos. Puedes darte de baja en cualquier momento.",
  "notifications.sms.verifyTitle": "Verificar tu número de teléfono",
  "notifications.sms.verifyDesc":
    "Se ha enviado un código de verificación a {phone}. Introdúcelo a continuación para activar las notificaciones SMS.",
  "notifications.sms.verificationCodeLabel": "Código de verificación:",
  "notifications.sms.verificationCodePlaceholder":
    "Introduce el código de 6 dígitos",
  "notifications.sms.verifyButton": "Verificar código",
  "notifications.sms.verifying": "Verificando...",
  "notifications.sms.optOutTitle": "Darse de baja de comunicaciones SMS",
  "notifications.sms.optOutDesc":
    "Haz clic en el botón de abajo para dejar de recibir mensajes SMS de KinShield. Puedes volver a inscribirte contactando al soporte.",
  "notifications.sms.optOutButton": "Dar de baja de SMS",
  "notifications.sms.processing": "Procesando...",
  "notifications.sms.criticalTitle": "Anuncios de seguridad críticos",
  "notifications.sms.criticalActiveDesc":
    "Notificaciones de seguridad urgentes por SMS",
  "notifications.sms.criticalInactiveDesc":
    "Inscríbete en SMS para recibir anuncios de seguridad críticos",
  "notifications.sms.noPhoneError":
    "No se encontró número de teléfono. Por favor contacta al soporte.",
  "notifications.sms.noUserInfo":
    "Información del usuario no encontrada. Por favor intenta actualizar la página.",
  "notifications.sms.optedOutSuccess":
    "Te has dado de baja exitosamente de las notificaciones SMS.",
  "notifications.sms.optOutFailed":
    "No se pudo dar de baja. Por favor intenta de nuevo.",
  "notifications.sms.optOutError":
    "Ocurrió un error al darse de baja. Por favor intenta de nuevo.",
  "notifications.sms.optInFailed":
    "No se pudo procesar la inscripción. Por favor intenta de nuevo.",
  "notifications.sms.optInError":
    "Ocurrió un error al inscribirse. Por favor intenta de nuevo.",
  "notifications.sms.verifyRequired":
    "Por favor introduce el código de verificación.",
  "notifications.sms.noUserInfoRefresh":
    "Información del usuario no encontrada. Por favor actualiza la página.",
  "notifications.sms.verifiedSuccess":
    "¡Número de teléfono verificado exitosamente! Las notificaciones SMS ahora están activas.",
  "notifications.sms.verifyFailed":
    "Verificación fallida. Por favor revisa el código e intenta de nuevo.",
  "notifications.inapp.title": "Notificaciones en la aplicación",
  "notifications.inapp.desktopTitle": "Notificaciones de escritorio",
  "notifications.inapp.desktopDesc":
    "Mostrar notificaciones del navegador cuando la aplicación está abierta",
  "notifications.announcements.title": "Preferencias de anuncios",
  "notifications.announcements.levelLabel":
    "¿Qué anuncios emergentes quieres ver?",
  "notifications.announcements.warningsOnly": "⚠️ Solo advertencias y errores",
  "notifications.announcements.plusInfo": "📢 + Mensajes informativos",
  "notifications.announcements.plusSuccess": "✅ + Mensajes de éxito",
  "notifications.announcements.showAll": "🔔 Mostrar todo",
  "notifications.announcements.noteLabel": "Nota:",
  "notifications.announcements.noteText":
    "Las advertencias y errores siempre se muestran y no se pueden desactivar.",

  // ── Appearance Settings ────────────────────────────────────────────
  "appearance.landingPage.title": "Página de inicio",
  "appearance.landingPage.label": "Página inicial después del inicio de sesión",
  "appearance.landingPage.dashboard": "📊 Panel",
  "appearance.landingPage.gettingStarted": "🚀 Primeros pasos",
  "appearance.landingPage.howDoI": "❓ ¿Cómo hago...?",
  "appearance.theme.title": "Tema",
  "appearance.theme.colorLabel": "Tema de color",
  "appearance.theme.optLight": "Claro",
  "appearance.theme.optDark": "Oscuro",
  "appearance.theme.optAuto": "Automático (Sistema)",
  "appearance.compactMode.title": "Modo compacto",
  "appearance.compactMode.desc":
    "Mostrar más contenido reduciendo el espaciado",
  "appearance.installApp.title": "Instalar aplicación",
  "appearance.dashboard.title": "Configuración del panel",
  "appearance.dashboard.viewLabel": "Vista del panel",
  "appearance.dashboard.detailed": "📊 Vista detallada (Panel completo)",
  "appearance.dashboard.simple": "📱 Vista simple (Amigable para mayores)",
  "appearance.dashboard.modeLabel": "Modo del panel (Solo vista detallada)",
  "appearance.dashboard.riskMode": "🚨 Modo riesgo (Enfoque en amenazas)",
  "appearance.dashboard.shieldMode": "🛡️ Modo escudo (Enfoque en protección)",
  "appearance.dashboard.showSettingsLabel":
    "Mostrar botón de configuración de vista",
  "appearance.dashboard.showSettingsDesc":
    "Mostrar el botón de configuración de vista (⚙️) en el encabezado del panel para acceso rápido a opciones de visualización temporales",

  // ── Change Password Modal ──────────────────────────────────────────
  "changePassword.title": "Cambiar contraseña",
  "changePassword.currentLabel": "Contraseña actual",
  "changePassword.currentPlaceholder": "Introduce tu contraseña actual",
  "changePassword.newLabel": "Nueva contraseña",
  "changePassword.newPlaceholder": "Introduce tu nueva contraseña",
  "changePassword.confirmLabel": "Confirmar nueva contraseña",
  "changePassword.confirmPlaceholder": "Confirma tu nueva contraseña",
  "changePassword.mismatch": "Las contraseñas no coinciden",
  "changePassword.match": "✓ Las contraseñas coinciden",
  "changePassword.requirements.title": "Requisitos de contraseña:",
  "changePassword.requirements.length":
    "Mínimo 12 caracteres (15+ recomendado para mayor seguridad) sin requisitos de caracteres especiales, números o mayúsculas/minúsculas",
  "changePassword.requirements.passphrase":
    "Usa una frase memorable en lugar de caracteres complejos",
  "changePassword.requirements.avoid":
    "Evita palabras comunes, patrones secuenciales o caracteres repetidos",
  "changePassword.requirements.example": 'Ejemplo: "café-mañana-sol-azul"',
  "changePassword.requirements.tip":
    "¡Las frases memorables son más fáciles de recordar y más seguras que las contraseñas cortas y complejas!",
  "changePassword.changing": "Cambiando...",
  "changePassword.submit": "Cambiar contraseña",
  "changePassword.error.enterCurrent":
    "Por favor introduce tu contraseña actual.",
  "changePassword.error.enterNew": "Por favor introduce una nueva contraseña.",
  "changePassword.error.mismatch": "Las nuevas contraseñas no coinciden.",
  "changePassword.error.samePassword":
    "La nueva contraseña debe ser diferente de la contraseña actual.",
  "changePassword.error.wrongPassword": "La contraseña actual es incorrecta.",
  "changePassword.error.failed":
    "No se pudo cambiar la contraseña. Por favor intenta de nuevo.",
  "changePassword.success": "¡Contraseña cambiada exitosamente!",

  // ── MFA Setup Modal ────────────────────────────────────────────────
  "mfaSetup.title": "Activar autenticación de dos factores",
  "mfaSetup.sendingCode": "Enviando código de verificación...",
  "mfaSetup.tryAgain": "Intentar de nuevo",
  "mfaSetup.codeSentTo": "Se ha enviado un código de verificación a",
  "mfaSetup.emailHint":
    "Revisa tu correo e introduce el código de 6 dígitos para continuar",
  "mfaSetup.smsHint": "Introduce el código de 6 dígitos para continuar",
  "mfaSetup.verifyButton": "Verificar código",
  "mfaSetup.verifyingButton": "Verificando...",
  "mfaSetup.codeArrival": "El código puede tardar hasta 30 segundos en llegar",
  "mfaSetup.enabledMessage":
    "¡La autenticación de dos factores ha sido activada!",
  "mfaSetup.saveCodesDesc":
    "Guarda estos códigos de respaldo en un lugar seguro",
  "mfaSetup.codesUsageDesc":
    "Cada código puede usarse una vez si pierdes acceso a tu teléfono",
  "mfaSetup.copyCode": "Copiar código",
  "mfaSetup.downloadCodes": "Descargar códigos",
  "mfaSetup.done": "Listo",
  "mfaSetup.warningLabel": "Advertencia:",
  "mfaSetup.warningText":
    "Guarda estos códigos de forma segura. No podrás verlos de nuevo.",
  "mfaSetup.failedToSend": "No se pudo enviar el código de verificación",
  "mfaSetup.verifyEnterCode": "Por favor introduce el código de verificación",
  "mfaSetup.verifyFailed": "Verificación fallida",

  // Deactivated account messages
  "deactivated.title": "Cuenta Desactivada",
  "deactivated.heroMessage":
    "Tu cuenta ha sido desactivada. Para acceder a las funciones de la aplicación, por favor contacta al propietario de tu cuenta.",
  "deactivated.accessDenied":
    "Tu cuenta ha sido desactivada. Por favor contacta a tu administrador.",
  "deactivated.accessRestricted": "Acceso restringido",

  // Lapsed subscription messages (después del período de gracia, inactiva)
  "lapsed.title": "Suscripción Inactiva",
  "lapsed.owner.message":
    "Tu suscripción ha vencido. Para restaurar el acceso a las funciones de la aplicación, por favor renueva tu suscripción. Aún puedes administrar tu Suscripción y Pago, Perfil de Usuario y Configuración.",
  "lapsed.owner.cta": "Ir a Suscripción y Pago",
  "lapsed.nonOwner.message":
    "La suscripción de esta cuenta ha vencido. Por favor contacta al propietario de tu cuenta para renovar la suscripción y restaurar el acceso a las funciones de la aplicación.",

  // ── URL Safety Analysis (Mobile) ────────────────────────────────────
  "urlsa.page.title":
    "Análisis de Seguridad de Publicaciones en Redes Sociales",
  "urlsa.page.subtitle":
    "Analiza cualquier enlace para detectar phishing, estafas y amenazas de seguridad",
  "urlsa.form.urlLabel": "Ingrese la URL a Analizar",
  "urlsa.form.urlPlaceholder": "https://ejemplo.com o ejemplo.com",
  "urlsa.form.clearAriaLabel": "Limpiar entrada",
  "urlsa.form.contextLabel": "Contexto (opcional)",
  "urlsa.form.contextPlaceholder":
    "¿Tiene detalles adicionales? ¿Cómo encontró esta URL? Cuanto más contexto proporcione, mejor podemos analizar el riesgo.",
  "urlsa.form.analyzeBtn": "Analizar",
  "urlsa.form.clearBtn": "Limpiar",
  "urlsa.form.viewResults": "Ver resultados del análisis",
  "urlsa.nav.backToInputs": "Volver al formulario",
  "urlsa.redirect.detected": "¡Redireccionamiento Detectado!",
  "urlsa.redirect.summary":
    "La redirección de {platform} fue resuelta — toca para ver los detalles.",
  "urlsa.redirect.body":
    "La URL que envió es un enlace de redirección de {platform}. Seguimos el enlace al destino real y lo analizamos.",
  "urlsa.redirect.youSubmitted": "URL enviada:",
  "urlsa.redirect.actualDest": "Destino real:",
  "urlsa.threat.scamDetected": "ESTAFA DETECTADA",
  "urlsa.threat.noThreats": "SIN AMENAZAS DETECTADAS",
  "urlsa.threat.shareIndividual":
    "Disponible para planes Familiar o Familiar Extendido",
  "urlsa.threat.shareFamily": "Compartir esta alerta con familiares",
  "urlsa.threat.share": "Compartir",
  "urlsa.results.whatToDo": "¿Qué Deberías Hacer?",
  "urlsa.results.detailedAnalysis": "Análisis Detallado",
  "urlsa.disclaimer":
    "El análisis de seguridad de URL proporcionado es solo para fines educativos. Para conocer los términos y limitaciones completos, consulte nuestro",

  // ── Image Analysis (Mobile) ──────────────────────────────────────────────
  "imgMobile.page.title": "Análisis de Capturas de Pantalla",
  "imgMobile.page.subtitle":
    "Sube capturas de pantalla para detectar estafas, phishing y contenido fraudulento usando análisis con IA",

  "imgMobile.upload.prompt": "Tomar una Foto o Elegir Imagen",
  "imgMobile.upload.formats": "JPEG, PNG, GIF, WebP, BMP, TIFF, SVG (Máx 10MB)",
  "imgMobile.upload.contextLabel": "Contexto (opcional)",
  "imgMobile.upload.contextPlaceholder":
    "¿Algún otro detalle o situación que agregar? (p.ej. 'Recibí esta imagen en un mensaje de un remitente desconocido')",
  "imgMobile.upload.analyzeBtn": "Analizar",
  "imgMobile.upload.cancelBtn": "Cancelar",
  "imgMobile.upload.startingUpload": "Iniciando carga...",
  "imgMobile.upload.complete": "¡Carga completa! Analizando...",
  "imgMobile.upload.loadingTitle": "Subiendo y analizando imagen...",
  "imgMobile.upload.loadingSubtitle":
    "Espera mientras subimos y analizamos tu captura de forma segura",
  "imgMobile.upload.noFileError": "Por favor selecciona un archivo primero",
  "imgMobile.upload.invalidTypeError":
    "Tipo de archivo no válido. Por favor sube una imagen (JPEG, PNG, GIF, WebP, BMP, TIFF, SVG)",
  "imgMobile.upload.fileTooLargeError":
    "Archivo demasiado grande. El tamaño máximo es 10MB",
  "imgMobile.upload.failedError":
    "Error al subir. Por favor inténtalo de nuevo.",
  "imgMobile.upload.dailyLimitError":
    "Límite diario de subidas alcanzado (25/día). Tu cuota se reiniciará mañana.",
  "imgMobile.upload.timeoutMsg":
    "El análisis está tardando más de lo esperado. Por favor actualiza.",
  "imgMobile.upload.successToast": "¡Verificaciones de seguridad completadas!",

  "imgMobile.quota.title": "Límite Diario Alcanzado",
  "imgMobile.quota.desc":
    "Has alcanzado tu límite diario de 25 subidas de imágenes. Se reinicia mañana.",

  "imgMobile.history.title": "Historial de Análisis",
  "imgMobile.history.loading": "Cargando...",
  "imgMobile.history.empty": "Sin análisis aún. ¡Sube tu primera imagen!",
  "imgMobile.history.showing": "Mostrando 10 de {total} análisis",
  "imgMobile.history.refreshSuccess": "¡Historial de análisis actualizado!",
  "imgMobile.history.refreshError": "Error al actualizar: ",
  "imgMobile.history.deleteError": "Error al eliminar: ",

  "imgMobile.card.statusVirus": "Virus",
  "imgMobile.card.statusBlocked": "Bloqueado",
  "imgMobile.card.statusScam": "Estafa",
  "imgMobile.card.statusSafe": "Seguro",

  "imgMobile.detail.backToUpload": "Volver a Subir",
  "imgMobile.detail.uploadAnother": "Subir Otra",
  "imgMobile.detail.deleteBtn": "Eliminar",
  "imgMobile.detail.noAnalysisTitle": "Ningún Análisis Seleccionado",
  "imgMobile.detail.noAnalysisDesc":
    "Selecciona un análisis del historial para ver los detalles.",
  "imgMobile.detail.malwareDetected": "MALWARE DETECTADO",
  "imgMobile.detail.securityFailed": "VERIFICACIÓN DE SEGURIDAD FALLIDA",
  "imgMobile.detail.scamDetected": "ESTAFA DETECTADA",
  "imgMobile.detail.noThreats": "SIN AMENAZAS DETECTADAS",
  "imgMobile.detail.shareIndividual":
    "Disponible Para Planes Familiar o Familiar Extendido",
  "imgMobile.detail.shareFamily": "Comparte esta alerta con familiares",
  "imgMobile.detail.share": "Compartir",
  "imgMobile.detail.blockedTitle": "Imagen Bloqueada por Tu Seguridad",
  "imgMobile.detail.malwareNotice":
    "Este archivo fue detectado como malware y ha sido puesto en cuarentena.",
  "imgMobile.detail.securityNotice":
    "Esta imagen no pasó nuestro filtro de seguridad y no puede mostrarse.",
  "imgMobile.detail.blockedReason": "Motivo:",
  "imgMobile.detail.scamTypeLabel": "Tipo de Estafa:",
  "imgMobile.detail.whatToDo": "¿Qué Deberías Hacer?",
  "imgMobile.detail.explanationTitle": "Explicación",
  "imgMobile.detail.imagePreviewTitle": "Vista Previa de Imagen",
  "imgMobile.detail.analyzedOn": "Análisis completado el {date}",
  "imgMobile.detail.unknownType": "Desconocido",

  "imgMobile.errors.unknown": "Error desconocido",

  // ── Phone Number Scam Check (Mobile + Desktop) ────────────────────────────
  "phoneCheck.page.title": "Búsqueda de Número de Teléfono",
  "phoneCheck.page.subtitle":
    "Verifica si un número de teléfono está asociado con estafadores conocidos o actividades fraudulentas",

  "phoneCheck.form.phoneLabel": "Número de Teléfono",
  "phoneCheck.form.phonePlaceholder": "Ingresa el número de teléfono",
  "phoneCheck.form.invalidPhone": "Número de teléfono no válido",
  "phoneCheck.form.contextLabel": "Contexto (opcional)",
  "phoneCheck.form.contextPlaceholder":
    "¿Algún otro detalle o situación que agregar? ¿Alguien te está llamando o pidiéndote que llames a este número? Cuanto más contexto proporciones, mejor podremos analizar el riesgo.",
  "phoneCheck.form.analyzing": "Analizando...",
  "phoneCheck.form.checkBtn": "Verificar Número",
  "phoneCheck.form.clearBtn": "Limpiar",

  "phoneCheck.mobile.viewResults": "Ver Resultados del Análisis",
  "phoneCheck.mobile.backToInputs": "Volver a Entradas",

  "phoneCheck.desktop.inputPanelTitle": "Entrada de Número de Teléfono",
  "phoneCheck.desktop.resultsPanelTitle": "Resultados del Análisis",

  "phoneCheck.tips.heading": "Consejos de Seguridad:",
  "phoneCheck.tips.tip1":
    "No pagues bajo presión. Tómate un momento y evalúa la situación",
  "phoneCheck.tips.tip2":
    "Nunca des información personal a personas que te llamen sin previo aviso",
  "phoneCheck.tips.tip3":
    "Ten cuidado con las personas que dicen ser de agencias gubernamentales",
  "phoneCheck.tips.tip4":
    "Verifica la identidad del llamante a través de canales oficiales",
  "phoneCheck.tips.tip5":
    "No cambies la configuración de tu teléfono o computadora",
  "phoneCheck.tips.tip6":
    "No interactúes con llamantes sospechosos, independientemente de sus amenazas de cárcel, policía o multas",
  "phoneCheck.tips.tip7": "No devuelvas llamadas de números desconocidos",
  "phoneCheck.tips.howTitle":
    "Cómo Funciona el Análisis de Números de Teléfono",
  "phoneCheck.tips.howDesc":
    "Nuestro verificador de números de teléfono busca en múltiples bases de datos de estafas e informes de la comunidad para identificar números asociados con actividades fraudulentas, robocalls y estafas.",

  "phoneCheck.results.readyTitle": "Listo para Verificar",
  "phoneCheck.results.readyDesc":
    "Ingresa un número de teléfono y haz clic en 'Verificar Número' para ver los resultados del análisis.",
  "phoneCheck.results.shareIndividual":
    "Disponible Para Planes Familiar o Familiar Extendido",
  "phoneCheck.results.shareFamily": "Comparte esta alerta con familiares",
  "phoneCheck.results.share": "Compartir",
  "phoneCheck.results.whatToDo": "¿Qué Deberías Hacer?",
  "phoneCheck.results.explanation": "Explicación",
  "phoneCheck.results.warningImportant": "Importante:",
  "phoneCheck.results.warningText":
    "NO llames a este número si una ventana emergente o un mensaje de texto te pide que lo llames. En su lugar, ve al sitio web oficial de la organización con la que estás tratando para encontrar un número al que llamar. Consulta los Consejos de Seguridad en el panel izquierdo.",
  "phoneCheck.results.dbResults": "Resultados de la Base de Datos",
  "phoneCheck.results.dbCategory": "Categoría:",
  "phoneCheck.results.dbSummary": "Resumen:",
  "phoneCheck.results.dbReports": "Reportes:",
  "phoneCheck.results.dbLastReported": "Último Reporte:",
  "phoneCheck.results.dbNoSources":
    "No se encontraron fuentes de base de datos para este número.",

  "phoneCheck.disclaimer":
    "El análisis de número de teléfono proporcionado es solo para fines educativos. Para conocer los términos y limitaciones completos, consulte nuestro",

  // ── Analysis Feedback Bar ──────────────────────────────────────────────────
  "feedback.prompt": "¿Fue útil este análisis?",
  "feedback.helpful": "Útil",
  "feedback.yesHelpful": "Sí, útil",
  "feedback.notHelpful": "No fue útil",
  "feedback.thanks": "¡Gracias por tu comentario!",
  "feedback.nudge":
    "Intenta agregar contexto a continuación para mejorar el resultado.",

  // ── Reanalyze With Context Panel ───────────────────────────────────────────
  "reanalyze.title": "Refinar Análisis con Contexto Adicional",
  "reanalyze.helperText":
    "Si tienes más contexto o detalles sobre el correo/texto que reenviaste, proporciónalos para mejorar el análisis.",
  "reanalyze.addContext": "Agregar Contexto",
  "reanalyze.contextLabel": "Tu Contexto Adicional",
  "reanalyze.contextPlaceholder":
    "ej., No tengo cuenta con esta empresa, no esperaba este correo, nunca realicé este pedido...",
  "reanalyze.reanalyzing": "Re-analizando...",
  "reanalyze.reanalyze": "Re-analizar",
  "reanalyze.cancel": "Cancelar",
  "reanalyze.error.addContext":
    "Por favor agrega algún contexto para ayudarnos a refinar el análisis.",
  "reanalyze.error.failed":
    "No se pudo re-analizar. Por favor inténtalo de nuevo.",
  "reanalyze.success": "Tu contexto ha sido procesado. Análisis actualizado.",

  // ── URL Safety Analysis (Desktop) – additional keys ───────────────────────
  "urlsa.section.enterUrl": "Ingresar Detalles de URL",
  "urlsa.section.results": "Resultados del Análisis",
  "urlsa.empty.title": "Listo para Analizar",
  "urlsa.empty.description":
    "Ingresa una URL a la izquierda para detectar phishing, estafas y amenazas de seguridad.",
  "urlsa.share.threatType": "Seguridad de URL",
  "urlsa.share.threatTitle": "URL Potencialmente Peligrosa Detectada",

  // ── Social Media Analysis (Desktop + Mobile + Details) ────────────────────
  "social.page.title": "Análisis de Redes Sociales",
  "social.page.subtitle":
    "Analiza negocios e interacciones en redes sociales para detectar posibles estafas e indicadores de fraude",
  "social.page.subtitleMobile":
    "Analiza negocios y sitios web en busca de posibles estafas",
  "social.tab.inputs": "Entradas",
  "social.tab.history": "Historial de Análisis",
  "social.disclaimer":
    "El análisis de redes sociales proporcionado es solo para fines educativos. Para conocer los términos y limitaciones completos, consulte nuestro",
  "social.section.inputs": "Entradas del Análisis",
  "social.section.results": "Resultados del Análisis",
  "social.viewResults": "Ver Resultados del Análisis",
  "social.history.recentTitle": "Análisis Recientes",
  "social.history.loading": "Cargando historial...",
  "social.history.empty":
    "No hay análisis recientes. Envía un formulario para comenzar.",
  "social.nav.backToForm": "Volver al Formulario",
  "social.nav.newAnalysis": "Nuevo Análisis",
  "social.nav.startAnalysis": "Iniciar Análisis",
  "social.empty.title": "Sin Resultados Aún",
  "social.empty.description":
    "Envía un análisis o selecciona un elemento reciente para ver los resultados.",
  "social.detail.heading": "Detalles del Análisis",
  "social.detail.analysisUnavailable": "Análisis No Disponible",
  "social.detail.insufficientData": "Datos Insuficientes",
  "social.detail.potentialScam": "Posible Estafa Detectada",
  "social.detail.appearsLegitimate": "Parece Legítimo",
  "social.detail.statusUnknown": "Estado del Análisis Desconocido",
  "social.detail.shareIndividual":
    "Disponible Para Planes Familiar o Familiar Extendido",
  "social.detail.shareFamily": "Compartir esta alerta con familiares",
  "social.detail.share": "Compartir",
  "social.detail.limitedData": "Datos Limitados Disponibles",
  "social.detail.moreAccurate":
    "Para un análisis más preciso, proporciona información adicional en los campos de Entrada del Caso. Cuantos más detalles ingreses, mejor será nuestro análisis:",
  "social.detail.missingWebsite": "URL completa del sitio web del negocio",
  "social.detail.missingMerchandise": "Tipo específico de producto/servicio",
  "social.detail.missingContact": "Información de la persona de contacto",
  "social.detail.missingInteraction": "Cómo descubriste este negocio",
  "social.detail.missingPlatform":
    "Detalles de la plataforma de redes sociales",
  "social.detail.missingName": "Nombre del negocio o persona",
  "social.detail.fillMissing":
    "Completa los campos faltantes arriba y reenvía para obtener mejores resultados.",
  "social.detail.comprehensiveInfo":
    "Has proporcionado información completa. El análisis puede estar limitado por la disponibilidad de datos externos.",
  "social.detail.whatToDo": "¿Qué Deberías Hacer?",
  "social.detail.explanation": "Explicación",
  "social.detail.additionalData": "Datos Adicionales Necesarios",
  "social.detail.noCase": "Ningún Caso Seleccionado",
  "social.detail.noCaseDesc":
    "Selecciona un caso del historial o envía un nuevo análisis para ver los detalles aquí.",
  "social.share.threatType": "Estafa en Redes Sociales",
  "social.form.error.nameOrUrl":
    "Por favor ingresa un Nombre de Negocio/Persona o una Página Web/URL, o ambos",
  "social.form.error.notLoggedIn":
    "Usuario no autenticado. Por favor inicia sesión para realizar el análisis.",
  "social.form.error.notLoggedInDesktop":
    "Debes iniciar sesión para realizar el análisis de redes sociales.",
  "social.form.error.deleteFailed":
    "No se pudo eliminar el análisis. Por favor inténtalo de nuevo.",

  // ── Simulation Profile Settings ──────────────────────────────────────────────
  "sps.page.title": "Configuración del Perfil de Simulación",
  "sps.page.subtitle":
    "Configura cómo funcionan tus escenarios de phishing de práctica",
  "sps.page.configuringFor": "Configurando perfil para: {name}",
  "sps.loading": "Cargando...",
  "sps.error.prefix": "Error: ",

  "sps.status.heading": "Estado de la Simulación",
  "sps.status.label": "Estado de la Simulación:",
  "sps.status.reasonLabel": "Motivo:",
  "sps.status.demo.text": "MODO DEMO",
  "sps.status.demo.messagePart1":
    "Los calendarios de simulación están desactivados para cuentas demo. No se enviarán correos electrónicos ni mensajes SMS de phishing.",
  "sps.status.demo.messagePart2":
    "Aún puedes explorar y ajustar la configuración de simulación a continuación.",
  "sps.status.paused.text": "EN PAUSA",
  "sps.status.paused.noSub":
    "Las simulaciones están en pausa porque no hay una suscripción activa. Se reanudarán automáticamente cuando tu suscripción esté activa.",
  "sps.status.scheduling.text": "PROGRAMANDO",
  "sps.status.scheduling.message":
    "Tu perfil de simulación está activo. Los calendarios se generan automáticamente durante la noche y los mensajes de simulación se enviarán pronto.",
  "sps.status.active.text": "ACTIVO",
  "sps.status.active.message":
    "Las simulaciones se están ejecutando según lo programado",
  "sps.status.paused.base": "Las simulaciones están en pausa",
  "sps.status.paused.until": "En pausa hasta el {date}",
  "sps.status.paused.ended":
    "El período de pausa ha terminado - por favor reanuda",
  "sps.status.paused.days": "En pausa por {days} días",
  "sps.status.paused.indefinitely": "En pausa indefinidamente",
  "sps.status.resumeDisabled": "Reanudar está desactivado en modo demo",
  "sps.status.resumeTitle": "Reanudar simulaciones",
  "sps.status.resume": "Reanudar",
  "sps.status.pauseDisabled": "Pausar está desactivado en modo demo",
  "sps.status.pauseTitle": "Pausar simulaciones",
  "sps.status.pause": "Pausar",

  "sps.config.heading": "Tu Configuración",
  "sps.presets.heading":
    "¿Qué tan difíciles deben ser tus escenarios de práctica?",
  "sps.presets.headingMobile": "Elige tu dificultad",
  "sps.presets.subheading":
    "Elige un nivel de práctica a continuación. Siempre puedes cambiarlo después.",
  "sps.presets.subheadingMobile": "Elige un nivel. Puedes cambiarlo después.",

  "sps.preset.easy.title": "Suave",
  "sps.preset.easy.subtitle": "Bueno para principiantes",
  "sps.preset.easy.desc":
    "Patrones de estafa obvios que son fáciles de identificar",
  "sps.preset.easy.difficulty": "Básico (1/10)",
  "sps.preset.easy.frequency": "2\u00d7 cada 10 días",
  "sps.preset.easy.channels": "Solo correo electrónico",
  "sps.preset.easy.timing": "Horario laboral",
  "sps.preset.easy.context": "General, Regional",

  "sps.preset.average.title": "Moderado",
  "sps.preset.average.subtitle": "Escenarios de estafa realistas",
  "sps.preset.average.desc":
    "Mezcla de intentos de phishing obvios y complicados",
  "sps.preset.average.difficulty": "Intermedio (4/10)",
  "sps.preset.average.frequency": "5\u00d7 cada 10 días",
  "sps.preset.average.channels": "Correo electrónico, SMS",
  "sps.preset.average.timing": "Horario laboral",
  "sps.preset.average.context": "General, Regional, Eventos",

  "sps.preset.difficult.title": "Desafiante",
  "sps.preset.difficult.subtitle": "Tácticas sutiles y avanzadas",
  "sps.preset.difficult.desc":
    "Ingeniería social sofisticada, difícil de detectar",
  "sps.preset.difficult.difficulty": "Experto (7/10)",
  "sps.preset.difficult.frequency": "8\u00d7 cada 10 días",
  "sps.preset.difficult.channels": "Correo electrónico, SMS, Voz",
  "sps.preset.difficult.timing": "Cualquier hora",
  "sps.preset.difficult.context": "Todas las categorías",

  "sps.preset.custom.title": "Personalizado",
  "sps.preset.custom.subtitle": "Control total",
  "sps.preset.custom.desc":
    "Tú eliges cada detalle de tu configuración de simulación",
  "sps.preset.custom.descMobile":
    "Elige cada detalle de tu configuración de simulación",

  "sps.preset.locked": "Requiere plan Familiar",
  "sps.preset.currentBadge": "Configuración Actual",
  "sps.preset.currentBadgeMobile": "Actual",
  "sps.preset.viewDetails": "Ver detalles de la configuración actual \u2192",

  "sps.dashboard.heading": "Configuración de Simulación",
  "sps.dashboard.back": "Volver",
  "sps.dashboard.profileSuffix": " Perfil",
  "sps.dashboard.tagPreset": "Preajuste",
  "sps.dashboard.tagCustom": "Personalizado",
  "sps.dashboard.tagActive": "Activo",
  "sps.dashboard.hintPreset":
    "Esta configuración está establecida por el preajuste {preset}.",
  "sps.dashboard.hintPresetMobile": "Configurado por el preajuste {preset}.",
  "sps.dashboard.switchToCustom": "Cambiar a Personalizado",
  "sps.dashboard.switchToCustomSuffix": " para editar individualmente.",
  "sps.dashboard.hintEditable":
    "Toca cualquier insignia para cambiar tu configuración.",
  "sps.dashboard.saveCustom": "Usar Configuración Personalizada",
  "sps.dashboard.savePreset": "Usar Preajuste {preset}",

  "sps.badge.difficulty": "Dificultad",
  "sps.badge.frequency": "Frecuencia",
  "sps.badge.channels": "Canales",
  "sps.badge.timing": "Horario",
  "sps.badge.context": "Contexto",

  "sps.tier.basic": "Básico",
  "sps.tier.intermediate": "Intermedio",
  "sps.tier.advanced": "Avanzado",
  "sps.tier.expert": "Experto",
  "sps.tier.master": "Maestro",

  "sps.freqTier.low": "Bajo",
  "sps.freqTier.medium": "Medio",
  "sps.freqTier.high": "Alto",
  "sps.freqTier.veryHigh": "Muy Alto",

  "sps.channel.email": "Correo Electrónico",
  "sps.channel.sms": "SMS",
  "sps.channel.qr": "Código QR",
  "sps.channel.voice": "Llamada de Voz",
  "sps.channel.social": "Redes Sociales",

  "sps.timing.day": "Horario Diurno",
  "sps.timing.evening": "Tarde",
  "sps.timing.any": "Cualquier Hora",

  "sps.context.general": "General",
  "sps.context.events": "Eventos",
  "sps.context.regional": "Regional",
  "sps.context.financial": "Financiero",
  "sps.context.emergencies": "Emergencias",
  "sps.context.personalized": "Personalizado",

  "sps.setupJourney.continue": "Continuar Configurando Tu Cuenta",

  "sps.pauseModal.title": "Pausar Simulaciones",
  "sps.pauseModal.description":
    "Pausar las simulaciones detendrá el envío de todos los mensajes programados. Puedes reanudarlas en cualquier momento.",
  "sps.pauseModal.optionIndefiniteTitle": "Pausar indefinidamente",
  "sps.pauseModal.optionIndefiniteDesc":
    "Las simulaciones permanecerán en pausa hasta que las reanudes manualmente",
  "sps.pauseModal.optionUntilTitle": "Pausar hasta una fecha específica",
  "sps.pauseModal.optionUntilDesc":
    "Las simulaciones se reanudarán automáticamente en la fecha seleccionada",
  "sps.pauseModal.optionDaysTitle": "Pausar por número de días",
  "sps.pauseModal.optionDaysDesc":
    "Las simulaciones se reanudarán después del número de días especificado",
  "sps.pauseModal.days": "días",
  "sps.pauseModal.reasonLabel": "Motivo para pausar (opcional)",
  "sps.pauseModal.reasonPlaceholder":
    "Ej., Vacaciones, período de mantenimiento, sesión de capacitación...",
  "sps.pauseModal.reasonCounter": "{n}/255 caracteres",
  "sps.pauseModal.cancel": "Cancelar",
  "sps.pauseModal.confirm": "Pausar Simulaciones",

  // ── Social Media Analysis — Form labels & placeholders ────────────────────
  "social.form.label.businessInfo": "Información del Negocio*",
  "social.form.label.businessName": "Nombre del Negocio",
  "social.form.label.websiteUrl": "Sitio Web/URL",
  "social.form.label.platform":
    "¿Dónde encontraste esta publicación o negocio?",
  "social.form.label.merchandise": "¿Qué producto o servicio se ofrece?",
  "social.form.label.interaction":
    "¿Cómo encontraste esta publicación o negocio?",
  "social.form.label.contactPerson": "Persona de Contacto",
  "social.form.label.contactPhone": "Teléfono de Contacto",
  "social.form.label.context": "Contexto (opcional)",
  "social.form.placeholder.businessName": "Ingresa el nombre del negocio",
  "social.form.placeholder.websiteUrl":
    "Ingresa la URL del sitio web (ej., ejemplo.com o www.ejemplo.com)",
  "social.form.placeholder.selectPlatform": "Seleccionar Plataforma",
  "social.form.placeholder.selectType": "Seleccionar Tipo",
  "social.form.placeholder.selectMethod": "Seleccionar Método",
  "social.form.placeholder.contactPerson": "Nombre de la persona de contacto",
  "social.form.placeholder.phone": "Ingresa el número de teléfono",
  "social.form.hint.phone":
    "Selecciona el país e ingresa el número de teléfono. Por defecto EE.UU. (+1)",
  "social.form.placeholder.context":
    "¿Otros detalles o situaciones que agregar?",
  "social.form.button.analyzing": "Analizando...",
  "social.form.button.analyze": "Analizar",
  "social.form.button.clear": "Limpiar",

  // ── Social Media Analysis — History panel ────────────────────────────────
  "social.history.heading": "Historial de Análisis",
  "social.history.caseCount": "{count} casos",
  "social.history.loadingCases": "Cargando casos...",
  "social.history.noResults": "No se encontraron casos",
  "social.history.submitHint":
    "Envía un análisis para ver tu historial de casos aquí.",
  "social.history.deleteTitle": "Eliminar este análisis",
  "social.history.delete": "Eliminar",
  "social.history.unknownBusiness": "Negocio Desconocido",
  "social.history.unknown": "Desconocido",
  "social.history.previous": "Anterior",
  "social.history.pageOf": "Página {page} de {total}",
  "social.history.next": "Siguiente",
  "social.history.hide": "Ocultar Historial",
  "social.history.show": "Mostrar Historial",
  "social.history.hideTitle": "Ocultar Historial de Casos",
  "social.history.showTitle": "Mostrar Historial de Casos",

  // ── Social Media Analysis — Details panel ────────────────────────────────
  "social.detail.unknownBusiness": "Negocio Desconocido",

  // ── ADA Accessibility Widget ───────────────────────────────────────────────
  "ada.title": "Accesibilidad",
  "ada.panel.ariaLabel": "Configuraci\u00f3n de accesibilidad",
  "ada.close.title": "Cerrar",
  "ada.close.ariaLabel": "Cerrar panel de accesibilidad",
  "ada.card.biggerText": "Texto m\u00e1s grande",
  "ada.card.contrast": "Contraste",
  "ada.card.textSpacing": "Espaciado de texto",
  "ada.card.lineHeight": "Altura de l\u00ednea",
  "ada.fontScale.ariaLabel": "Escala de tama\u00f1o de fuente",
  "ada.resetAll": "Restablecer todo",
  "ada.resetAll.ariaLabel":
    "Restablecer todas las configuraciones de accesibilidad",
  "ada.footer.brand": "Accesibilidad \u00b7 KinShield",
  "ada.fab.open": "Abrir configuraci\u00f3n de accesibilidad",
  "ada.fab.close": "Cerrar configuraci\u00f3n de accesibilidad",
  "ada.fab.label": "Accesibilidad",
  "ada.biggerText.title": "Actual: {label} \u2014 clic para aumentar",
  "ada.biggerText.ariaLabel":
    "Texto m\u00e1s grande, tama\u00f1o actual {label}, clic para aumentar",
  "ada.setFontSize.ariaLabel": "Establecer tama\u00f1o de fuente a {label}",
  "ada.contrast.title": "Actual: {theme} \u2014 clic para cambiar",
  "ada.contrast.ariaLabel":
    "Contraste, actualmente tema {theme}, clic para cambiar",
  "ada.contrast.dark": "Oscuro",
  "ada.contrast.light": "Claro",
  "ada.textSpacing.title":
    "Espaciado de texto: {label} \u2014 clic para aumentar",
  "ada.textSpacing.ariaLabel":
    "Espaciado de texto, actualmente {label}, clic para aumentar",
  "ada.setSpacing.ariaLabel": "Establecer espaciado de texto a {label}",
  "ada.lineHeight.cardTitle":
    "Altura de l\u00ednea: {label} \u2014 clic para aumentar",
  "ada.lineHeight.cardAriaLabel":
    "Altura de l\u00ednea, actualmente {label}, clic para aumentar",
  "ada.setLineHeight.ariaLabel": "Establecer altura de l\u00ednea a {label}",
  "ada.fontSizeBadge.ariaLabel": "Tama\u00f1o de fuente: {label}",
  "ada.spacing.label.default": "Predeterminado",
  "ada.spacing.label.wide": "Amplio",
  "ada.spacing.label.wider": "M\u00e1s amplio",
  "ada.spacing.label.widest": "M\u00e1ximo",
  "ada.lineHeight.label.default": "Predeterminado",
  "ada.lineHeight.label.relaxed": "Relajado",
  "ada.lineHeight.label.loose": "Suelto",
  "ada.lineHeight.label.veryLoose": "Muy suelto",

  // ── ChatWidget / Scout chatbot ─────────────────────────────────────
  "chat.fab.label": "Scout",
  "chat.fab.close": "Cerrar chat",
  "chat.fab.open": "Chatear con Scout \u2014 tu Guardia Personal Digital",
  "chat.fab.ariaOpen": "Abrir Scout, Guardia Personal Digital",
  "chat.window.ariaLabel": "Scout \u2014 Guardia Personal Digital",
  "chat.header.name": "Scout",
  "chat.header.subtitle": "Guardia Personal Digital",
  "chat.header.status.analyzing": "Analizando...",
  "chat.header.status.ready": "En servicio \u2014 Listo para proteger",
  "chat.header.newConversation": "Nueva conversaci\u00f3n",
  "chat.tts.enable": "Activar lectura en voz alta",
  "chat.tts.disable": "Desactivar lectura en voz alta",
  "chat.tts.ariaEnable": "Activar texto a voz",
  "chat.tts.ariaDisable": "Desactivar texto a voz",
  "chat.tts.readAloud": "Leer en voz alta",
  "chat.tts.stop": "Detener lectura",
  "chat.tts.ariaReadAloud": "Leer mensaje en voz alta",
  "chat.welcome.desc":
    "Soy Scout, tu Guardia Personal Digital \u2014 pi\u00e9nsame como tu experto en seguridad personal. Siempre en servicio, siempre cuid\u00e1ndote.",
  "chat.starter.email": "\u00bfC\u00f3mo verifico si un correo es una estafa?",
  "chat.starter.training":
    "\u00bfQu\u00e9 capacitaci\u00f3n de seguridad est\u00e1 disponible?",
  "chat.starter.alerts": "Mu\u00e9strame las \u00faltimas alertas de estafas",
  "chat.starter.help": "\u00bfEn qu\u00e9 me puedes ayudar?",
  "chat.error.connection":
    "Lo siento, tengo problemas para conectarme ahora mismo. Por favor int\u00e9ntalo de nuevo en un momento.",
  "chat.nudge.dismiss": "Descartar",
  "chat.input.placeholder": "Preg\u00fantame cualquier cosa...",
  "chat.send.title": "Enviar mensaje",
  "chat.footer.powered": "Desarrollado por KinShield AI",

  // ── DPG Personality — greetings, nudges, reactions ──────────────────────
  "dpg.greeting.morning.0":
    "\u00a1Buenos d\u00edas, {name}! Tu Guardia Personal Digital est\u00e1 de servicio. \u2600\ufe0f",
  "dpg.greeting.morning.1":
    "\u00a1Buenos d\u00edas, {name}! He estado vigilando tus cuentas durante la noche. \u00a1Todo en orden!",
  "dpg.greeting.morning.2":
    "\u00a1Buenos d\u00edas, {name}! \u00bfListo para empezar el d\u00eda de forma segura? Aqu\u00ed estoy si me necesitas.",
  "dpg.greeting.afternoon.0":
    "\u00a1Buenas tardes, {name}! Aqu\u00ed estoy si necesitas ayuda con la seguridad.",
  "dpg.greeting.afternoon.1":
    "Revisi\u00f3n de la tarde \u2014 todo parece seguro por mi parte.",
  "dpg.greeting.afternoon.2":
    "\u00a1Hola, {name}! \u00bfQued\u00e1ndote seguro esta tarde? Te tengo cubierto.",
  "dpg.greeting.evening.0":
    "\u00a1Buenas noches, {name}! \u00bfTerminando el d\u00eda? Mantendr\u00e9 vigilancia mientras descansas. \ud83c\udf19",
  "dpg.greeting.evening.1":
    "\u00a1Buenas noches, {name}! Un buen momento para revisar mensajes sospechosos del d\u00eda.",
  "dpg.greeting.evening.2":
    "\u00a1Hola, {name}! \u00bfAntes de terminar el d\u00eda, quieres que haga un an\u00e1lisis r\u00e1pido de seguridad?",
  "dpg.nudge.dashboard.0":
    "\u00a1Bienvenido de nuevo! Tu puntuaci\u00f3n de seguridad est\u00e1 bien. \u00bfQuieres que haga una revisi\u00f3n r\u00e1pida de seguridad?",
  "dpg.nudge.dashboard.1":
    "He estado vigilando mientras estabas fuera. \u00a1Todo parece seguro! \ud83d\udee1\ufe0f",
  "dpg.nudge.dashboard.2":
    "Consejo profesional: Revisa tu puntuaci\u00f3n de seguridad con regularidad \u2014 te avisar\u00e9 si algo cambia.",
  "dpg.nudge.email.0":
    "Veo que est\u00e1s revisando un correo. \u00a1P\u00e9galo aqu\u00ed y lo analizar\u00e9 en busca de se\u00f1ales de phishing!",
  "dpg.nudge.email.1":
    "Recuerda: Las empresas leg\u00edtimas nunca piden contrase\u00f1as por correo. Estoy aqu\u00ed si parece sospechoso.",
  "dpg.nudge.text.0":
    "\u00bfRecibiste un texto sospechoso? Puedo analizarlo para detectar patrones de estafa como avisos de entrega falsos o alertas bancarias.",
  "dpg.nudge.social.0":
    "\u00a1Las estafas en redes sociales est\u00e1n en aumento! D\u00e9jame ayudarte a verificar si ese mensaje o perfil es leg\u00edtimo.",
  "dpg.nudge.url.0":
    "\u00bfRevisando un enlace? \u00a1Buena decisi\u00f3n! Pega la URL y te dir\u00e9 si es seguro antes de hacer clic.",
  "dpg.nudge.training.0":
    "\u00a1Excelente decisi\u00f3n invertir en tu conocimiento de seguridad! Estar\u00e9 aqu\u00ed si tienes preguntas sobre alguna lecci\u00f3n.",
  "dpg.nudge.alerts.0":
    "\u00a1Mant\u00e9nte informado! Estas son las \u00faltimas alertas de estafas en tu \u00e1rea. Toca cualquiera para aprender c\u00f3mo protegerte.",
  "dpg.nudge.settings.0":
    "\u00bfRevisando tu configuraci\u00f3n? \u00a1Aseg\u00farate de tener activada la autenticaci\u00f3n de dos factores \u2014 es tu mejor defensa!",
  "dpg.nudge.settings.1":
    "\u00bfSab\u00edas que hay un panel de Accesibilidad en la esquina inferior izquierda? Puedes ajustar el tama\u00f1o de letra, el espaciado y el contraste \u2014 \u00a1sin necesidad de cambiar tu cuenta!",
  "dpg.nudge.plan.0":
    "\u00bfMirando planes? Los niveles superiores desbloquean monitoreo avanzado de amenazas. \u00a1Puedo explicarte las diferencias!",
  "dpg.nudge.idle.0":
    "Todo tranquilo en el frente digital. \u00a1Estoy vigilando! \ud83d\udee1\ufe0f",
  "dpg.nudge.idle.1":
    "\u00bfSab\u00edas que se env\u00edan m\u00e1s de 3.400 millones de correos de phishing al d\u00eda? \u00bfTienes uno sospechoso? \u00a1Reenv\u00edamelo y lo analizar\u00e9!",
  "dpg.nudge.idle.2":
    "He estado de guardia. Av\u00edsame si algo parece raro \u2014 siempre estoy de servicio.",
  "dpg.nudge.idle.3":
    "Consejo r\u00e1pido: \u00a1Nunca compartas c\u00f3digos de verificaci\u00f3n con nadie, aunque digan ser de tu banco!",
  "dpg.nudge.idle.4":
    "Tu seguridad digital es mi prioridad n\u00famero 1. \u00bfEn qu\u00e9 puedo ayudarte?",
  "dpg.nudge.idle.5":
    "Dato curioso: \u00a1Puedo detectar m\u00e1s de 50 tipos de patrones de estafa. Ponme a prueba!",
  "dpg.nudge.idle.6":
    "Recuerda actualizar tus contrase\u00f1as con regularidad. \u00a1Puedo guiarte en las mejores pr\u00e1cticas!",
  "dpg.reaction.scam.0":
    "\ud83d\udea8 \u00a1Atenci\u00f3n! Eso parece sospechoso. D\u00e9jame explicarte las se\u00f1ales de alerta que encontr\u00e9.",
  "dpg.reaction.scam.1":
    "\u26a0\ufe0f \u00a1Mis sensores est\u00e1n activos! Esto tiene varios indicadores de estafa. Vamos a analizarlo m\u00e1s de cerca.",
  "dpg.reaction.scam.2":
    "\ud83d\uded1 Espera \u2014 estoy detectando se\u00f1ales de alerta serias. \u00a1No hagas clic en nada todav\u00eda!",
  "dpg.reaction.safe.0":
    "\u2705 \u00a1Todo despejado! Esto parece leg\u00edtimo. \u00a1Pero mant\u00e9nte alerta!",
  "dpg.reaction.safe.1":
    "\ud83d\udc4d \u00a1Parece seguro! No se detectaron se\u00f1ales de alerta. \u00a1Bien hecho por verificar!",
  "dpg.reaction.safe.2":
    "\ud83d\udee1\ufe0f Buenas noticias \u2014 esto parece genuino. \u00a1Fue inteligente de tu parte verificar primero!",
  "dpg.reaction.returned.0":
    "\u00a1Bienvenido de nuevo! He estado de guardia. \ud83d\udee1\ufe0f",
  "dpg.reaction.returned.1":
    "\u00a1Hola, ya est\u00e1s de vuelta! Todo estuvo tranquilo mientras no estabas.",
  "dpg.reaction.returned.2":
    "\u00a1Me alegra verte de nuevo! Listo para ayudarte cuando lo necesites.",
  "dpg.reaction.first.0":
    "\u00a1Bienvenido a KinShield! Soy tu Guardia Personal Digital. Pi\u00e9nsame como tu experto personal en seguridad \u2014 siempre de servicio, siempre cuid\u00e1ndote. \ud83d\udee1\ufe0f",
  "dpg.reaction.inactive.0":
    "\u00a1Aqu\u00ed sigo! Solo vigilando. Av\u00edsame si necesitas algo.",
  "dpg.reaction.inactive.1":
    "No me he ido a ning\u00fan lado \u2014 \u00a1tu Guardia Personal Digital nunca duerme!",

  // ── Help Guide Modal UI ─────────────────────────────────────────────────
  "helpGuide.modal.closeGuide": "Cerrar gu\u00eda",
  "helpGuide.modal.stepLabel": "Paso",
  "helpGuide.modal.of": "de",
  "helpGuide.modal.allSet": "\u00a1Todo listo!",
  "helpGuide.modal.complete": "Completado",
  "helpGuide.modal.back": "Atr\u00e1s",
  "helpGuide.modal.next": "Siguiente",
  "helpGuide.modal.finish": "Finalizar",
  "helpGuide.modal.goThereNow": "Ir ahora",
  "helpGuide.modal.copy": " Copiar",
  "helpGuide.modal.copied": " \u00a1Copiado!",
  "helpGuide.modal.copyToClipboard": "Copiar al portapapeles",

  // Help Guide Button
  "helpGuide.button.how": "\u00bfC\u00f3mo?",
  "helpGuide.button.showMeHow": "Mu\u00e9strame c\u00f3mo",

  // ── Help Guide 1 — Reenviar un correo sospechoso ─────────────────────────
  "helpGuide.1.title": "C\u00f3mo reenviar un correo sospechoso",
  "helpGuide.1.subtitle":
    "Elige tu aplicaci\u00f3n de correo y sigue los pasos \u2014 tu an\u00e1lisis aparecer\u00e1 en esta p\u00e1gina.",
  "helpGuide.1.finalStep":
    "Una vez que env\u00edes el correo, vuelve aqu\u00ed y <strong>toca Actualizar</strong> \u2014 tu an\u00e1lisis de amenazas normalmente aparece en 60 segundos.",
  "helpGuide.1.actionLabel": "\u00a1Entendido!",
  "helpGuide.1.p0.name": "Gmail",
  "helpGuide.1.p0.s0.instruction":
    "Abre el correo sospechoso en Gmail. <strong>No hagas clic en ning\u00fan enlace</strong> dentro de \u00e9l.",
  "helpGuide.1.p0.s0.detail":
    "Si est\u00e1s en tu tel\u00e9fono, abre la app de Gmail. En una computadora, ve a mail.google.com.",
  "helpGuide.1.p0.s1.instruction":
    "Toca el <strong>men\u00fa de tres puntos \u22ee</strong> (arriba a la derecha del correo) y selecciona <strong>\u201cReenviar\u201d</strong>.",
  "helpGuide.1.p0.s1.detail":
    "En m\u00f3vil, puede que necesites desplazarte hasta el final del correo y tocar la flecha de Reenviar.",
  "helpGuide.1.p0.s2.instruction":
    "En el campo <strong>\u201cPara\u201d</strong>, ingresa:",
  "helpGuide.1.p0.s2.detail":
    "No cambies el asunto ni el cuerpo del correo \u2014 KinShield necesita el contenido original para analizarlo con precisi\u00f3n.",
  "helpGuide.1.p0.s3.instruction":
    "Toca <strong>Enviar</strong>. Tu an\u00e1lisis aparecer\u00e1 en esta p\u00e1gina en aproximadamente 60 segundos.",
  "helpGuide.1.p0.s3.detail":
    "Tambi\u00e9n recibir\u00e1s una notificaci\u00f3n cuando est\u00e9 listo. Toca <strong>Actualizar</strong> en esta p\u00e1gina si no lo ves de inmediato.",
  "helpGuide.1.p1.name": "Outlook",
  "helpGuide.1.p1.s0.instruction":
    "Abre el correo sospechoso en Outlook. <strong>No hagas clic en ning\u00fan enlace</strong> dentro de \u00e9l.",
  "helpGuide.1.p1.s0.detail":
    "Funciona igual en Outlook de escritorio, web (outlook.com) o la app m\u00f3vil.",
  "helpGuide.1.p1.s1.instruction":
    "Haz clic en el bot\u00f3n <strong>Reenviar</strong> (icono de flecha) en la barra de herramientas, o haz clic derecho en el correo y elige <strong>\u201cReenviar\u201d</strong>.",
  "helpGuide.1.p1.s1.detail":
    "En m\u00f3vil, toca la flecha de respuesta en la parte inferior y elige Reenviar.",
  "helpGuide.1.p1.s2.instruction":
    "En el campo <strong>\u201cPara\u201d</strong>, ingresa:",
  "helpGuide.1.p1.s2.detail":
    "Deja el asunto y el cuerpo sin cambios para que KinShield reciba el mensaje original.",
  "helpGuide.1.p1.s3.instruction":
    "Haz clic en <strong>Enviar</strong>, luego vuelve a esta p\u00e1gina.",
  "helpGuide.1.p1.s3.detail":
    "Tu an\u00e1lisis aparecer\u00e1 aqu\u00ed en aproximadamente 60 segundos. Toca Actualizar si es necesario.",
  "helpGuide.1.p2.name": "Apple Mail",
  "helpGuide.1.p2.s0.instruction":
    "Abre el correo sospechoso en la <strong>app Mail</strong> de tu iPhone, iPad o Mac.",
  "helpGuide.1.p2.s0.detail":
    "No toques ning\u00fan enlace ni descargues archivos adjuntos.",
  "helpGuide.1.p2.s1.instruction":
    "Toca la <strong>flecha de responder/reenviar</strong> (\u21a9 en la parte inferior), luego selecciona <strong>Reenviar</strong>.",
  "helpGuide.1.p2.s1.detail":
    "En Mac, tambi\u00e9n puedes ir a Mensaje \u2192 Reenviar en la barra de men\u00fa, o presionar \u21e7\u2318F.",
  "helpGuide.1.p2.s2.instruction":
    "En el campo <strong>\u201cPara\u201d</strong>, escribe:",
  "helpGuide.1.p2.s2.detail":
    "Mant\u00e9n el contenido del correo original intacto \u2014 no modifiques el asunto ni el cuerpo.",
  "helpGuide.1.p2.s3.instruction":
    "Toca el bot\u00f3n <strong>Enviar</strong> (flecha \u2191). Tu an\u00e1lisis aparecer\u00e1 aqu\u00ed en breve.",
  "helpGuide.1.p2.s3.detail":
    "Generalmente listo en 60 segundos. Toca Actualizar en esta p\u00e1gina si no lo ves.",
  "helpGuide.1.p3.name": "Yahoo Mail",
  "helpGuide.1.p3.s0.instruction":
    "Abre el correo sospechoso en Yahoo Mail. <strong>No hagas clic en ning\u00fan enlace</strong>.",
  "helpGuide.1.p3.s1.instruction":
    "Haz clic en el bot\u00f3n <strong>Reenviar</strong> en la barra de herramientas (o el men\u00fa \u201c...\u201d \u2192 Reenviar en m\u00f3vil).",
  "helpGuide.1.p3.s2.instruction":
    "En el campo <strong>\u201cPara\u201d</strong>, ingresa:",
  "helpGuide.1.p3.s2.detail":
    "No cambies el asunto ni el contenido del correo.",
  "helpGuide.1.p3.s3.instruction":
    "Haz clic en <strong>Enviar</strong>. Tu an\u00e1lisis aparecer\u00e1 en esta p\u00e1gina en aproximadamente un minuto.",

  // ── Help Guide 2 — Reenviar un mensaje de texto sospechoso ───────────────
  "helpGuide.2.title": "C\u00f3mo reenviar un mensaje de texto sospechoso",
  "helpGuide.2.subtitle":
    "Reenv\u00eda el texto a KinShield \u2014 tu an\u00e1lisis aparecer\u00e1 en esta p\u00e1gina.",
  "helpGuide.2.finalStep":
    "Una vez que env\u00edes el texto, vuelve aqu\u00ed y <strong>toca Actualizar</strong> \u2014 tu an\u00e1lisis de amenazas normalmente aparece en 60 segundos.",
  "helpGuide.2.actionLabel": "\u00a1Entendido!",
  "helpGuide.2.p0.name": "iPhone (iOS)",
  "helpGuide.2.p0.s0.instruction":
    "Abre la app <strong>Mensajes</strong> y encuentra el texto sospechoso.",
  "helpGuide.2.p0.s0.detail": "No toques ning\u00fan enlace en el mensaje.",
  "helpGuide.2.p0.s1.instruction":
    "<strong>Mant\u00e9n presionado</strong> el globo del mensaje, no ning\u00fan enlace que pueda haber en \u00e9l, luego toca <strong>M\u00e1s\u2026</strong>",
  "helpGuide.2.p0.s1.detail":
    "Ver\u00e1s aparecer casillas de verificaci\u00f3n junto a los mensajes. Aseg\u00farate de que el sospechoso est\u00e9 seleccionado.",
  "helpGuide.2.p0.s2.instruction":
    'Toca la <strong>flecha de reenviar</strong> <img src="/icons/ios-forward-arrow.svg" alt="Flecha de reenviar" style="width:1.2em;height:1.2em;vertical-align:middle;margin:0 0.15em;" /> en la parte inferior derecha.',
  "helpGuide.2.p0.s3.instruction":
    "En el campo <strong>\u201cPara\u201d</strong>, ingresa el n\u00famero de KinShield:",
  "helpGuide.2.p0.s3.detail":
    "El contenido del mensaje se reenviar\u00e1 autom\u00e1ticamente.",
  "helpGuide.2.p0.s4.instruction":
    "Toca <strong>Enviar</strong> (la flecha \u2191). Tu an\u00e1lisis aparecer\u00e1 en esta p\u00e1gina en breve.",
  "helpGuide.2.p0.s4.detail":
    "Generalmente listo en aproximadamente un minuto. Toca Actualizar en esta p\u00e1gina si no lo ves.",
  "helpGuide.2.p1.name": "Android",
  "helpGuide.2.p1.s0.instruction":
    "Abre tu app de <strong>Mensajes</strong> y encuentra el texto sospechoso.",
  "helpGuide.2.p1.s0.detail": "No toques ning\u00fan enlace en el mensaje.",
  "helpGuide.2.p1.s1.instruction":
    "<strong>Mant\u00e9n presionado</strong> el globo del mensaje hasta que est\u00e9 seleccionado.",
  "helpGuide.2.p1.s1.detail":
    "Puede aparecer un resaltado o marca de verificaci\u00f3n en el mensaje.",
  "helpGuide.2.p1.s2.instruction":
    "Toca el <strong>men\u00fa \u22ee</strong> (o el \u00edcono de compartir/reenviar) y elige <strong>\u201cReenviar\u201d</strong>.",
  "helpGuide.2.p1.s2.detail":
    "El texto exacto puede variar seg\u00fan la marca del tel\u00e9fono (Samsung, Pixel, etc.) pero la opci\u00f3n siempre est\u00e1 ah\u00ed.",
  "helpGuide.2.p1.s3.instruction":
    "En el campo <strong>\u201cPara\u201d</strong>, ingresa:",
  "helpGuide.2.p1.s4.instruction":
    "Toca <strong>Enviar</strong>, luego vuelve a esta p\u00e1gina.",
  "helpGuide.2.p1.s4.detail":
    "Tu an\u00e1lisis aparecer\u00e1 aqu\u00ed en aproximadamente un minuto. Toca Actualizar si es necesario.",

  // ── Help Guide 3 — An\u00e1lisis de conversaci\u00f3n de texto ─────────────────────
  "helpGuide.3.title": "C\u00f3mo analizar una conversaci\u00f3n",
  "helpGuide.3.subtitle":
    "Pega un hilo de conversaci\u00f3n para que KinShield pueda detectar manipulaci\u00f3n, fraude y patrones sospechosos.",
  "helpGuide.3.finalStep":
    "Pega tu conversaci\u00f3n en el \u00e1rea de texto de esta p\u00e1gina y toca <strong>Analizar</strong> para comenzar.",
  "helpGuide.3.actionLabel": "\u00a1Entendido!",
  "helpGuide.3.p0.name": "Todos los dispositivos",
  "helpGuide.3.p0.s0.instruction":
    "Ve a tu app de mensajer\u00eda y <strong>copia la conversaci\u00f3n</strong> que quieres analizar.",
  "helpGuide.3.p0.s0.detail":
    "Selecciona todos los mensajes del hilo \u2014 cuanto m\u00e1s contexto tenga KinShield, mejor ser\u00e1 el an\u00e1lisis. Incluye ambos lados de la conversaci\u00f3n si es posible.",
  "helpGuide.3.p0.s1.instruction":
    "<strong>Pega la conversaci\u00f3n</strong> en el \u00e1rea de texto de esta p\u00e1gina.",
  "helpGuide.3.p0.s1.detail":
    "Tambi\u00e9n puedes escribir o describir la conversaci\u00f3n si copiar y pegar no es posible. Incluye detalles como qui\u00e9n dijo qu\u00e9 y cu\u00e1ndo.",
  "helpGuide.3.p0.s2.instruction":
    "Toca <strong>\u201cAnalizar\u201d</strong> y espera un momento para ver tus resultados.",
  "helpGuide.3.p0.s2.detail":
    "KinShield buscar\u00e1 t\u00e1cticas de manipulaci\u00f3n, patrones de fraude, ingenier\u00eda social e indicadores de estafas rom\u00e1nticas.",

  // ── Help Guide 4 — Verificar publicaci\u00f3n o enlace en redes sociales ────────
  "helpGuide.4.title":
    "C\u00f3mo verificar una publicaci\u00f3n o enlace en redes sociales",
  "helpGuide.4.subtitle":
    "Copia el enlace de una publicaci\u00f3n y p\u00e9galo abajo, o toma una captura para anuncios sin enlaces.",
  "helpGuide.4.finalStep":
    "Pega el enlace en el campo URL de esta p\u00e1gina y toca <strong>Analizar</strong>. Si no puedes copiar un enlace, usa <strong>An\u00e1lisis de capturas</strong> en su lugar.",
  "helpGuide.4.actionLabel": "\u00a1Entendido!",
  "helpGuide.4.p0.name": "Copiar el enlace",
  "helpGuide.4.p0.s0.instruction":
    "Encuentra la publicaci\u00f3n sospechosa en Facebook, Instagram, X/Twitter, LinkedIn o cualquier plataforma social.",
  "helpGuide.4.p0.s0.detail":
    "No hagas clic en ning\u00fan enlace de la publicaci\u00f3n todav\u00eda.",
  "helpGuide.4.p0.s1.instruction":
    "Toca el <strong>bot\u00f3n de compartir</strong> o el <strong>men\u00fa \u22ef</strong> en la publicaci\u00f3n y elige <strong>\u201cCopiar enlace\u201d</strong>.",
  "helpGuide.4.p0.s1.detail":
    "En la mayor\u00eda de las plataformas: toca el \u00edcono de compartir o \u22ef \u2192 Copiar enlace. En X/Twitter: toca la flecha de compartir \u2192 Copiar enlace.",
  "helpGuide.4.p0.s2.instruction":
    "<strong>Pega el enlace</strong> en el campo URL de esta p\u00e1gina.",
  "helpGuide.4.p0.s3.instruction":
    "Toca <strong>\u201cAnalizar\u201d</strong> para ejecutar el an\u00e1lisis.",
  "helpGuide.4.p0.s3.detail":
    "KinShield examinar\u00e1 el destino de la URL, la reputaci\u00f3n del dominio y el contenido en busca de patrones de estafa conocidos.",
  "helpGuide.4.p1.name": "\u00bfAnuncio patrocinado?",
  "helpGuide.4.p1.s0.instruction":
    "Los anuncios patrocinados (marcados con <strong>\u201cPatrocinado\u201d</strong>) generalmente <strong>no tienen bot\u00f3n para compartir ni un enlace copiable</strong>.",
  "helpGuide.4.p1.s0.detail":
    "Al hacer clic en el anuncio se abren ventanas emergentes o formularios <em>dentro</em> de la app \u2014 la URL del navegador permanece como facebook.com o linkedin.com. No hay una URL real que copiar.",
  "helpGuide.4.p1.s1.instruction":
    "<strong>No rellenes ning\u00fan formulario ni hagas clic en \u201cDescargar\u201d / \u201cContinuar\u201d</strong> dentro del anuncio. As\u00ed es exactamente c\u00f3mo los anuncios fraudulentos roban tu informaci\u00f3n.",
  "helpGuide.4.p1.s1.detail":
    "Las empresas leg\u00edtimas no necesitan tu informaci\u00f3n personal para dejarte leer un informe. Si pide correo laboral, nombre y cargo antes de mostrar contenido \u2014 ten cuidado.",
  "helpGuide.4.p1.s2.instruction":
    "<strong>Toma capturas de pantalla</strong> del anuncio y de cualquier ventana emergente que muestre, luego usa la herramienta de <strong>An\u00e1lisis de capturas</strong> de KinShield para verificarlas.",
  "helpGuide.4.p1.s3.instruction":
    "<strong>Verifica la empresa de forma independiente:</strong> Toca el nombre de la empresa en el anuncio para comprobar si es una p\u00e1gina verificada, luego busca la empresa y la oferta en Google \u2014 no a trav\u00e9s del anuncio.",
  "helpGuide.4.p1.s3.detail":
    "Si el informe u oferta es real, lo encontrar\u00e1s en el sitio web oficial de la empresa sin dar tu informaci\u00f3n a un formulario de anuncio.",
  "helpGuide.4.p1.finalStep":
    "Toma capturas del anuncio y de cualquier formulario que muestre, luego usa <strong>An\u00e1lisis de capturas</strong> para verificarlos.",
  "helpGuide.4.p1.actionLabel": "Abrir An\u00e1lisis de capturas",
  "helpGuide.4.p2.name": "Captura en su lugar",
  "helpGuide.4.p2.s0.instruction":
    "\u00bfNo puedes copiar un enlace? No hay problema \u2014 toma una captura de pantalla y usa la herramienta de <strong>An\u00e1lisis de capturas</strong> de KinShield en su lugar.",
  "helpGuide.4.p2.s0.detail":
    "El An\u00e1lisis de capturas usa IA para detectar indicadores de estafa en im\u00e1genes \u2014 perfecto para anuncios, ventanas emergentes o publicaciones de las que no puedes copiar un enlace.",
  "helpGuide.4.p2.finalStep":
    "Toma una captura del contenido sospechoso, luego abre <strong>An\u00e1lisis de capturas</strong> para subirla y verificarla.",
  "helpGuide.4.p2.actionLabel": "Abrir An\u00e1lisis de capturas",

  // ── Help Guide 5 — B\u00fasqueda de n\u00famero de tel\u00e9fono ────────────────────────────
  "helpGuide.5.title": "C\u00f3mo buscar un n\u00famero de tel\u00e9fono",
  "helpGuide.5.subtitle":
    "Ingresa cualquier n\u00famero de tel\u00e9fono para ver si est\u00e1 asociado con estafas conocidas.",
  "helpGuide.5.finalStep":
    "Ingresa el n\u00famero de tel\u00e9fono en el campo de esta p\u00e1gina y toca <strong>Verificar n\u00famero</strong> para ver los resultados.",
  "helpGuide.5.actionLabel": "\u00a1Entendido!",
  "helpGuide.5.p0.name": "Todos los dispositivos",
  "helpGuide.5.p0.s0.instruction":
    "Ingresa el <strong>n\u00famero de tel\u00e9fono</strong> en el campo de entrada de esta p\u00e1gina.",
  "helpGuide.5.p0.s0.detail":
    "Incluye el c\u00f3digo de pa\u00eds si es un n\u00famero internacional (p. ej., +1 para EE. UU./Canad\u00e1). El n\u00famero puede provenir de una llamada perdida, texto, correo de voz o cualquier fuente.",
  "helpGuide.5.p0.s1.instruction":
    "Toca <strong>\u201cVerificar n\u00famero\u201d</strong>.",
  "helpGuide.5.p0.s1.detail":
    "KinShield buscar\u00e1 en bases de datos de estafas y proporcionar\u00e1 una evaluaci\u00f3n de riesgo para el n\u00famero.",
  "helpGuide.5.p0.s2.instruction":
    "Revisa los resultados \u2014 KinShield mostrar\u00e1 el <strong>nivel de riesgo</strong>, <strong>informaci\u00f3n del operador</strong> y cualquier <strong>actividad de estafa reportada</strong>.",
  "helpGuide.5.p0.s2.detail":
    "Si el n\u00famero est\u00e1 marcado, ver\u00e1s detalles sobre el tipo de estafa y cu\u00e1ntas personas lo han reportado.",

  // ── Help Guide 6 — An\u00e1lisis de captura de pantalla / imagen ────────────────
  "helpGuide.6.title": "C\u00f3mo analizar una captura de pantalla o imagen",
  "helpGuide.6.subtitle":
    "Sube una imagen de un mensaje sospechoso, factura, ventana emergente o c\u00f3digo QR y KinShield la escanear\u00e1.",
  "helpGuide.6.finalStep":
    "Sube tu imagen usando el bot\u00f3n <strong>Subir</strong> en esta p\u00e1gina, luego toca <strong>Analizar</strong> para obtener tus resultados.",
  "helpGuide.6.actionLabel": "\u00a1Entendido!",
  "helpGuide.6.p0.name": "iPhone / iPad",
  "helpGuide.6.p0.s0.instruction":
    "Toca el \u00e1rea <strong>\u201cTomar foto o elegir imagen\u201d</strong> en esta p\u00e1gina. Tu dispositivo ofrecer\u00e1 tres opciones:",
  "helpGuide.6.p0.s0.detail":
    "<strong>Tomar captura</strong> \u2014 captura tu pantalla actual.<br/><strong>Tomar foto</strong> \u2014 abre tu c\u00e1mara para fotografiar una carta, factura o cualquier cosa en otra pantalla.<br/><strong>Elegir de Fotos</strong> \u2014 elige una imagen ya guardada en tu biblioteca.",
  "helpGuide.6.p0.s1.instruction":
    "Selecciona o captura el contenido sospechoso usando el m\u00e9todo que mejor funcione para tu situaci\u00f3n.",
  "helpGuide.6.p0.s1.detail":
    "Formatos admitidos: PNG, JPG, HEIC, GIF, WebP, BMP, TIFF, SVG. Tama\u00f1o m\u00e1ximo 10 MB.",
  "helpGuide.6.p0.s2.instruction":
    "Opcionalmente agrega <strong>Contexto</strong> para describir la situaci\u00f3n, luego toca <strong>\u201cAnalizar\u201d</strong>.",
  "helpGuide.6.p0.s2.detail":
    "La IA de KinShield examinar\u00e1 la imagen en busca de facturas falsas, estafas de soporte t\u00e9cnico, fraude con c\u00f3digos QR y m\u00e1s.",
  "helpGuide.6.p1.name": "Android",
  "helpGuide.6.p1.s0.instruction":
    "Toca el \u00e1rea <strong>\u201cTomar foto o elegir imagen\u201d</strong> en esta p\u00e1gina. Tu dispositivo ofrecer\u00e1 tres opciones:",
  "helpGuide.6.p1.s0.detail":
    "<strong>Tomar captura</strong> \u2014 captura tu pantalla actual.<br/><strong>Tomar foto</strong> \u2014 abre tu c\u00e1mara para fotografiar un documento, carta u otra pantalla.<br/><strong>Elegir de Galer\u00eda/Archivos</strong> \u2014 selecciona una imagen ya guardada en tu dispositivo.",
  "helpGuide.6.p1.s1.instruction":
    "Selecciona o captura el contenido sospechoso usando el m\u00e9todo que mejor funcione para tu situaci\u00f3n.",
  "helpGuide.6.p1.s1.detail":
    "Formatos admitidos: PNG, JPG, GIF, WebP, BMP, TIFF, SVG. Tama\u00f1o m\u00e1ximo 10 MB.",
  "helpGuide.6.p1.s2.instruction":
    "Opcionalmente agrega <strong>Contexto</strong> para describir la situaci\u00f3n, luego toca <strong>\u201cAnalizar\u201d</strong>.",
  "helpGuide.6.p1.s2.detail":
    "El an\u00e1lisis de IA generalmente toma 10\u201330 segundos seg\u00fan la imagen.",
  "helpGuide.6.p2.name": "Computadora",
  "helpGuide.6.p2.s0.instruction":
    "Sigue las instrucciones de esta p\u00e1gina \u2014 puedes <strong>tomar una captura</strong> o <strong>subir un archivo de imagen existente</strong> desde tu computadora.",
  "helpGuide.6.p2.s0.detail":
    "La p\u00e1gina te guiar\u00e1 para subir y analizar la imagen. Formatos admitidos: PNG, JPG. Tama\u00f1o m\u00e1ximo 10 MB.",

  // ── Help Guide 7 — Verificar negocio o persona ───────────────────────────
  "helpGuide.7.title": "C\u00f3mo evaluar un negocio o persona",
  "helpGuide.7.subtitle":
    "Ingresa detalles sobre alguien con quien est\u00e1s interactuando para obtener una evaluaci\u00f3n de riesgo.",
  "helpGuide.7.finalStep":
    "Ingresa los detalles en el formulario de esta p\u00e1gina y toca <strong>Analizar</strong> para obtener tu evaluaci\u00f3n de riesgo.",
  "helpGuide.7.actionLabel": "\u00a1Entendido!",
  "helpGuide.7.p0.name": "Todos los dispositivos",
  "helpGuide.7.p0.s0.instruction":
    "En el formulario de esta p\u00e1gina, ingresa lo que sabes: un <strong>nombre de negocio</strong>, <strong>sitio web</strong>, <strong>nombre de persona</strong> o una descripci\u00f3n de la interacci\u00f3n.",
  "helpGuide.7.p0.s0.detail":
    "Se requiere al menos <strong>un</strong> campo en la secci\u00f3n de Informaci\u00f3n del Negocio \u2014 ya sea un <strong>Nombre de Negocio/Persona</strong> o un <strong>Sitio Web/URL</strong> (o ambos). Cuantos m\u00e1s detalles proporciones, mejor ser\u00e1 la evaluaci\u00f3n. Incluye cosas como: qu\u00e9 ofrecen, c\u00f3mo te contactaron, cualquier t\u00e1ctica de presi\u00f3n, etc.",
  "helpGuide.7.p0.s1.instruction":
    "Toca <strong>\u201cAnalizar\u201d</strong> para ejecutar la evaluaci\u00f3n.",
  "helpGuide.7.p0.s1.detail":
    "KinShield verifica se\u00f1ales de alerta como negocios falsos, patrones de suplantaci\u00f3n y esquemas de fraude conocidos.",
  "helpGuide.7.p0.s2.instruction":
    "Revisa los resultados \u2014 KinShield mostrar\u00e1 el <strong>nivel de riesgo</strong> y los <strong>indicadores de alerta espec\u00edficos</strong> encontrados.",
  "helpGuide.7.p0.s2.detail":
    "Tambi\u00e9n puedes consultar tus an\u00e1lisis anteriores en el panel de Historial a la derecha (o la pesta\u00f1a Reciente en m\u00f3vil).",

  // ── Help Guide 8 — Estado de simulaci\u00f3n y controles de pausa ─────────────
  "helpGuide.8.title": "Estado de simulaci\u00f3n y controles de pausa",
  "helpGuide.8.subtitle":
    "Pausa o reanuda tus ejercicios de simulaci\u00f3n de phishing en cualquier momento.",
  "helpGuide.8.finalStep":
    "Usa la secci\u00f3n de <strong>Estado de simulaci\u00f3n</strong> para pausar o reanudar tus ejercicios de entrenamiento cuando necesites un descanso.",
  "helpGuide.8.actionLabel": "\u00a1Entendido!",
  "helpGuide.8.p0.name": "Todos los dispositivos",
  "helpGuide.8.p0.s0.instruction":
    "Busca la secci\u00f3n de <strong>Estado de simulaci\u00f3n</strong> en la parte superior de esta p\u00e1gina.",
  "helpGuide.8.p0.s0.detail":
    "Muestra si tus simulaciones est\u00e1n actualmente activas o en pausa, junto con cualquier detalle del programa de pausa.",
  "helpGuide.8.p0.s1.instruction":
    "Toca <strong>\u201cPausar simulaciones\u201d</strong> para dejar de recibir ejercicios de phishing temporalmente.",
  "helpGuide.8.p0.s1.detail":
    "Puedes pausar indefinidamente, por un n\u00famero determinado de d\u00edas, o hasta una fecha espec\u00edfica.",
  "helpGuide.8.p0.s2.instruction":
    "Toca <strong>\u201cReanudar simulaciones\u201d</strong> para comenzar a recibir ejercicios nuevamente.",
  "helpGuide.8.p0.s2.detail":
    "La reanudaci\u00f3n tiene efecto inmediato. La configuraci\u00f3n de tus simulaciones no cambiar\u00e1.",

  // ── Help Guide 11 — An\u00e1lisis manual de correo ───────────────────────────
  "helpGuide.11.title": "C\u00f3mo analizar manualmente un correo",
  "helpGuide.11.subtitle":
    "Copia los detalles clave de un correo sospechoso y p\u00e9galos en el formulario de esta p\u00e1gina.",
  "helpGuide.11.finalStep":
    "Copia la direcci\u00f3n del remitente, el asunto y el cuerpo del correo sospechoso en los campos de esta p\u00e1gina, luego toca <strong>Analizar correo</strong>.",
  "helpGuide.11.actionLabel": "\u00a1Entendido!",
  "helpGuide.11.p0.name": "Todos los dispositivos",
  "helpGuide.11.p0.s0.instruction":
    "Abre el correo sospechoso en tu app de correo. <strong>No hagas clic en ning\u00fan enlace</strong> dentro de \u00e9l.",
  "helpGuide.11.p0.s0.detail":
    "Necesitar\u00e1s copiar tres cosas: la direcci\u00f3n de correo del remitente, el asunto y el cuerpo del correo.",
  "helpGuide.11.p0.s1.instruction":
    "Encuentra la <strong>direcci\u00f3n de correo del remitente</strong> (normalmente se muestra cerca de la parte superior del correo) y c\u00f3piala.",
  "helpGuide.11.p0.s1.detail":
    'En m\u00f3vil, toca el nombre del remitente para revelar la direcci\u00f3n completa. Puede decir algo como "Juan Garc\u00eda &lt;fake@scamdomain.com&gt;" \u2014 quieres la parte dentro de los corchetes angulares.',
  "helpGuide.11.p0.s2.instruction":
    "Pega la direcci\u00f3n de correo del remitente en el campo <strong>\u201cDirecci\u00f3n de correo del remitente\u201d</strong> de esta p\u00e1gina.",
  "helpGuide.11.p0.s3.instruction":
    "Copia el <strong>asunto</strong> del correo y p\u00e9galo en el campo <strong>\u201cAsunto del correo\u201d</strong>.",
  "helpGuide.11.p0.s3.detail":
    "El asunto generalmente se muestra en la parte superior del correo en texto en negrita.",
  "helpGuide.11.p0.s4.instruction":
    "Selecciona y copia el <strong>cuerpo completo del correo</strong> (el contenido del mensaje principal), luego p\u00e9galo en el campo <strong>\u201cCuerpo del correo\u201d</strong>.",
  "helpGuide.11.p0.s4.detail":
    "Incluye todo \u2014 saludos, enlaces, n\u00fameros de tel\u00e9fono, firmas. Cuanto m\u00e1s contenido tenga KinShield, mejor ser\u00e1 el an\u00e1lisis.",
  "helpGuide.11.p0.s5.instruction":
    "Toca <strong>\u201cAnalizar correo\u201d</strong> y espera un momento para ver tus resultados.",
  "helpGuide.11.p0.s5.detail":
    "La IA de KinShield examinar\u00e1 el remitente, el asunto y el cuerpo en busca de indicadores de phishing, patrones de estafa y t\u00e1cticas de ingenier\u00eda social.",

  // ── Help Guide 12 — An\u00e1lisis manual de texto/SMS ─────────────────────────
  "helpGuide.12.title": "C\u00f3mo analizar manualmente un mensaje de texto",
  "helpGuide.12.subtitle":
    "Ingresa el n\u00famero de tel\u00e9fono del remitente y pega el contenido del mensaje en el formulario de esta p\u00e1gina.",
  "helpGuide.12.finalStep":
    "Ingresa el n\u00famero de tel\u00e9fono (o c\u00f3digo corto) y pega el mensaje en los campos de esta p\u00e1gina, luego toca <strong>Analizar contenido</strong>.",
  "helpGuide.12.actionLabel": "\u00a1Entendido!",
  "helpGuide.12.p0.name": "Todos los dispositivos",
  "helpGuide.12.p0.s0.instruction":
    "Abre el mensaje de texto sospechoso en tu app de mensajer\u00eda. <strong>No toques ning\u00fan enlace</strong> en el mensaje.",
  "helpGuide.12.p0.s1.instruction":
    "Encuentra el <strong>n\u00famero de tel\u00e9fono del remitente</strong> o el <strong>c\u00f3digo corto SMS</strong> (mostrado en la parte superior de la conversaci\u00f3n).",
  "helpGuide.12.p0.s1.detail":
    "Si el remitente muestra un nombre en lugar de un n\u00famero, t\u00f3calo para revelar el n\u00famero real. Los c\u00f3digos cortos son t\u00edpicamente n\u00fameros de 5\u20136 d\u00edgitos como 33513.",
  "helpGuide.12.p0.s2.instruction":
    "Ingresa el n\u00famero de tel\u00e9fono en el campo <strong>\u201cN\u00famero de tel\u00e9fono\u201d</strong> de esta p\u00e1gina \u2014 o si es un c\u00f3digo corto, usa el campo <strong>\u201cC\u00f3digo corto SMS\u201d</strong>.",
  "helpGuide.12.p0.s2.detail":
    "Incluye el c\u00f3digo de pa\u00eds para n\u00fameros de tel\u00e9fono (p. ej., +1 para EE. UU./Canad\u00e1). Solo necesitas completar uno \u2014 n\u00famero de tel\u00e9fono O c\u00f3digo corto.",
  "helpGuide.12.p0.s3.instruction":
    "Vuelve al mensaje, <strong>copia el texto del mensaje</strong>, y p\u00e9galo en el campo <strong>\u201cContenido del mensaje\u201d</strong>.",
  "helpGuide.12.p0.s3.detail":
    "Mant\u00e9n presionado el globo del mensaje para seleccionarlo y copiarlo. Incluye el mensaje completo \u2014 enlaces, n\u00fameros de tel\u00e9fono y todo.",
  "helpGuide.12.p0.s4.instruction":
    "Toca <strong>\u201cAnalizar contenido\u201d</strong> y espera un momento para ver tus resultados.",
  "helpGuide.12.p0.s4.detail":
    "KinShield verificar\u00e1 el n\u00famero de tel\u00e9fono contra bases de datos de estafas y analizar\u00e1 el texto del mensaje en busca de patrones de fraude.",

  // ── Help Guide 13 — Verificar identidad de quien llama ──────────────────
  "helpGuide.13.title": "C\u00f3mo verificar la identidad de quien llama",
  "helpGuide.13.subtitle":
    "Nunca conf\u00edes en lo que te dice quien llama \u2014 siempre verifica a trav\u00e9s de una fuente independiente en la que conf\u00edes.",
  "helpGuide.13.finalStep":
    "Siempre <strong>cuelga</strong> y encuentra el n\u00famero real t\u00fa mismo a trav\u00e9s de una fuente independiente y confiable.",
  "helpGuide.13.actionLabel": "\u00a1Entendido!",
  "helpGuide.13.p0.name": "Todos los dispositivos",
  "helpGuide.13.p0.s0.instruction":
    "Busca el <strong>sitio web oficial</strong> de la organizaci\u00f3n y llama al n\u00famero que aparece all\u00ed.",
  "helpGuide.13.p0.s0.detail":
    "Escribe el nombre de la empresa o agencia directamente en tu navegador \u2014 no hagas clic en enlaces de correos, textos o ventanas emergentes. Ten cuidado con los primeros resultados de b\u00fasqueda, ya que los estafadores pueden pagar por <strong>anuncios patrocinados</strong> que imitan sitios oficiales. Busca resultados org\u00e1nicos no patrocinados.",
  "helpGuide.13.p0.s1.instruction":
    "Usa el n\u00famero de tel\u00e9fono impreso en el <strong>reverso de tu tarjeta de cr\u00e9dito o d\u00e9bito</strong>.",
  "helpGuide.13.p0.s1.detail":
    "Esta es la forma m\u00e1s segura de contactar a tu banco o emisor de tarjeta. Los estafadores no pueden cambiar lo que est\u00e1 impreso f\u00edsicamente en tu tarjeta.",
  "helpGuide.13.p0.s2.instruction":
    "Busca el n\u00famero en <strong>documentos f\u00edsicos oficiales</strong> \u2014 facturas, estados de cuenta, cartas o tarjetas de seguro.",
  "helpGuide.13.p0.s2.detail":
    "Las agencias gubernamentales y empresas imprimen sus n\u00fameros de contacto reales en la correspondencia que te env\u00edan por correo.",
  "helpGuide.13.p0.s3.instruction":
    "<strong>Nunca</strong> conf\u00edes en el n\u00famero de tel\u00e9fono que aparece en tu pantalla desde una ventana emergente, mensaje de texto o correo.",
  "helpGuide.13.p0.s3.detail":
    "Los estafadores pueden falsificar el identificador de llamadas y crear ventanas emergentes falsas que muestran n\u00fameros de soporte fraudulentos.",
  "helpGuide.13.p0.s4.instruction":
    "<strong>Nunca</strong> uses un n\u00famero de tel\u00e9fono que te d\u00e9 quien llama \u2014 pueden redirigirte directamente a ellos mismos.",
  "helpGuide.13.p0.s4.detail":
    "Si alguien te pide que lo llames al n\u00famero que te proporciona, cuelga y encuentra el n\u00famero real t\u00fa mismo.",

  // ── Help Guide 14 — Configurar ajustes de simulaci\u00f3n ─────────────────────
  "helpGuide.14.title": "C\u00f3mo configurar tus ajustes de simulaci\u00f3n",
  "helpGuide.14.subtitle":
    "Elige un perfil preestablecido o crea una configuraci\u00f3n personalizada para alcanzar tus objetivos de entrenamiento.",
  "helpGuide.14.finalStep":
    "Elige un perfil preestablecido de las tarjetas, o selecciona <strong>Personalizado</strong> para ajustar cada par\u00e1metro. \u00a1No olvides guardar tus cambios!",
  "helpGuide.14.actionLabel": "\u00a1Entendido!",
  "helpGuide.14.p0.name": "Todos los dispositivos",
  "helpGuide.14.p0.s0.instruction":
    "Comienza seleccionando un <strong>perfil preestablecido</strong> \u2014 Suave, Moderado o Desafiante.",
  "helpGuide.14.p0.s0.detail":
    "Cada perfil configura autom\u00e1ticamente la dificultad, frecuencia, canales, tiempo y contexto. Toca una tarjeta para seleccionarlo.",
  "helpGuide.14.p0.s1.instruction":
    "Revisa tu configuraci\u00f3n en el panel de <strong>Ajustes de simulaci\u00f3n</strong>.",
  "helpGuide.14.p0.s1.detail":
    "Despu\u00e9s de seleccionar un perfil, ver\u00e1s todos los par\u00e1metros configurados como insignias \u2014 nivel de dificultad, frecuencia, canales de comunicaci\u00f3n, tiempo y contexto.",
  "helpGuide.14.p0.s2.instruction":
    "Selecciona <strong>Personalizado</strong> para configurar manualmente cada par\u00e1metro.",
  "helpGuide.14.p0.s2.detail":
    "En modo Personalizado, toca cualquier insignia para alternar canales, cambiar tiempo, ajustar niveles de dificultad o frecuencia, y seleccionar categor\u00edas de contexto. Las opciones no seleccionadas aparecen con bordes punteados. Requiere una suscripci\u00f3n de Familia Extendida.",
  "helpGuide.14.p0.s3.instruction":
    "Toca <strong>\u201cUsar perfil [Preestablecido]\u201d</strong> o <strong>\u201cUsar configuraci\u00f3n personalizada\u201d</strong> para guardar.",
  "helpGuide.14.p0.s3.detail":
    "Los cambios no tendr\u00e1n efecto hasta que guardes. La insignia Activo aparece junto al nombre del perfil cuando tu vista actual coincide con la configuraci\u00f3n guardada.",

  // ── Help Guide 15 — Gestionar usuario miembro ────────────────────────────
  "helpGuide.15.title": "C\u00f3mo gestionar un usuario miembro",
  "helpGuide.15.subtitle":
    "Selecciona un usuario de la lista y usa el men\u00fa de navegaci\u00f3n para gestionar su cuenta.",
  "helpGuide.15.finalStep":
    "Comienza seleccionando una tarjeta de usuario abajo, luego usa el men\u00fa de navegaci\u00f3n a la izquierda para gestionar su cuenta.",
  "helpGuide.15.actionLabel": "\u00a1Entendido!",
  "helpGuide.15.p0.name": "Todos los dispositivos",
  "helpGuide.15.p0.s0.instruction":
    "Haz clic en una <strong>tarjeta de usuario</strong> en la lista de abajo para seleccionarlo.",
  "helpGuide.15.p0.s0.detail":
    "Aparecer\u00e1 una casilla de verificaci\u00f3n en la tarjeta seleccionada. Solo puedes gestionar un usuario a la vez.",
  "helpGuide.15.p0.s1.instruction":
    "Navega a <strong>Actualizar informaci\u00f3n del usuario</strong> en el men\u00fa lateral para editar su informaci\u00f3n b\u00e1sica.",
  "helpGuide.15.p0.s1.detail":
    "Aqu\u00ed puedes cambiar el nombre, la direcci\u00f3n de correo o el n\u00famero de tel\u00e9fono del usuario. Los cambios de correo activar\u00e1n un correo de verificaci\u00f3n.",
  "helpGuide.15.p0.s2.instruction":
    "Ve a <strong>Agregar detalles adicionales</strong> para gestionar informaci\u00f3n extra del perfil.",
  "helpGuide.15.p0.s2.detail":
    "Los detalles adicionales como ubicaci\u00f3n, edad e intereses se usan para personalizar la experiencia de simulaci\u00f3n del usuario.",
  "helpGuide.15.p0.s3.instruction":
    "Visita <strong>Ajustes de perfil de simulaci\u00f3n</strong> (bajo la secci\u00f3n Simulaciones en el men\u00fa principal) para configurar su perfil de entrenamiento.",
  "helpGuide.15.p0.s3.detail":
    "All\u00ed puedes establecer el nivel de sofisticaci\u00f3n, canales de comunicaci\u00f3n, frecuencia de contacto y contexto de escenarios para el usuario seleccionado.",

  // ── Contact Support page ───────────────────────────────────────────
  "contactSupport.title": "Contactar soporte",
  "contactSupport.subtitle":
    "\u00a1Estamos aqu\u00ed para ayudar! Elige la mejor manera de comunicarte con nosotros o env\u00eda un ticket de soporte a continuaci\u00f3n.",

  // Contact method cards
  "contactSupport.email.title": "Soporte por correo",
  "contactSupport.email.info": "support@kinprotect.com",
  "contactSupport.email.description":
    "Esr\u00edbenos en cualquier momento. Normalmente respondemos en 24 horas.",
  "contactSupport.email.button": "Enviar correo",

  "contactSupport.phone.title": "Soporte telef\u00f3nico",
  "contactSupport.phone.info": "1-301-237-7843",
  "contactSupport.phone.description":
    "Habla directamente con nuestro equipo de soporte durante el horario laboral.",
  "contactSupport.phone.button": "Llamar ahora",

  "contactSupport.chat.title": "Chat en vivo",
  "contactSupport.chat.info": "Disponible durante el horario laboral",
  "contactSupport.chat.description":
    "Obt\u00e9n respuestas instant\u00e1neas a tus preguntas con nuestro chat en vivo.",

  "contactSupport.helpCenter.title": "Centro de ayuda",
  "contactSupport.helpCenter.info": "Recursos de autoservicio",
  "contactSupport.helpCenter.description":
    "Explora nuestra base de conocimientos y preguntas frecuentes para obtener respuestas r\u00e1pidas.",

  // Support hours
  "contactSupport.hours.title": "Horario de soporte",
  "contactSupport.hours.monFri": "Lunes a viernes:",
  "contactSupport.hours.monFriTime": "8:00 a.\u00a0m. - 8:00 p.\u00a0m. EST",
  "contactSupport.hours.saturday": "S\u00e1bado:",
  "contactSupport.hours.saturdayTime": "10:00 a.\u00a0m. - 6:00 p.\u00a0m. EST",
  "contactSupport.hours.sunday": "Domingo:",
  "contactSupport.hours.sundayClosed": "Cerrado",
  "contactSupport.hours.note":
    "El soporte por correo est\u00e1 disponible las 24 horas, los 7 d\u00edas de la semana, con respuestas en un plazo de 24 horas",

  // Support ticket form
  "contactSupport.ticket.title": "Enviar un ticket de soporte",
  "contactSupport.ticket.name": "Nombre",
  "contactSupport.ticket.email": "Correo electr\u00f3nico",
  "contactSupport.ticket.priority": "Prioridad",
  "contactSupport.ticket.priority.low": "Baja",
  "contactSupport.ticket.priority.medium": "Media",
  "contactSupport.ticket.priority.high": "Alta",
  "contactSupport.ticket.priority.critical": "Cr\u00edtica",
  "contactSupport.ticket.message": "Mensaje",
  "contactSupport.ticket.namePlaceholder": "Tu nombre",
  "contactSupport.ticket.emailPlaceholder": "tu@correo.com",
  "contactSupport.ticket.messagePlaceholder": "Describe tu problema...",
  "contactSupport.ticket.submit": "Enviar",

  // Validation messages
  "contactSupport.validation.nameRequired": "Por favor, ingresa tu nombre.",
  "contactSupport.validation.nameTooLong":
    "El nombre no debe superar los 100 caracteres.",
  "contactSupport.validation.emailRequired":
    "Por favor, ingresa tu direcci\u00f3n de correo electr\u00f3nico.",
  "contactSupport.validation.emailInvalid":
    "Por favor, ingresa una direcci\u00f3n de correo electr\u00f3nico v\u00e1lida.",
  "contactSupport.validation.messageRequired": "Por favor, ingresa tu mensaje.",
  "contactSupport.validation.messageTooLong":
    "El mensaje no debe superar los 5000 caracteres.",

  // Status messages
  "contactSupport.success.message":
    "\u00a1Gracias por contactarnos! Hemos recibido tu mensaje y responderemos en un plazo de 24 horas.",
  "contactSupport.error.submit":
    "Ocurri\u00f3 un error al enviar tu solicitud. Por favor, int\u00e9ntalo de nuevo o esr\u00edbenos directamente a support@kinprotect.com",

  // Additional resources
  "contactSupport.resources.title": "Antes de contactarnos",
  "contactSupport.resources.account.title": "Verifica el estado de tu cuenta",
  "contactSupport.resources.account.desc":
    "Muchos problemas comunes se pueden resolver revisando la configuraci\u00f3n de tu cuenta y el estado de la suscripci\u00f3n.",
  "contactSupport.resources.alerts.title": "Revisa las alertas recientes",
  "contactSupport.resources.alerts.desc":
    "Si experimentas problemas con el servicio, verifica si hay alertas generales del sistema o avisos de mantenimiento.",
  "contactSupport.resources.troubleshoot.title":
    "Prueba soluciones b\u00e1sicas",
  "contactSupport.resources.troubleshoot.desc":
    "Limpia la cach\u00e9 del navegador, prueba con otro navegador o verifica tu conexi\u00f3n a internet antes de contactar el soporte.",
  "contactSupport.resources.prepare.title":
    "Prepara la informaci\u00f3n de tu cuenta",
  "contactSupport.resources.prepare.desc":
    "Ten a mano el correo de tu cuenta y cualquier mensaje de error relevante para que podamos ayudarte m\u00e1s r\u00e1pido.",

  // ── Contact Us Modal (ContactUsModal.js) ─────────────────────────────
  "contactUs.toast.nameRequired": "Por favor ingresa tu nombre",
  "contactUs.toast.nameTooLong": "El nombre no debe superar los 100 caracteres",
  "contactUs.toast.emailRequired":
    "Por favor ingresa tu correo electr\u00f3nico",
  "contactUs.toast.emailInvalid":
    "Por favor ingresa una direcci\u00f3n de correo v\u00e1lida",
  "contactUs.toast.emailTooLong":
    "El correo no debe superar los 255 caracteres",
  "contactUs.toast.subjectRequired": "Por favor ingresa un asunto",
  "contactUs.toast.subjectTooLong":
    "El asunto no debe superar los 200 caracteres",
  "contactUs.toast.messageRequired": "Por favor ingresa tu mensaje",
  "contactUs.toast.messageTooLong":
    "El mensaje no debe superar los 5000 caracteres",
  "contactUs.toast.success":
    "\u00a1Gracias por contactarnos! Te responderemos pronto.",

  // ── Side Panel (SidePanel.js) ─────────────────────────────────────────
  "sidePanel.toast.verificationEmailFailed":
    "Error al enviar el correo de verificaci\u00f3n. Por favor intenta de nuevo.",
  "sidePanel.toast.adminDashboardFailed":
    "Error al acceder al Panel de Administraci\u00f3n. Por favor intenta de nuevo.",
  "sidePanel.toast.generateBlogsFailed":
    "Error al acceder a Generar Blogs. Por favor intenta de nuevo.",

  // ── Shield Reports (ShieldReports.js, ShieldReportDetail.js) ─────────
  "shieldReports.toast.reportGenerated":
    "\u00a1Nuevo reporte generado exitosamente!",
  "shieldReportDetail.toast.emailSent":
    "\u00a1Correo del reporte enviado exitosamente!",
  "shieldReportDetail.toast.emailFailed":
    "Error al enviar el correo. Por favor intenta de nuevo.",

  // ── Image Analysis (ImageAnalysisSimple.js) ───────────────────────────
  "imageAnalysis.toast.uploadSuccess": "\u00a1Carga exitosa!",
  "imageAnalysis.toast.checksComplete":
    "\u00a1Verificaciones de seguridad completadas!",
  "imageAnalysis.toast.historyRefreshed":
    "\u00a1Historial de an\u00e1lisis actualizado!",
  "imageAnalysis.toast.refreshFailed": "Error al actualizar: {error}",

  // ── URL Safety Analysis (URLSafetyAnalysis.js) ────────────────────────
  "urlSafety.toast.enterUrl": "Por favor ingresa una URL para analizar",
  "urlSafety.toast.loginRequired":
    "Debes iniciar sesi\u00f3n para analizar URLs",
  "urlSafety.toast.safe": "La URL parece segura",
  "urlSafety.toast.dangerous": "Advertencia: Esta URL puede ser peligrosa",
  "urlSafety.toast.failed": "Error al analizar la URL",

  // ── Simulation Heuristics (SimulationHeuristics.js) ───────────────────
  "simulation.toast.emailSent": "Mensaje enviado exitosamente a {email}",
  "simulation.toast.emailFailed": "Error al enviar el mensaje: {error}",
  "simulation.toast.smsSent": "SMS enviado exitosamente a {phone}",
  "simulation.toast.smsFailed": "Error al enviar el SMS: {error}",
  "simulation.toast.error": "Error: {error}",

  // ── Simulation Profile (useSimulationProfileState.js) ─────────────────
  "simProfile.toast.profileSaved":
    "\u00a1Perfil de simulaci\u00f3n guardado exitosamente!",
  "simProfile.toast.profileSaveFailed":
    "Error al guardar el perfil de simulaci\u00f3n.",
  "simProfile.toast.profileSaveError":
    "Ocurri\u00f3 un error al guardar el perfil de simulaci\u00f3n.",
  "simProfile.toast.noUserSelected": "Ning\u00fan usuario seleccionado",
  "simProfile.toast.scheduleResumed":
    "Programa de simulaci\u00f3n reanudado exitosamente",
  "simProfile.toast.resumeFailed":
    "Error al reanudar el programa. Por favor intenta de nuevo.",
  "simProfile.toast.schedulePaused":
    "Programa de simulaci\u00f3n pausado exitosamente",
  "simProfile.toast.pauseFailed":
    "Error al pausar el programa. Por favor intenta de nuevo.",

  // ── Admin: Scam Reports (AdminScamReports.js) ─────────────────────────
  "adminScamReports.toast.loadFailed": "Error al cargar los reportes de estafa",
  "adminScamReports.toast.deleted": "Reporte #{id} eliminado",
  "adminScamReports.toast.deleteFailed": "Error al eliminar el reporte",

  // ── Admin: User Sessions (UserSessionsAdmin.js) ───────────────────────
  "userSessions.toast.sessionLoadFailed":
    "Error al cargar los datos de sesi\u00f3n",
  "userSessions.toast.activeUsersLoadFailed":
    "Error al cargar los usuarios activos",

  // ── Admin: System Errors (SystemErrors.js) ────────────────────────────
  "systemErrors.toast.summaryLoadFailed":
    "Error al cargar el resumen de errores",
  "systemErrors.toast.listLoadFailed": "Error al cargar la lista de errores",
  "systemErrors.toast.dashboardRefreshed": "Panel actualizado",
  "systemErrors.toast.detailLoadFailed":
    "Error al cargar los detalles del error",
  "systemErrors.toast.acknowledged": "Error reconocido",
  "systemErrors.toast.acknowledgeFailed": "Error al reconocer el error",
  "systemErrors.toast.resolved": "Error resuelto",
  "systemErrors.toast.resolveFailed": "Error al resolver el error",

  // ── Admin: Promotions (PromotionsAdmin.js) ────────────────────────────
  "promotionsAdmin.toast.synced":
    "Se sincronizaron {total} elementos desde Stripe",

  // ── Admin: FCC Cache (FccCacheAdmin.js) ───────────────────────────────
  "fccCache.toast.loadFailed":
    "Error al cargar las estad\u00edsticas de cach\u00e9 FCC",
  "fccCache.toast.purged":
    "Se eliminaron {count} entradas de cach\u00e9 obsoletas",

  // ── Admin: Demo Users (DemoAdminPanel.js) ─────────────────────────────
  "demoAdmin.toast.loadFailed": "Error al cargar los usuarios demo",
  "demoAdmin.toast.approved": "Usuario demo aprobado",
  "demoAdmin.toast.approveFailed": "Error al aprobar el usuario",
  "demoAdmin.toast.revoked": "Usuario demo revocado",
  "demoAdmin.toast.revokeFailed": "Error al revocar el usuario",
  "demoAdmin.toast.usageReset": "Contador de uso reiniciado a 0",
  "demoAdmin.toast.usageResetFailed": "Error al reiniciar el uso",
  "demoAdmin.toast.deleted": "Usuario demo eliminado",
  "demoAdmin.toast.deleteFailed": "Error al eliminar el usuario",
  "demoAdmin.toast.noUsersSelected": "Ning\u00fan usuario seleccionado",
  "demoAdmin.toast.bulkApprovalFailed": "Error en la aprobaci\u00f3n masiva",

  // ── Admin: Full Access Demo (FullAccessDemoAdmin.js) ──────────────────
  "fullAccessDemo.toast.loadFailed":
    "Error al cargar los asientos de demo del evento",
  "fullAccessDemo.toast.deactivated": "Asiento desactivado",
  "fullAccessDemo.toast.deactivateFailed": "Error al desactivar el asiento",
  "fullAccessDemo.toast.reactivated": "Asiento reactivado",
  "fullAccessDemo.toast.reactivateFailed": "Error al reactivar el asiento",
  "fullAccessDemo.toast.selectNewExpiry":
    "Selecciona una nueva fecha/hora de vencimiento",
  "fullAccessDemo.toast.extended": "Asiento extendido",
  "fullAccessDemo.toast.extendFailed": "Error al extender el asiento",
  "fullAccessDemo.toast.passwordUpdated": "Contrase\u00f1a actualizada",
  "fullAccessDemo.toast.passwordFailed":
    "Error al restablecer la contrase\u00f1a",
  "fullAccessDemo.toast.expiryRequired":
    "La fecha/hora de vencimiento es obligatoria",
  "fullAccessDemo.toast.expiryAfterStart":
    "El vencimiento debe ser posterior al inicio",
  "fullAccessDemo.toast.createFailed": "Error al crear el usuario demo",

  // Panel de administración de usuarios de prueba (revisor de la extensión, etc.)
  "Test Users": "Usuarios de prueba",
  "testUsers.toast.loadFailed": "Error al cargar los usuarios de prueba",
  "testUsers.toast.deactivated": "Usuario de prueba desactivado",
  "testUsers.toast.deactivateFailed":
    "Error al desactivar el usuario de prueba",
  "testUsers.toast.reactivated": "Usuario de prueba reactivado",
  "testUsers.toast.reactivateFailed": "Error al reactivar el usuario de prueba",
  "testUsers.toast.selectExpiry":
    "Seleccione una nueva fecha/hora de vencimiento",
  "testUsers.toast.extended": "Usuario de prueba extendido",
  "testUsers.toast.extendFailed": "Error al extender el usuario de prueba",
  "testUsers.toast.passwordUpdated": "Contraseña actualizada",
  "testUsers.toast.passwordResetFailed": "Error al restablecer la contraseña",
  "testUsers.toast.expiryRequired":
    "La fecha/hora de vencimiento es obligatoria",
  "testUsers.toast.expiryAfterStart":
    "El vencimiento debe ser posterior al inicio",
  "testUsers.toast.createFailed": "Error al crear el usuario de prueba",
  "testUsers.toast.deleted": "Usuario de prueba eliminado permanentemente",
  "testUsers.toast.deleteFailed": "Error al eliminar el usuario de prueba",
};

export default es;
